(() => {
  const controllers = new WeakMap();
  const clampCleanups = new WeakMap();
  const noteSavePromises = new WeakMap();

  const getFocusable = (root) =>
    Array.from(
      root.querySelectorAll(
        'a[href], button:not([disabled]), summary, textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
      if (el.getAttribute('aria-disabled') === 'true') return false;
      if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
      if (el.closest('[hidden]')) return false;
      if (el.parentElement?.closest('[aria-hidden="true"]')) return false;
      if (typeof el.checkVisibility === 'function') {
        try {
          return el.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true });
        } catch (error) {
          // Fall through to geometry check on partial implementations.
        }
      }
      return el.getClientRects().length > 0;
    });

  const announceStatus = (message) => {
    if (!message) return;
    const status = document.getElementById('ThemeA11yStatus');
    if (!status) return;
    status.textContent = '';
    status.textContent = message;
  };

  // Abort hung cart/clipboard-adjacent fetches so busy UI can recover (share 775 parity).
  const fetchWithTimeout = (input, init = {}, ms = 20000) => {
    const controller = new AbortController();
    const parent = init.signal;
    const onParentAbort = () => controller.abort();
    if (parent instanceof AbortSignal) {
      if (parent.aborted) controller.abort();
      else parent.addEventListener('abort', onParentAbort, { once: true });
    }
    const timer = window.setTimeout(() => controller.abort(), ms);
    return fetch(input, { ...init, signal: controller.signal }).finally(() => {
      window.clearTimeout(timer);
      if (parent instanceof AbortSignal) {
        parent.removeEventListener('abort', onParentAbort);
      }
    });
  };

  // Headers can arrive while the body stalls — bound json/text reads too.
  const withTimeout = (promise, ms = 20000) =>
    new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('Timed out')), ms);
      Promise.resolve(promise).then(
        (value) => {
          window.clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          window.clearTimeout(timer);
          reject(error);
        }
      );
    });

  const announceQuantityBound = (delta, min, max) => {
    const template =
      delta > 0
        ? document.body.getAttribute('data-quantity-max-template') ||
          'Maximum quantity is [count]'
        : document.body.getAttribute('data-quantity-min-template') ||
          'Minimum quantity is [count]';
    const count = delta > 0 && Number.isFinite(max) ? max : min;
    announceStatus(template.replaceAll('[count]', String(count)));
  };

  const recentSearchKey = 'bls-recent-searches';
  const recentSearchLimit = 6;

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');

  const readRecentSearches = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(recentSearchKey) || '[]');
      return Array.isArray(parsed)
        ? parsed.filter((item) => typeof item === 'string' && item.trim()).slice(0, recentSearchLimit)
        : [];
    } catch (error) {
      return [];
    }
  };

  const writeRecentSearches = (items) => {
    try {
      window.localStorage.setItem(
        recentSearchKey,
        JSON.stringify(items.slice(0, recentSearchLimit))
      );
    } catch (error) {
      // Ignore storage failures.
    }
  };

  const rememberRecentSearch = (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    const next = [
      trimmed,
      ...readRecentSearches().filter((item) => item.toLowerCase() !== trimmed.toLowerCase()),
    ];
    writeRecentSearches(next);
  };

  const enhanceQuantityControls = (root) => {
    const cleanups = [];

    root.querySelectorAll('[data-quantity-controls]').forEach((controls) => {
      if (controls.dataset.qtyEnhanced === 'true') return;
      controls.dataset.qtyEnhanced = 'true';

      const input = controls.querySelector('input[type="number"]');
      const decrease = controls.querySelector('[data-quantity-decrease]');
      const increase = controls.querySelector('[data-quantity-increase]');
      if (!input || !decrease || !increase) return;

      const readBounds = () => {
        const min = Number(input.min || 0);
        const maxRaw = input.getAttribute('max');
        const max = maxRaw == null || maxRaw === '' ? Infinity : Number(maxRaw);
        return { min, max: Number.isFinite(max) ? max : Infinity };
      };

      const syncButtons = ({ announceClamp = false } = {}) => {
        const { min, max } = readBounds();
        let value = Number(input.value || min);
        if (!Number.isFinite(value)) value = min;
        let clampedDelta = 0;
        if (value < min) {
          value = min;
          input.value = String(min);
          clampedDelta = -1;
        }
        if (value > max) {
          value = max;
          input.value = String(max);
          clampedDelta = 1;
        }
        decrease.disabled = value <= min;
        increase.disabled = value >= max;
        input.setAttribute('aria-valuemin', String(min));
        if (Number.isFinite(max)) input.setAttribute('aria-valuemax', String(max));
        else input.removeAttribute('aria-valuemax');
        input.setAttribute('aria-valuenow', String(value));
        // Sticky ATC / back-to-top parity: don't leave focus on a now-disabled stepper.
        const active = document.activeElement;
        if (
          (active === decrease && decrease.disabled) ||
          (active === increase && increase.disabled)
        ) {
          if (input instanceof HTMLElement) input.focus({ preventScroll: true });
        }
        // Cart page parity: speak when a typed value is clamped to min/max on commit.
        if (announceClamp && clampedDelta) announceQuantityBound(clampedDelta, min, max);
      };

      const bump = (delta) => {
        const { min, max } = readBounds();
        const current = Number(input.value || min);
        const next = Math.min(max, Math.max(min, current + delta));
        if (next === current) {
          announceQuantityBound(delta, min, max);
          return;
        }
        input.value = String(next);
        syncButtons();
        // Soft qty bump feedback on PDP / shared steppers (respect reduced motion).
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const bumpClass = delta > 0 ? 'is-bump-up' : 'is-bump-down';
          controls.classList.remove('is-bump-up', 'is-bump-down');
          // Retrigger CSS animation when clicking rapidly.
          void controls.offsetWidth;
          controls.classList.add(bumpClass);
          window.setTimeout(() => controls.classList.remove(bumpClass), 320);
        }
        // Focus stays on +/- — speak the new value (typed input already exposes aria-valuenow).
        const template =
          document.body.getAttribute('data-quantity-value-template') || 'Quantity [count]';
        announceStatus(template.replaceAll('[count]', String(next)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      const onDecrease = () => bump(-1);
      const onIncrease = () => bump(1);
      const onInput = () => syncButtons();
      const onChange = () => syncButtons({ announceClamp: true });

      decrease.addEventListener('click', onDecrease);
      increase.addEventListener('click', onIncrease);
      input.addEventListener('input', onInput);
      input.addEventListener('change', onChange);
      syncButtons();

      cleanups.push(() => {
        decrease.removeEventListener('click', onDecrease);
        increase.removeEventListener('click', onIncrease);
        input.removeEventListener('input', onInput);
        input.removeEventListener('change', onChange);
        delete controls.dataset.qtyEnhanced;
      });
    });

    return () => cleanups.forEach((fn) => fn());
  };

  const persistCartNote = async (input) => {
    if (!(input instanceof HTMLTextAreaElement)) return true;

    const field = input.closest('[data-note-field]');
    const markSynced = (value) => {
      input.dataset.noteSavedValue = value;
      if (field instanceof HTMLElement) field.classList.remove('is-error');
      const max = Number(input.getAttribute('maxlength') || 5000);
      input.setAttribute('aria-invalid', value.length >= max ? 'true' : 'false');
    };

    // Join an in-flight save, then retry if the note changed while waiting.
    const inFlight = noteSavePromises.get(input);
    if (inFlight) {
      const ok = await inFlight;
      if (input.dataset.noteSavedValue === input.value) return ok;
    }

    const next = input.value;
    if (input.dataset.noteSavedValue === next) {
      if (field instanceof HTMLElement) field.classList.remove('is-error');
      return true;
    }

    input.dataset.noteSaving = 'true';
    announceStatus(
      (field instanceof HTMLElement && field.getAttribute('data-note-saving')) ||
        document.body.getAttribute('data-cart-note-saving') ||
        'Saving order note'
    );
    const savePromise = (async () => {
      try {
        const response = await fetchWithTimeout(
          `${window.Shopify?.routes?.root || '/'}cart/update.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ note: next }),
          },
          20000
        );
        if (!response.ok) throw new Error('Note save failed');
        markSynced(next);
        return true;
      } catch (error) {
        if (field instanceof HTMLElement) field.classList.add('is-error');
        input.setAttribute('aria-invalid', 'true');
        const message =
          document.body.getAttribute('data-cart-refresh-error') ||
          'Could not update cart. Try again.';
        announceStatus(message);
        return false;
      } finally {
        delete input.dataset.noteSaving;
        noteSavePromises.delete(input);
      }
    })();

    noteSavePromises.set(input, savePromise);
    return savePromise;
  };

  const flushDirtyCartNotes = () => {
    const url = `${window.Shopify?.routes?.root || '/'}cart/update.js`;
    document.querySelectorAll('[data-note-input]').forEach((input) => {
      if (!(input instanceof HTMLTextAreaElement)) return;
      const next = input.value;
      if (input.dataset.noteSavedValue === next) return;

      const body = JSON.stringify({ note: next });
      let sent = false;
      try {
        if (typeof navigator.sendBeacon === 'function') {
          sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        }
      } catch (error) {
        sent = false;
      }

      if (sent) {
        input.dataset.noteSavedValue = next;
        const field = input.closest('[data-note-field]');
        if (field instanceof HTMLElement) field.classList.remove('is-error');
        return;
      }

      // keepalive fetch for browsers without a usable sendBeacon path.
      try {
        fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body,
          keepalive: true,
        });
        input.dataset.noteSavedValue = next;
        const field = input.closest('[data-note-field]');
        if (field instanceof HTMLElement) field.classList.remove('is-error');
      } catch (error) {
        // Best-effort only while the document is unloading.
      }
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushDirtyCartNotes();
  });
  window.addEventListener('pagehide', flushDirtyCartNotes);

  const captureNoteFieldState = (noteInput) => {
    if (!(noteInput instanceof HTMLTextAreaElement)) {
      return { value: null, savedValue: null, selection: null, focused: false };
    }
    const focused = document.activeElement === noteInput;
    let selection = null;
    if (focused && typeof noteInput.selectionStart === 'number') {
      selection = {
        start: noteInput.selectionStart,
        end: noteInput.selectionEnd,
      };
    }
    return {
      value: noteInput.value,
      savedValue:
        noteInput.dataset.noteSavedValue != null ? noteInput.dataset.noteSavedValue : null,
      selection,
      focused,
    };
  };

  const restoreNoteFieldValue = (root, state) => {
    if (!(root instanceof HTMLElement) || state.value == null) return null;
    const restored = root.querySelector('[data-note-input]');
    if (!(restored instanceof HTMLTextAreaElement)) return null;
    restored.value = state.value;
    // Preserve dirty baseline so an unsaved draft isn't treated as already persisted.
    if (state.savedValue != null) {
      restored.dataset.noteSavedValue = state.savedValue;
    }
    return restored;
  };

  const focusRestoredNote = (note, selection) => {
    if (!(note instanceof HTMLElement)) return;
    note.focus({ preventScroll: true });
    if (
      !(note instanceof HTMLTextAreaElement) ||
      !selection ||
      typeof note.setSelectionRange !== 'function'
    ) {
      return;
    }
    try {
      note.setSelectionRange(selection.start, selection.end);
    } catch (error) {
      // Some environments reject selection ranges.
    }
  };

  const enhanceNoteFields = (root) => {
    root.querySelectorAll('[data-note-field]').forEach((field) => {
      if (field.dataset.noteEnhanced === 'true') return;
      field.dataset.noteEnhanced = 'true';

      const input = field.querySelector('[data-note-input]');
      const count = field.querySelector('[data-note-count]');
      if (!(input instanceof HTMLTextAreaElement) || !count) return;

      const max = Number(input.getAttribute('maxlength') || 5000);
      const template =
        field.getAttribute('data-note-count-template') || '__CURRENT__ / __MAX__';

      if (input.dataset.noteSavedValue == null) {
        input.dataset.noteSavedValue = input.value;
      }

      const nearThreshold = max * 0.9;
      let announcedLimit = input.value.length >= max;
      let announcedNear =
        input.value.length >= nearThreshold && input.value.length < max;

      const sync = () => {
        const current = input.value.length;
        const near = current >= nearThreshold && current < max;
        count.textContent = template
          .replaceAll('__CURRENT__', String(current))
          .replaceAll('__MAX__', String(max));
        field.classList.toggle('is-near-limit', near);
        field.classList.toggle('is-at-limit', current >= max);
        if (field.classList.contains('is-error')) {
          input.setAttribute('aria-invalid', 'true');
        } else {
          input.setAttribute('aria-invalid', current >= max ? 'true' : 'false');
        }
        if (current >= max) {
          announcedNear = true;
          if (!announcedLimit) {
            announcedLimit = true;
            announceStatus(
              field.getAttribute('data-note-limit') || 'Order note character limit reached'
            );
          }
        } else if (near) {
          announcedLimit = false;
          if (!announcedNear) {
            announcedNear = true;
            announceStatus(
              field.getAttribute('data-note-near') || 'Order note nearing character limit'
            );
          }
        } else {
          announcedLimit = false;
          announcedNear = false;
        }
      };

      const onInput = () => {
        if (field.classList.contains('is-error')) {
          field.classList.remove('is-error');
        }
        sync();
      };

      const announceNoteSaved = () => {
        announceStatus(
          field.getAttribute('data-note-saved') ||
            document.body.getAttribute('data-cart-note-saved') ||
            'Order note saved'
        );
      };

      const onBlur = async () => {
        const dirty = input.dataset.noteSavedValue !== input.value;
        const ok = await persistCartNote(input);
        // Match Ctrl/Cmd+Enter: confirm autosave only when a dirty note actually persisted.
        if (ok && dirty) announceNoteSaved();
      };

      const onKeyDown = async (event) => {
        if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
        event.preventDefault();
        const dirty = input.dataset.noteSavedValue !== input.value;
        const ok = await persistCartNote(input);
        if (ok) {
          if (dirty) announceNoteSaved();
          return;
        }
        input.focus({ preventScroll: true });
      };

      input.addEventListener('input', onInput);
      input.addEventListener('blur', onBlur);
      input.addEventListener('keydown', onKeyDown);
      sync();
    });
  };

  const offerCarouselCleanups = new WeakMap();

  const enhanceCardOffers = (root = document) => {
    root.querySelectorAll('[data-card-offers-carousel]').forEach((rootEl) => {
      if (!(rootEl instanceof HTMLElement) || rootEl.dataset.offersEnhanced === 'true') return;
      const track = rootEl.querySelector('[data-card-offers-track]');
      if (!(track instanceof HTMLElement)) return;

      const slides = Array.from(track.children).filter((node) => node instanceof HTMLElement);
      if (slides.length < 2) return;

      rootEl.dataset.offersEnhanced = 'true';
      let index = 0;
      let timer = 0;
      let settleTimer = 0;

      const setSlideVisibility = (activeIndex, { revealingIndex = -1 } = {}) => {
        slides.forEach((slide, i) => {
          const visible = i === activeIndex || i === revealingIndex;
          slide.style.visibility = visible ? 'visible' : 'hidden';
          slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        });
      };

      const apply = ({ revealingIndex = -1 } = {}) => {
        window.clearTimeout(settleTimer);
        setSlideVisibility(index, { revealingIndex });
        track.style.transform = `translateY(calc(${index} * -1 * var(--card-offer-row)))`;

        // After the slide settles, keep only the active chip visible so the
        // next badge's border/shadow cannot peek under the clip edge.
        const settleMs = prefersReducedMotion() ? 0 : 350;

        settleTimer = window.setTimeout(() => {
          setSlideVisibility(index);
        }, settleMs);
      };

      const stop = () => {
        window.clearInterval(timer);
        timer = 0;
      };

      const start = () => {
        if (prefersReducedMotion()) {
          stop();
          index = 0;
          apply();
          return;
        }
        stop();
        timer = window.setInterval(() => {
          const previous = index;
          index = (index + 1) % slides.length;
          apply({ revealingIndex: previous });
        }, 2800);
      };

      const pause = () => {
        rootEl.classList.add('is-paused');
        stop();
      };

      const resume = () => {
        rootEl.classList.remove('is-paused');
        start();
      };

      const onEnter = () => pause();
      const onLeave = () => resume();
      const onFocusIn = () => pause();
      const onFocusOut = (event) => {
        if (event instanceof FocusEvent && event.relatedTarget instanceof Node && rootEl.contains(event.relatedTarget)) {
          return;
        }
        resume();
      };

      rootEl.addEventListener('pointerenter', onEnter);
      rootEl.addEventListener('pointerleave', onLeave);
      rootEl.addEventListener('focusin', onFocusIn);
      rootEl.addEventListener('focusout', onFocusOut);

      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const onMotionChange = () => start();
      if (typeof motionQuery.addEventListener === 'function') {
        motionQuery.addEventListener('change', onMotionChange);
      } else if (typeof motionQuery.addListener === 'function') {
        motionQuery.addListener(onMotionChange);
      }

      apply();
      start();

      offerCarouselCleanups.set(rootEl, () => {
        stop();
        window.clearTimeout(settleTimer);
        rootEl.removeEventListener('pointerenter', onEnter);
        rootEl.removeEventListener('pointerleave', onLeave);
        rootEl.removeEventListener('focusin', onFocusIn);
        rootEl.removeEventListener('focusout', onFocusOut);
        if (typeof motionQuery.removeEventListener === 'function') {
          motionQuery.removeEventListener('change', onMotionChange);
        } else if (typeof motionQuery.removeListener === 'function') {
          motionQuery.removeListener(onMotionChange);
        }
        track.style.transform = '';
        slides.forEach((slide) => {
          slide.style.visibility = '';
          slide.removeAttribute('aria-hidden');
        });
        delete rootEl.dataset.offersEnhanced;
        rootEl.classList.remove('is-paused');
      });
    });
  };

  const enhanceClampText = (root) => {
    root.querySelectorAll('[data-clamp-text]').forEach((wrap) => {
      if (!(wrap instanceof HTMLElement) || wrap.dataset.clampEnhanced === 'true') return;
      wrap.dataset.clampEnhanced = 'true';

      const body = wrap.querySelector('[data-clamp-body]');
      const toggle = wrap.querySelector('[data-clamp-toggle]');
      if (!(body instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;

      const moreLabel = wrap.getAttribute('data-clamp-more') || 'Read more';
      const lessLabel = wrap.getAttribute('data-clamp-less') || 'Show less';
      let expanded = false;

      if (!body.id) {
        body.id = `ClampBody-${Math.random().toString(36).slice(2, 10)}`;
      }
      toggle.setAttribute('aria-controls', body.id);

      let labelEl = toggle.querySelector('[data-clamp-label]');
      if (!(labelEl instanceof HTMLElement)) {
        const initial = toggle.textContent.trim() || moreLabel;
        toggle.replaceChildren();
        labelEl = document.createElement('span');
        labelEl.setAttribute('data-clamp-label', '');
        labelEl.textContent = initial;
        const chevron = document.createElement('span');
        chevron.setAttribute('data-clamp-chevron', '');
        chevron.setAttribute('aria-hidden', 'true');
        chevron.textContent = '▾';
        toggle.append(labelEl, chevron);
      }

      const setLabel = (text) => {
        labelEl.textContent = text;
      };

      const restoreFocusFromToggle = () => {
        if (document.activeElement !== toggle) return;
        const fallback =
          wrap.closest('section, article, .main-content')?.querySelector('h1, h2, h3') ||
          document.getElementById('MainContent');
        if (fallback instanceof HTMLElement) {
          if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
          fallback.focus({ preventScroll: true });
        }
      };

      const measure = () => {
        const keepExpanded = expanded;
        const wasToggleVisible = !toggle.hidden;
        const hadToggleFocus = document.activeElement === toggle;
        wrap.classList.add('is-clamped');
        wrap.classList.remove('is-expanded');
        const overflows = body.scrollHeight > body.clientHeight + 1;
        body.style.setProperty('--clamp-expanded-height', `${body.scrollHeight}px`);
        if (!overflows) {
          wrap.classList.remove('is-clamped');
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          expanded = false;
          // Sticky ATC / back-to-top parity: speak when a visible toggle vanishes under focus.
          if (wasToggleVisible && hadToggleFocus) {
            announceStatus(
              wrap.getAttribute('data-clamp-hidden') ||
                document.body.getAttribute('data-clamp-hidden') ||
                'Read more closed'
            );
          }
          restoreFocusFromToggle();
          return;
        }

        toggle.hidden = false;
        if (keepExpanded) {
          wrap.classList.remove('is-clamped');
          wrap.classList.add('is-expanded');
          setLabel(lessLabel);
          toggle.setAttribute('aria-expanded', 'true');
          expanded = true;
        } else {
          setLabel(moreLabel);
          toggle.setAttribute('aria-expanded', 'false');
          expanded = false;
        }
      };

      const onToggle = () => {
        expanded = !expanded;
        wrap.classList.toggle('is-expanded', expanded);
        wrap.classList.toggle('is-clamped', !expanded);
        const nextLabel = expanded ? lessLabel : moreLabel;
        setLabel(nextLabel);
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        announceStatus(nextLabel);
        if (!expanded) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          toggle.scrollIntoView({
            block: 'nearest',
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }
      };

      // Disclosure parity: Escape collapses expanded Read more (dialogs/search already do).
      const onKeyDown = (event) => {
        if (event.key !== 'Escape' || !expanded) return;
        if (!wrap.contains(document.activeElement)) return;
        event.preventDefault();
        if (expanded) onToggle();
        toggle.focus({ preventScroll: true });
      };

      toggle.addEventListener('click', onToggle);
      wrap.addEventListener('keydown', onKeyDown);
      measure();

      let resizeTimer = 0;
      const onResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(measure, 120);
      };
      window.addEventListener('resize', onResize);

      clampCleanups.set(wrap, () => {
        toggle.removeEventListener('click', onToggle);
        wrap.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('resize', onResize);
        window.clearTimeout(resizeTimer);
        delete wrap.dataset.clampEnhanced;
      });
    });
  };

  const enhanceHeaderMenu = (element) => {
    const openButton = element.querySelector('[data-menu-open]');
    const closeButton = element.querySelector('[data-menu-close]');
    const drawer = element.querySelector('[data-menu-drawer]');
    if (!openButton || !closeButton || !drawer || !(drawer instanceof HTMLDialogElement)) return;

    const panel = drawer.querySelector('.site-header__drawer-panel');
    let lastFocused = null;
    let closeTimer = 0;
    let transitionHandler = null;
    const mobileDetailCleanups = [];

    const setExpanded = (open) => {
      openButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    const clearCloseWait = () => {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
      if (transitionHandler && panel) {
        panel.removeEventListener('transitionend', transitionHandler);
        transitionHandler = null;
      }
    };

    const finishCloseDetails = (details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      details.open = false;
      details.classList.remove('is-closing');
      syncDetailsSummaryExpanded(details);
    };

    const closeMobileDetails = (details, { animate = true } = {}) => {
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      if (!animate || prefersReducedMotion()) {
        finishCloseDetails(details);
        return;
      }

      const submenu = details.querySelector(':scope > .site-header__mobile-submenu');
      details.classList.add('is-closing');
      syncDetailsSummaryExpanded(details);

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (submenu) submenu.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
        finishCloseDetails(details);
      };
      const onEnd = (event) => {
        if (event.target !== submenu) return;
        if (event.propertyName !== 'grid-template-rows' && event.propertyName !== 'opacity') return;
        finish();
      };
      if (submenu) submenu.addEventListener('transitionend', onEnd);
      const fallback = window.setTimeout(finish, 520);
    };

    const collapseMobileDetails = ({ animate = false } = {}) => {
      drawer.querySelectorAll('.site-header__mobile-menu details[open]').forEach((details) => {
        closeMobileDetails(details, { animate });
      });
    };

    const openMobileDetails = (details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      drawer.querySelectorAll('[data-mobile-nav-details][open]').forEach((peer) => {
        if (peer !== details) closeMobileDetails(peer, { animate: true });
      });
      details.classList.remove('is-closing');
      details.open = true;
      syncDetailsSummaryExpanded(details);
    };

    drawer.querySelectorAll('[data-mobile-nav-details]').forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      const summary = details.querySelector(':scope > summary');
      if (!(summary instanceof HTMLElement)) return;

      const onSummaryClick = (event) => {
        event.preventDefault();
        if (details.open && !details.classList.contains('is-closing')) {
          closeMobileDetails(details, { animate: true });
        } else {
          openMobileDetails(details);
        }
      };

      summary.addEventListener('click', onSummaryClick);
      mobileDetailCleanups.push(() => summary.removeEventListener('click', onSummaryClick));
      syncDetailsSummaryExpanded(details);
    });

    const finishDrawerClose = () => {
      clearCloseWait();
      drawer.classList.remove('is-closing', 'is-open', 'is-opening');
      if (drawer.open) drawer.close();
    };

    const openMenu = () => {
      const wasOpen = drawer.open && !drawer.classList.contains('is-closing');
      if (!wasOpen) lastFocused = document.activeElement;
      clearCloseWait();
      collapseMobileDetails({ animate: false });
      drawer.classList.remove('is-closing');
      drawer.classList.add('is-opening');
      if (!drawer.open) drawer.showModal();
      setExpanded(true);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!drawer.open) return;
          drawer.classList.remove('is-opening');
          drawer.classList.add('is-open');
        });
      });

      if (!wasOpen) {
        announceStatus(
          (openButton.getAttribute('aria-label') || openButton.textContent || 'Menu')
            .replace(/\s+/g, ' ')
            .trim() || 'Menu'
        );
      }
      if (wasOpen && drawer.contains(document.activeElement)) return;
      const focusables = getFocusable(drawer);
      (focusables[0] || closeButton).focus({ preventScroll: true });
    };

    const closeMenu = () => {
      if (!drawer.open || drawer.classList.contains('is-closing')) return;
      clearCloseWait();
      drawer.classList.remove('is-opening', 'is-open');
      drawer.classList.add('is-closing');
      setExpanded(false);
      collapseMobileDetails({ animate: false });

      if (prefersReducedMotion() || !(panel instanceof HTMLElement)) {
        finishDrawerClose();
        return;
      }

      const complete = () => finishDrawerClose();
      transitionHandler = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        complete();
      };
      panel.addEventListener('transitionend', transitionHandler);
      closeTimer = window.setTimeout(complete, 520);
    };

    const onOpenClick = () => openMenu();
    const onCloseClick = () => closeMenu();
    const onDialogClose = () => {
      clearCloseWait();
      drawer.classList.remove('is-closing', 'is-open', 'is-opening');
      setExpanded(false);
      collapseMobileDetails({ animate: false });
      announceStatus(document.body.getAttribute('data-menu-closed') || 'Menu closed');
      const target = lastFocused;
      lastFocused = null;
      if (
        target instanceof HTMLElement &&
        document.contains(target) &&
        typeof target.focus === 'function'
      ) {
        target.focus({ preventScroll: true });
      } else {
        openButton.focus({ preventScroll: true });
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && drawer.open) {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== 'Tab' || !drawer.open) return;
      const focusables = getFocusable(drawer);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    openButton.addEventListener('click', onOpenClick);
    closeButton.addEventListener('click', onCloseClick);
    drawer.addEventListener('close', onDialogClose);
    drawer.addEventListener('keydown', onKeyDown);
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) closeMenu();
    });

    // Desktop Catalog / submenu: smooth hover open with close delay.
    const hoverCleanups = [];
    const canHover =
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (canHover) {
      element.querySelectorAll('details[data-nav-hover]').forEach((details) => {
        if (!(details instanceof HTMLDetailsElement)) return;
        let hoverCloseTimer = 0;
        let openTimer = 0;

        const openNav = () => {
          window.clearTimeout(hoverCloseTimer);
          window.clearTimeout(openTimer);
          openTimer = window.setTimeout(() => {
            element.querySelectorAll('details[data-nav-hover][open]').forEach((peer) => {
              if (peer !== details && peer instanceof HTMLDetailsElement) peer.open = false;
            });
            details.open = true;
            details.classList.add('is-hover-open');
            syncDetailsSummaryExpanded(details);
          }, 40);
        };

        const scheduleClose = () => {
          window.clearTimeout(openTimer);
          window.clearTimeout(hoverCloseTimer);
          hoverCloseTimer = window.setTimeout(() => {
            details.open = false;
            details.classList.remove('is-hover-open');
            syncDetailsSummaryExpanded(details);
          }, 160);
        };

        const onEnter = () => openNav();
        const onLeave = () => scheduleClose();
        details.addEventListener('pointerenter', onEnter);
        details.addEventListener('pointerleave', onLeave);
        hoverCleanups.push(() => {
          window.clearTimeout(openTimer);
          window.clearTimeout(hoverCloseTimer);
          details.removeEventListener('pointerenter', onEnter);
          details.removeEventListener('pointerleave', onLeave);
          details.classList.remove('is-hover-open');
        });
      });
    }

    return () => {
      clearCloseWait();
      openButton.removeEventListener('click', onOpenClick);
      closeButton.removeEventListener('click', onCloseClick);
      drawer.removeEventListener('close', onDialogClose);
      drawer.removeEventListener('keydown', onKeyDown);
      mobileDetailCleanups.forEach((fn) => fn());
      hoverCleanups.forEach((fn) => fn());
    };
  };

  const enhanceAccountMenu = (element) => {
    if (!(element instanceof HTMLDetailsElement)) return;
    const trigger = element.querySelector(':scope > summary');
    const panel = element.querySelector('.site-header__account-menu-panel');
    const closeButton = element.querySelector('[data-account-menu-close]');
    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(closeButton instanceof HTMLButtonElement)) {
      return;
    }

    let closeTimer = 0;
    let transitionHandler = null;

    const cancelPendingClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
      if (transitionHandler) {
        panel.removeEventListener('transitionend', transitionHandler);
        transitionHandler = null;
      }
    };

    const finishClose = ({ restoreFocus = false, announce = true } = {}) => {
      cancelPendingClose();
      element.open = false;
      element.classList.remove('is-opening', 'is-closing');
      trigger.setAttribute('aria-expanded', 'false');
      if (announce) announceStatus(element.dataset.labelClosed || 'Account menu closed');
      if (restoreFocus && document.contains(trigger)) {
        trigger.focus({ preventScroll: true });
      }
    };

    const closeMenu = ({ restoreFocus = false } = {}) => {
      if (!element.open) return;
      cancelPendingClose();
      element.classList.remove('is-opening');
      element.classList.add('is-closing');
      trigger.setAttribute('aria-expanded', 'false');

      if (prefersReducedMotion()) {
        finishClose({ restoreFocus });
        return;
      }

      const complete = () => finishClose({ restoreFocus });
      transitionHandler = (event) => {
        if (event.target !== panel || event.propertyName !== 'opacity') return;
        complete();
      };
      panel.addEventListener('transitionend', transitionHandler);
      closeTimer = window.setTimeout(complete, 450);
    };

    const openMenu = ({ focusFirst = false } = {}) => {
      cancelPendingClose();
      element.classList.remove('is-closing');
      element.classList.add('is-opening');
      element.open = true;
      trigger.setAttribute('aria-expanded', 'true');
      announceStatus(element.dataset.labelOpened || 'Account menu opened');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!element.open) return;
          element.classList.remove('is-opening');
          if (!focusFirst) return;
          const firstLink = panel.querySelector('a[href]');
          if (firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: true });
        });
      });
    };

    const onTriggerClick = (event) => {
      event.preventDefault();
      if (element.open && !element.classList.contains('is-closing')) {
        closeMenu();
      } else {
        openMenu({ focusFirst: event.detail === 0 });
      }
    };
    const onCloseClick = () => closeMenu({ restoreFocus: true });
    const onKeyDown = (event) => {
      if (
        event.target === trigger &&
        (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')
      ) {
        event.preventDefault();
        if (element.open && !element.classList.contains('is-closing')) {
          closeMenu();
        } else {
          openMenu({ focusFirst: true });
        }
        return;
      }
      if (event.key !== 'Escape' || !element.open) return;
      event.preventDefault();
      event.stopPropagation();
      closeMenu({ restoreFocus: true });
    };
    const onDocumentPointerDown = (event) => {
      if (!element.open || element.contains(event.target)) return;
      closeMenu();
    };
    const onDocumentFocusIn = (event) => {
      if (!element.open || element.contains(event.target)) return;
      closeMenu();
    };

    trigger.addEventListener('click', onTriggerClick);
    closeButton.addEventListener('click', onCloseClick);
    element.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onDocumentPointerDown);
    document.addEventListener('focusin', onDocumentFocusIn);

    return () => {
      trigger.removeEventListener('click', onTriggerClick);
      closeButton.removeEventListener('click', onCloseClick);
      element.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onDocumentPointerDown);
      document.removeEventListener('focusin', onDocumentFocusIn);
      finishClose({ announce: false });
    };
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    const showCode = document.body?.dataset?.currencyCodeEnabled !== 'false';
    let formatted = '';
    try {
      formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(Number(cents) / 100);
    } catch (error) {
      formatted = `${(Number(cents) / 100).toFixed(2)}`;
    }
    // Match Liquid money_with_currency: "$39.99 CAD"
    if (showCode && currency && !formatted.toUpperCase().includes(currency.toUpperCase())) {
      return `${formatted} ${currency}`;
    }
    return formatted;
  };

  const enhanceProduct = (element) => {
    const jsonEl = element.querySelector('[data-product-json]');
    const form = element.querySelector('[data-product-form]');
    const variantInput = element.querySelector('[data-product-variant-id]');
    const optionSelects = Array.from(element.querySelectorAll('[data-product-option]'));
    const submit = element.querySelector('[data-product-submit]');
    const submitLabel = element.querySelector('[data-product-submit-label]');
    const priceEl = element.querySelector('[data-product-price]');
    const statusEl = element.querySelector('[data-product-status]');
    const skuEl = element.querySelector('[data-product-sku]');
    const skuValueEl = element.querySelector('[data-product-sku-value]');
    const barcodeEl = element.querySelector('[data-product-barcode]');
    const barcodeValueEl = element.querySelector('[data-product-barcode-value]');
    const inventoryEl = element.querySelector('[data-product-inventory]');
    const badgesEl = element.querySelector('[data-product-badges]');
    const saleBadgeEl = element.querySelector('[data-product-sale-badge]');
    const mediaItems = Array.from(element.querySelectorAll('.product__media-item'));
    const thumbs = Array.from(element.querySelectorAll('[data-thumb]'));
    const gallery = element.querySelector('[data-product-gallery]');
    const galleryPrev = element.querySelector('[data-gallery-prev]');
    const galleryNext = element.querySelector('[data-gallery-next]');
    const galleryCount = element.querySelector('[data-gallery-count]');
    const mediaRoleBadge = element.querySelector('[data-media-role-badge]');
    const stickyImage = element.querySelector('[data-product-sticky-image]');

    if (!jsonEl || !form || !variantInput || !submit || !submitLabel || !priceEl) return;

    let product;
    try {
      product = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }

    // Shopify's product JSON omits inventory_quantity; Liquid can still expose it.
    const inventoryElJson = element.querySelector('[data-product-inventory-json]');
    if (inventoryElJson && Array.isArray(product.variants)) {
      try {
        const inventoryById = JSON.parse(inventoryElJson.textContent || '{}');
        product.variants = product.variants.map((variant) => {
          const inv = inventoryById[String(variant.id)];
          if (!inv || typeof inv !== 'object') return variant;
          return {
            ...variant,
            inventory_management:
              inv.inventory_management != null
                ? inv.inventory_management
                : variant.inventory_management,
            inventory_policy:
              inv.inventory_policy != null ? inv.inventory_policy : variant.inventory_policy,
            inventory_quantity:
              inv.inventory_quantity != null
                ? inv.inventory_quantity
                : variant.inventory_quantity,
            available: inv.available != null ? inv.available : variant.available,
          };
        });
      } catch (error) {
        // Keep product JSON as-is if the inventory payload is malformed.
      }
    }

    const labels = {
      add: element.getAttribute('data-label-add') || 'Add to cart',
      soldOut: element.getAttribute('data-label-sold-out') || 'Sold out',
      unavailable: element.getAttribute('data-label-unavailable') || 'Unavailable',
      added: element.getAttribute('data-label-added') || 'Added to cart',
      adding: element.getAttribute('data-label-adding') || 'Adding to cart',
      error: element.getAttribute('data-label-error') || 'Could not add to cart. Try again.',
      minQty: element.getAttribute('data-label-min-qty') || 'Minimum quantity not met.',
    };
    const minQty = Number(element.getAttribute('data-min-qty') || 1);
    const quantityInput = element.querySelector('[data-product-quantity], input[name="quantity"]');

    const getSelectedOptions = () => optionSelects.map((select) => select.value);

    const findVariant = () => {
      if (optionSelects.length === 0) return product.variants[0];
      const selected = getSelectedOptions();
      return product.variants.find((variant) =>
        selected.every((value, index) => variant[`option${index + 1}`] === value)
      );
    };

    const pauseMediaEmbeds = (root) => {
      if (!(root instanceof HTMLElement)) return;
      root.querySelectorAll('video').forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        try {
          video.pause();
        } catch (error) {
          // Ignore pause failures from detached/restricted media.
        }
      });
      root.querySelectorAll('model-viewer').forEach((viewer) => {
        if (!(viewer instanceof HTMLElement)) return;
        try {
          if (typeof viewer.pause === 'function') viewer.pause();
        } catch (error) {
          // Some hosts expose model-viewer without a pause() method.
        }
      });
      root.querySelectorAll('iframe').forEach((iframe) => {
        if (!(iframe instanceof HTMLIFrameElement) || !iframe.contentWindow) return;
        try {
          // YouTube (enablejsapi) + Vimeo player API best-effort pause.
          iframe.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (error) {
          // Cross-origin postMessage can throw in some embeds.
        }
      });
    };

    const syncGalleryControls = () => {
      const multi = thumbs.length > 1;
      [galleryPrev, galleryNext].forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        button.hidden = !multi;
        if (button instanceof HTMLButtonElement) button.disabled = !multi;
        button.setAttribute('aria-hidden', multi ? 'false' : 'true');
      });
      if (galleryCount instanceof HTMLElement) {
        galleryCount.hidden = !multi;
      }
      // Qty stepper parity: don't leave focus on hidden/disabled gallery controls.
      if (!multi) {
        const active = document.activeElement;
        if (active === galleryPrev || active === galleryNext) {
          const activeThumb = thumbs.find((thumb) => thumb.classList.contains('is-active'));
          const fallback =
            activeThumb instanceof HTMLElement
              ? activeThumb
              : gallery instanceof HTMLElement
                ? gallery
                : null;
          if (fallback) fallback.focus({ preventScroll: true });
        }
      }
    };

    const syncThumbTabs = () => {
      thumbs.forEach((thumb) => {
        if (!(thumb instanceof HTMLElement)) return;
        thumb.tabIndex = thumb.classList.contains('is-active') ? 0 : -1;
      });
    };

    const syncMediaRoleBadge = (mediaId) => {
      if (!(mediaRoleBadge instanceof HTMLElement)) return;
      const activeItem = mediaId
        ? element.querySelector(`.product__media-item[data-media-id="${mediaId}"]`)
        : element.querySelector('.product__media-item.is-active');
      const roleLabel = activeItem?.getAttribute('data-media-role-label')?.trim() || '';
      const role = activeItem?.getAttribute('data-media-role')?.trim() || '';
      if (roleLabel) {
        mediaRoleBadge.textContent = roleLabel;
        if (role) mediaRoleBadge.setAttribute('data-role', role);
        else mediaRoleBadge.removeAttribute('data-role');
        mediaRoleBadge.hidden = false;
      } else {
        mediaRoleBadge.textContent = '';
        mediaRoleBadge.removeAttribute('data-role');
        mediaRoleBadge.hidden = true;
      }
    };

    const showMedia = (mediaId, { announce = false } = {}) => {
      if (!mediaId) return;
      element.querySelectorAll('.product__media-item').forEach((item) => {
        const match = String(item.getAttribute('data-media-id')) === String(mediaId);
        item.classList.toggle('is-active', match);
        item.toggleAttribute('aria-hidden', !match);
        item.toggleAttribute('hidden', !match);
        if (!match) pauseMediaEmbeds(item);
      });
      let activeIndex = 0;
      let activeThumb = null;
      thumbs.forEach((thumb, index) => {
        const match = String(thumb.getAttribute('data-media-id')) === String(mediaId);
        thumb.classList.toggle('is-active', match);
        thumb.setAttribute('aria-pressed', match ? 'true' : 'false');
        if (match) {
          activeIndex = index;
          activeThumb = thumb;
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      syncThumbTabs();
      // Variant/media changes can leave focus on a now-untabbable thumb or a hidden embed.
      const active = document.activeElement;
      const focusInHiddenMedia =
        active instanceof HTMLElement &&
        Boolean(
          active.closest(
            '.product__media-item[hidden], .product__media-item[aria-hidden="true"]'
          )
        );
      if (
        activeThumb instanceof HTMLElement &&
        active instanceof HTMLElement &&
        ((thumbs.includes(active) && active !== activeThumb) || focusInHiddenMedia)
      ) {
        activeThumb.focus({ preventScroll: true });
      } else if (
        focusInHiddenMedia &&
        gallery instanceof HTMLElement &&
        typeof gallery.focus === 'function'
      ) {
        gallery.focus({ preventScroll: true });
      }
      if (activeThumb instanceof HTMLElement && typeof activeThumb.scrollIntoView === 'function') {
        activeThumb.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
        });
      }
      if (galleryCount instanceof HTMLElement && thumbs.length > 0) {
        const template = galleryCount.getAttribute('data-template') || '[current] / [total]';
        const label = template
          .replace('[current]', String(activeIndex + 1))
          .replace('[total]', String(thumbs.length));
        galleryCount.textContent = label;
        if (announce) announceStatus(label);
      }
      syncMediaRoleBadge(mediaId);
      syncGalleryControls();
      if (stickyImage instanceof HTMLImageElement) {
        const activeItem = element.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
        const sourceImage = activeItem ? activeItem.querySelector('img') : null;
        if (sourceImage instanceof HTMLImageElement && sourceImage.currentSrc) {
          stickyImage.src = sourceImage.currentSrc;
          stickyImage.removeAttribute('srcset');
        } else if (sourceImage instanceof HTMLImageElement && sourceImage.src) {
          stickyImage.src = sourceImage.src;
          if (sourceImage.srcset) stickyImage.srcset = sourceImage.srcset;
        }
      }
    };

    const stepMedia = (delta) => {
      if (thumbs.length <= 1) return;
      const currentIndex = Math.max(
        0,
        thumbs.findIndex((thumb) => thumb.classList.contains('is-active'))
      );
      const nextIndex = (currentIndex + delta + thumbs.length) % thumbs.length;
      const nextThumb = thumbs[nextIndex];
      if (!nextThumb) return;
      showMedia(nextThumb.getAttribute('data-media-id'), { announce: true });
      if (typeof nextThumb.focus === 'function' && document.activeElement === gallery) {
        // Keep focus on gallery for continued arrow navigation.
        gallery.focus({ preventScroll: true });
      }
    };

    const buildPriceMarkup = (variant) => {
      if (!variant) return '';
      const onSale = variant.compare_at_price > variant.price;
      let saveMarkup = '';
      if (onSale && variant.compare_at_price > 0) {
        const percent = Math.round(
          ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
        );
        if (percent > 0) {
          const template =
            document.body.getAttribute('data-save-percent-template') || 'Save [percent]%';
          saveMarkup = `<span class="price__save">${template.replace('[percent]', String(percent))}</span>`;
        }
      }
      let unitMarkup = '';
      if (variant.unit_price && variant.unit_price_measurement) {
        const measurement = variant.unit_price_measurement;
        const reference =
          measurement.reference_value && measurement.reference_value !== 1
            ? String(measurement.reference_value)
            : '';
        unitMarkup = `<span class="price__unit">${formatMoney(variant.unit_price)}/${reference}${
          measurement.reference_unit || ''
        }</span>`;
      }
      return `
        <div class="price${onSale ? ' price--sale' : ''}">
          <span class="price__current">${formatMoney(variant.price)}</span>
          ${
            onSale
              ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>${saveMarkup}`
              : ''
          }
          ${unitMarkup}
        </div>
      `;
    };

    const updatePrice = (variant) => {
      if (!variant) {
        priceEl.innerHTML = '';
        return;
      }
      priceEl.innerHTML = buildPriceMarkup(variant);
    };

    const updateURL = (variant) => {
      if (!window.history?.replaceState) return;
      const url = new URL(window.location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.toString());
    };

    const stickyBar = element.querySelector('[data-product-sticky-bar]');
    const stickySubmit = element.querySelector('[data-product-sticky-submit]');
    const stickyLabel = element.querySelector('[data-product-sticky-label]');
    const stickyPrice = element.querySelector('[data-product-sticky-price]');
    const stickySave = element.querySelector('[data-product-sticky-save]');
    const stickyPriceRow = stickySave?.closest('.product-sticky-bar__price-row');
    let stickyObserver = null;

    const syncSticky = (variant) => {
      if (!stickySubmit || !stickyLabel) return;
      if (!variant) {
        stickySubmit.disabled = true;
        stickyLabel.textContent = labels.unavailable;
        if (stickySave) stickySave.hidden = true;
        if (stickyPrice) stickyPrice.innerHTML = '';
        return;
      }
      stickySubmit.disabled = !variant.available;
      stickyLabel.textContent = variant.available ? labels.add : labels.soldOut;
      if (stickyPrice) {
        stickyPrice.innerHTML = buildPriceMarkup(variant);
      }
      if (stickySave) {
        const compareAt = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || 0);
        const onSale = compareAt > price;
        stickySave.hidden = !onSale;
        if (onSale) {
          const saleFallback =
            (stickyPriceRow && stickyPriceRow.getAttribute('data-label-sale')) || 'Sale';
          const saveTemplate =
            (stickyPriceRow && stickyPriceRow.getAttribute('data-label-save-template')) ||
            'Save __PERCENT__%';
          const percent = Math.round(((compareAt - price) * 100) / compareAt);
          stickySave.textContent =
            percent > 0 ? saveTemplate.replaceAll('__PERCENT__', String(percent)) : saleFallback;
        }
      }
    };

    const restoreFocusFromHiddenMetaChip = (chip) => {
      if (!(chip instanceof HTMLElement) || !chip.hidden) return;
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || (active !== chip && !chip.contains(active))) return;

      const peers = [skuEl, barcodeEl].filter(
        (el) => el instanceof HTMLElement && el !== chip && !el.hidden
      );
      const optionFallback = optionSelects.find(
        (select) => select instanceof HTMLElement && !select.disabled
      );
      const fallback =
        peers[0] ||
        (optionFallback instanceof HTMLElement ? optionFallback : null) ||
        (quantityInput instanceof HTMLElement ? quantityInput : null) ||
        (submit instanceof HTMLElement && !submit.disabled ? submit : null);
      if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
    };

    const syncSku = (variant) => {
      if (!skuEl || !skuValueEl) return;
      const sku = variant && variant.sku ? String(variant.sku) : '';
      skuValueEl.textContent = sku;
      skuEl.hidden = !sku;
      if (sku) {
        const template = skuEl.getAttribute('data-label-copy-template') || 'Copy SKU __SKU__';
        skuEl.setAttribute('aria-label', template.replaceAll('__SKU__', sku));
      }
      restoreFocusFromHiddenMetaChip(skuEl);
    };

    const syncBarcode = (variant) => {
      if (!barcodeEl || !barcodeValueEl) return;
      const barcode = variant && variant.barcode ? String(variant.barcode) : '';
      barcodeValueEl.textContent = barcode;
      barcodeEl.hidden = !barcode;
      if (barcode) {
        const template =
          barcodeEl.getAttribute('data-label-copy-template') || 'Copy barcode __BARCODE__';
        barcodeEl.setAttribute('aria-label', template.replaceAll('__BARCODE__', barcode));
      }
      restoreFocusFromHiddenMetaChip(barcodeEl);
    };

    const syncSaleBadge = (variant) => {
      if (!(saleBadgeEl instanceof HTMLElement)) return;
      const compareAt = variant ? Number(variant.compare_at_price || 0) : 0;
      const price = variant ? Number(variant.price || 0) : 0;
      const onSale = Boolean(variant && compareAt > price);
      saleBadgeEl.hidden = !onSale;
      if (!onSale) return;

      const labelEl = saleBadgeEl.querySelector('.badge') || saleBadgeEl;
      const saleFallback =
        (badgesEl && badgesEl.getAttribute('data-label-sale')) || 'Sale';
      const saveTemplate =
        (badgesEl && badgesEl.getAttribute('data-label-save-template')) ||
        'Save __PERCENT__%';
      const percent = Math.round(((compareAt - price) * 100) / compareAt);
      labelEl.textContent =
        percent > 0 ? saveTemplate.replaceAll('__PERCENT__', String(percent)) : saleFallback;
    };

    const syncInventory = (variant) => {
      if (!(inventoryEl instanceof HTMLElement)) return;
      if (!variant || !variant.inventory_management) {
        inventoryEl.hidden = true;
        inventoryEl.classList.remove('product__inventory--in', 'product__inventory--low', 'product__inventory--out');
        const hiddenLabel = inventoryEl.querySelector('[data-inventory-label]');
        if (hiddenLabel instanceof HTMLElement) hiddenLabel.textContent = '';
        else inventoryEl.textContent = '';
        return;
      }

      const threshold = Number(inventoryEl.getAttribute('data-inventory-threshold') || 10);
      const quantity = Number(variant.inventory_quantity || 0);
      const labelIn = inventoryEl.getAttribute('data-label-in-stock') || 'In stock';
      const labelLow = inventoryEl.getAttribute('data-label-low-stock') || 'Only [count] left';
      const labelOut = inventoryEl.getAttribute('data-label-out-of-stock') || 'Out of stock';

      let state = 'in';
      let label = labelIn;
      if (!variant.available) {
        state = 'out';
        label = labelOut;
      } else if (quantity > 0 && quantity <= threshold) {
        state = 'low';
        label = labelLow.replace('[count]', String(quantity));
      }

      let labelEl = inventoryEl.querySelector('[data-inventory-label]');
      if (!(labelEl instanceof HTMLElement)) {
        inventoryEl.replaceChildren();
        const dot = document.createElement('span');
        dot.setAttribute('data-inventory-dot', '');
        dot.setAttribute('aria-hidden', 'true');
        labelEl = document.createElement('span');
        labelEl.setAttribute('data-inventory-label', '');
        inventoryEl.append(dot, labelEl);
      }

      inventoryEl.hidden = false;
      const previous = (labelEl.textContent || '').trim();
      if (previous !== label) {
        // Re-assert so polite live regions announce variant inventory changes.
        labelEl.textContent = '';
        labelEl.textContent = label;
      } else {
        labelEl.textContent = label;
      }
      inventoryEl.classList.toggle('product__inventory--in', state === 'in');
      inventoryEl.classList.toggle('product__inventory--low', state === 'low');
      inventoryEl.classList.toggle('product__inventory--out', state === 'out');
    };

    const syncQuantityLimits = (variant) => {
      if (!(quantityInput instanceof HTMLInputElement)) return null;
      const managed = Boolean(variant && variant.inventory_management);
      const deny = variant && variant.inventory_policy === 'deny';
      const stock = Number(variant && variant.inventory_quantity);
      let clampedMax = null;
      if (managed && deny && Number.isFinite(stock) && stock > 0) {
        quantityInput.max = String(stock);
        const current = Number(quantityInput.value || 1);
        if (Number.isFinite(current) && current > stock) {
          quantityInput.value = String(stock);
          clampedMax = stock;
        }
      } else {
        quantityInput.removeAttribute('max');
      }
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      return clampedMax;
    };

    let pendingOptionCorrections = [];

    const syncOptionAvailability = () => {
      pendingOptionCorrections = [];
      if (optionSelects.length === 0 || !Array.isArray(product.variants)) return;
      // Left-to-right so later selects re-read corrected earlier values.
      optionSelects.forEach((select, optionIndex) => {
        if (!(select instanceof HTMLSelectElement)) return;
        const selected = getSelectedOptions();
        Array.from(select.options).forEach((option) => {
          if (!option.value) {
            option.disabled = false;
            return;
          }
          const available = product.variants.some((variant) => {
            if (!variant.available) return false;
            if (variant[`option${optionIndex + 1}`] !== option.value) return false;
            return selected.every((value, index) => {
              if (index === optionIndex) return true;
              return !value || variant[`option${index + 1}`] === value;
            });
          });
          option.disabled = !available;
        });
        const current = select.options[select.selectedIndex];
        if (current?.disabled) {
          const firstEnabled = Array.from(select.options).find((opt) => opt.value && !opt.disabled);
          if (firstEnabled) {
            select.value = firstEnabled.value;
            const corrected = (firstEnabled.textContent || firstEnabled.value || '')
              .replace(/\s+/g, ' ')
              .trim();
            if (corrected) pendingOptionCorrections.push({ select, label: corrected });
          }
        }
      });
    };

    const restoreFocusFromDisabledSubmit = () => {
      const active = document.activeElement;
      const optionFallback = optionSelects.find(
        (select) => select instanceof HTMLElement && !select.disabled
      );
      const fallback =
        optionFallback instanceof HTMLElement
          ? optionFallback
          : quantityInput instanceof HTMLElement
            ? quantityInput
            : null;

      if (active === submit && submit.disabled && fallback) {
        fallback.focus({ preventScroll: true });
        return;
      }
      if (stickySubmit && active === stickySubmit && stickySubmit.disabled) {
        if (submit instanceof HTMLElement && !submit.disabled) {
          submit.focus({ preventScroll: true });
          return;
        }
        if (fallback) fallback.focus({ preventScroll: true });
      }
    };

    // Fall back to first product media when a variant has no featured_media (or is invalid).
    const syncGalleryForVariant = (variant, { announce = false } = {}) => {
      const previousMediaId =
        thumbs.find((thumb) => thumb.classList.contains('is-active'))?.getAttribute('data-media-id') ||
        '';
      const nextMediaId = variant?.featured_media
        ? String(variant.featured_media.id)
        : thumbs[0]?.getAttribute('data-media-id') || '';
      if (!nextMediaId || previousMediaId === nextMediaId) return;
      showMedia(nextMediaId, { announce });
    };

    const updateUI = ({ announceGallery = false } = {}) => {
      syncOptionAvailability();
      const variant = findVariant();
      if (!variant) {
        variantInput.value = '';
        variantInput.disabled = true;
        submit.disabled = true;
        submitLabel.textContent = labels.unavailable;
        updatePrice(null);
        syncSticky(null);
        syncSku(null);
        syncBarcode(null);
        syncSaleBadge(null);
        syncInventory(null);
        syncQuantityLimits(null);
        updateURL(null);
        syncGalleryForVariant(null, { announce: announceGallery });
        restoreFocusFromDisabledSubmit();
        return null;
      }

      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
      submit.disabled = !variant.available;
      submitLabel.textContent = variant.available ? labels.add : labels.soldOut;
      updatePrice(variant);
      syncSticky(variant);
      syncSku(variant);
      syncBarcode(variant);
      syncSaleBadge(variant);
      syncInventory(variant);
      const clampedMax = syncQuantityLimits(variant);
      updateURL(variant);
      syncGalleryForVariant(variant, { announce: announceGallery });
      restoreFocusFromDisabledSubmit();
      return clampedMax;
    };

    const onOptionChange = () => {
      const previousLabel = submitLabel.textContent;
      const previousPrice = (priceEl.textContent || '').replace(/\s+/g, ' ').trim();
      const previousSku = (skuValueEl?.textContent || '').trim();
      const previousBarcode = (barcodeValueEl?.textContent || '').trim();
      const previousSale =
        saleBadgeEl instanceof HTMLElement && !saleBadgeEl.hidden
          ? (saleBadgeEl.textContent || '').replace(/\s+/g, ' ').trim()
          : '';
      const wasInventoryVisible =
        inventoryEl instanceof HTMLElement && !inventoryEl.hidden;
      const previousInventoryLabel =
        inventoryEl instanceof HTMLElement
          ? (
              inventoryEl.querySelector('[data-inventory-label]')?.textContent ||
              inventoryEl.textContent ||
              ''
            )
              .replace(/\s+/g, ' ')
              .trim()
          : '';
      const clampedMax = updateUI({ announceGallery: true });
      const optionCorrections = pendingOptionCorrections.slice();
      pendingOptionCorrections = [];
      const nextLabel = submitLabel.textContent;
      const nextPrice = (priceEl.textContent || '').replace(/\s+/g, ' ').trim();
      const nextSku = (skuValueEl?.textContent || '').trim();
      const nextBarcode = (barcodeValueEl?.textContent || '').trim();
      const nextSale =
        saleBadgeEl instanceof HTMLElement && !saleBadgeEl.hidden
          ? (saleBadgeEl.textContent || '').replace(/\s+/g, ' ').trim()
          : '';
      const parts = [];
      // Cascading option fixes (disabled value → first available) were silent.
      const correctionLabels = optionCorrections
        .map((entry) => (typeof entry === 'string' ? entry : entry?.label) || '')
        .filter(Boolean);
      if (correctionLabels.length) {
        const template =
          document.body.getAttribute('data-options-adjusted-template') ||
          'Options adjusted: [options]';
        parts.push(template.replaceAll('[options]', correctionLabels.join(', ')));
      }
      // Announce sold-out / unavailable / back-in-stock after option changes.
      if (
        nextLabel &&
        nextLabel !== previousLabel &&
        (nextLabel === labels.soldOut ||
          nextLabel === labels.unavailable ||
          nextLabel === labels.add)
      ) {
        parts.push(nextLabel);
      }
      // Price-only variant swaps were silent when the ATC label stayed the same.
      if (nextPrice && nextPrice !== previousPrice) {
        parts.push(nextPrice);
      }
      if (nextSale && nextSale !== previousSale) {
        parts.push(nextSale);
      }
      if (nextSku && nextSku !== previousSku) {
        const template =
          document.body.getAttribute('data-sku-announce-template') || 'SKU [sku]';
        parts.push(template.replaceAll('[sku]', nextSku));
      }
      if (nextBarcode && nextBarcode !== previousBarcode) {
        const template =
          document.body.getAttribute('data-barcode-announce-template') || 'Barcode [barcode]';
        parts.push(template.replaceAll('[barcode]', nextBarcode));
      }
      // Tracked → untracked: chip hides and clears its own live region — speak globally.
      const isInventoryVisible =
        inventoryEl instanceof HTMLElement && !inventoryEl.hidden;
      if (wasInventoryVisible && !isInventoryVisible && previousInventoryLabel) {
        parts.push(
          document.body.getAttribute('data-inventory-hidden') ||
            'Stock availability no longer shown for this variant'
        );
      }
      // SKU/barcode hide: focus handoffs (592) but speech was missing (728 inventory parity).
      if (previousSku && !nextSku) {
        parts.push(
          document.body.getAttribute('data-sku-hidden') ||
            'SKU no longer shown for this variant'
        );
      }
      if (previousBarcode && !nextBarcode) {
        parts.push(
          document.body.getAttribute('data-barcode-hidden') ||
            'Barcode no longer shown for this variant'
        );
      }
      if (previousSale && !nextSale) {
        parts.push(
          document.body.getAttribute('data-sale-hidden') ||
            'Sale badge no longer shown for this variant'
        );
      }
      // Qty silently dropped to stock when switching to a tighter inventory variant.
      if (Number.isFinite(clampedMax)) {
        const template =
          document.body.getAttribute('data-quantity-max-template') ||
          'Maximum quantity is [count]';
        parts.push(template.replaceAll('[count]', String(clampedMax)));
      }
      if (parts.length) announceStatus(parts.join('. '));
      // Move focus to the first auto-corrected select so SR users land on what changed.
      const firstCorrected = optionCorrections.find(
        (entry) => entry && entry.select instanceof HTMLElement
      )?.select;
      if (firstCorrected instanceof HTMLElement && document.contains(firstCorrected)) {
        firstCorrected.focus({ preventScroll: true });
      }
    };
    const onThumbClick = (event) => {
      const button = event.currentTarget;
      showMedia(button.getAttribute('data-media-id'), { announce: true });
    };
    const onThumbKeyDown = (event) => {
      if (thumbs.length <= 1) return;
      const currentIndex = thumbs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % thumbs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + thumbs.length) % thumbs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = thumbs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      const nextThumb = thumbs[nextIndex];
      if (!(nextThumb instanceof HTMLElement)) return;
      showMedia(nextThumb.getAttribute('data-media-id'), { announce: true });
      nextThumb.focus({ preventScroll: true });
    };
    const onGalleryPrev = () => stepMedia(-1);
    const onGalleryNext = () => stepMedia(1);
    const onGalleryKeyDown = (event) => {
      // Pointer swipe already skips embeds — keyboard must too (video seek / model orbit).
      if (
        event.target instanceof Element &&
        event.target.closest(
          '.product__media-item.is-active video, .product__media-item.is-active model-viewer, .product__media-item.is-active iframe'
        )
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepMedia(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepMedia(1);
      } else if (event.key === 'Home' && thumbs.length > 1) {
        event.preventDefault();
        showMedia(thumbs[0]?.getAttribute('data-media-id'), { announce: true });
        if (document.activeElement === gallery) gallery.focus({ preventScroll: true });
      } else if (event.key === 'End' && thumbs.length > 1) {
        event.preventDefault();
        showMedia(thumbs[thumbs.length - 1]?.getAttribute('data-media-id'), { announce: true });
        if (document.activeElement === gallery) gallery.focus({ preventScroll: true });
      }
    };

    let swipeStartX = null;
    let swipeStartY = null;
    const onGalleryPointerDown = (event) => {
      if (!gallery || event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button, a, video, model-viewer, iframe')) return;
      swipeStartX = event.clientX;
      swipeStartY = event.clientY;
    };
    const onGalleryPointerUp = (event) => {
      if (swipeStartX == null || swipeStartY == null) return;
      const deltaX = event.clientX - swipeStartX;
      const deltaY = event.clientY - swipeStartY;
      swipeStartX = null;
      swipeStartY = null;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      if (deltaX < 0) stepMedia(1);
      else stepMedia(-1);
    };
    const onGalleryPointerCancel = () => {
      swipeStartX = null;
      swipeStartY = null;
    };

    const onSubmit = async (event) => {
      if (!(form instanceof HTMLFormElement)) return;

      const quantity = Number(quantityInput?.value || 1);
      if (minQty > 1 && quantity < minQty) {
        event.preventDefault();
        if (statusEl) {
          statusEl.hidden = false;
          statusEl.textContent = labels.minQty;
          statusEl.classList.add('is-error');
        }
        announceStatus(labels.minQty);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        quantityInput?.focus({ preventScroll: true });
        quantityInput?.scrollIntoView({
          block: 'center',
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
        return;
      }

      if (!window.fetch || !window.FormData) return;

      event.preventDefault();
      // Re-entry gate — global submit handler skips [data-product-form]; copy/share parity.
      if (
        submit.classList.contains('is-busy') ||
        (stickySubmit instanceof HTMLElement && stickySubmit.classList.contains('is-busy'))
      ) {
        return;
      }
      if (statusEl) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.classList.remove('is-error', 'is-success');
      }
      const activeBeforeBusy = document.activeElement;
      // Busy handoff moves focus to qty/status — remember the ATC for drawer close restore.
      const returnFocus =
        stickySubmit && activeBeforeBusy === stickySubmit
          ? stickySubmit
          : activeBeforeBusy === submit
            ? submit
            : null;
      submit.disabled = true;
      submit.classList.add('is-busy');
      submit.setAttribute('aria-busy', 'true');
      if (stickySubmit) {
        stickySubmit.disabled = true;
        stickySubmit.classList.add('is-busy');
        stickySubmit.setAttribute('aria-busy', 'true');
      }
      // Speak progress while the control is busy/disabled (cart:update announces completion).
      announceStatus(labels.adding);
      // Don't leave keyboard focus on a disabled ATC while the request is in flight.
      if (returnFocus) {
        const fallback =
          (quantityInput instanceof HTMLElement && !quantityInput.disabled && quantityInput) ||
          (statusEl instanceof HTMLElement ? statusEl : null) ||
          null;
        if (fallback instanceof HTMLElement) {
          if (!fallback.hasAttribute('tabindex') && fallback === statusEl) {
            fallback.setAttribute('tabindex', '-1');
          }
          fallback.focus({ preventScroll: true });
        }
      }

      let atcFailed = false;
      try {
        const response = await fetchWithTimeout(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          },
          20000
        );
        let payload = null;
        try {
          payload = await withTimeout(response.json(), 20000);
        } catch (parseError) {
          payload = null;
        }
        if (!response.ok) {
          const apiMessage =
            (payload && typeof payload.description === 'string' && payload.description.trim()) ||
            (payload && typeof payload.message === 'string' && payload.message.trim()) ||
            '';
          throw new Error(apiMessage || labels.error);
        }
        // Success is confirmed on the ATC button itself — don't leave a second
        // "Added to cart" chip sitting between Add to cart and Buy it now.
        announceStatus(labels.added);

        const ensureAtcCheck = (button) => {
          let icon = button.querySelector('[data-atc-check]');
          if (!(icon instanceof HTMLElement)) {
            icon = document.createElement('span');
            icon.setAttribute('data-atc-check', '');
            icon.setAttribute('aria-hidden', 'true');
            icon.textContent = '✓';
            button.prepend(icon);
          }
          return icon;
        };

        const submitCheck = ensureAtcCheck(submit);
        submitCheck.hidden = false;
        submitLabel.textContent = labels.added;
        submit.classList.add('is-success');

        let stickyCheck = null;
        if (stickySubmit instanceof HTMLElement) {
          stickyCheck = ensureAtcCheck(stickySubmit);
          stickyCheck.hidden = false;
          stickySubmit.classList.add('is-success');
          if (stickyLabel instanceof HTMLElement) stickyLabel.textContent = labels.added;
        }

        document.dispatchEvent(
          new CustomEvent('theme:cart:update', {
            detail: returnFocus ? { returnFocus } : undefined,
          })
        );
        window.setTimeout(() => {
          const variant = findVariant();
          const restoreLabel = variant && variant.available ? labels.add : labels.soldOut;
          submitCheck.hidden = true;
          submitLabel.textContent = restoreLabel;
          submit.classList.remove('is-success');
          if (stickyCheck instanceof HTMLElement) stickyCheck.hidden = true;
          if (stickyLabel instanceof HTMLElement) stickyLabel.textContent = restoreLabel;
          stickySubmit?.classList.remove('is-success');
          if (statusEl) {
            statusEl.hidden = true;
            statusEl.textContent = '';
            statusEl.classList.remove('is-success', 'is-error');
          }
        }, 1600);
      } catch (error) {
        atcFailed = true;
        const message =
          error instanceof Error && error.message ? error.message : labels.error;
        if (statusEl) {
          statusEl.hidden = false;
          statusEl.textContent = message;
          statusEl.classList.add('is-error');
        }
        announceStatus(message);
        submit.classList.add('is-error');
        window.setTimeout(() => submit.classList.remove('is-error'), 1600);
        if (stickySubmit instanceof HTMLElement) {
          stickySubmit.classList.add('is-error');
          window.setTimeout(() => stickySubmit.classList.remove('is-error'), 1600);
        }
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        statusEl?.scrollIntoView({
          block: 'nearest',
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      } finally {
        const variant = findVariant();
        const canBuy = !!(variant && variant.available);
        submit.disabled = !canBuy;
        submit.classList.remove('is-busy');
        submit.removeAttribute('aria-busy');
        if (stickySubmit) {
          stickySubmit.disabled = !canBuy;
          stickySubmit.classList.remove('is-busy');
          stickySubmit.removeAttribute('aria-busy');
        }
        // Return to the pressed ATC when focus is still on the handoff fallback —
        // errors always; success when the cart drawer won't open to take focus.
        const drawerWillOpen =
          !atcFailed && Boolean(document.querySelector('[data-cart-drawer-open]'));
        if (
          !drawerWillOpen &&
          returnFocus instanceof HTMLElement &&
          document.contains(returnFocus) &&
          !returnFocus.disabled
        ) {
          const active = document.activeElement;
          if (
            active === quantityInput ||
            active === statusEl ||
            active === document.body ||
            !(active instanceof HTMLElement)
          ) {
            returnFocus.focus({ preventScroll: true });
          }
        }
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      mediaItems.forEach((item) => pauseMediaEmbeds(item));
    };

    optionSelects.forEach((select) => select.addEventListener('change', onOptionChange));
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', onThumbClick);
      thumb.addEventListener('keydown', onThumbKeyDown);
    });
    galleryPrev?.addEventListener('click', onGalleryPrev);
    galleryNext?.addEventListener('click', onGalleryNext);
    gallery?.addEventListener('keydown', onGalleryKeyDown);
    gallery?.addEventListener('pointerdown', onGalleryPointerDown);
    gallery?.addEventListener('pointerup', onGalleryPointerUp);
    gallery?.addEventListener('pointercancel', onGalleryPointerCancel);
    form.addEventListener('submit', onSubmit);
    document.addEventListener('visibilitychange', onVisibilityChange);
    syncGalleryControls();
    syncThumbTabs();
    updateUI();
    // Ensure the role badge paints even when the variant media is already active.
    const bootMediaId =
      thumbs.find((thumb) => thumb.classList.contains('is-active'))?.getAttribute('data-media-id') ||
      element.querySelector('.product__media-item.is-active')?.getAttribute('data-media-id') ||
      thumbs[0]?.getAttribute('data-media-id') ||
      '';
    if (bootMediaId) syncMediaRoleBadge(bootMediaId);
    // Boot sync may auto-correct options; don't announce until a shopper changes one.
    pendingOptionCorrections = [];

    if (stickyBar && submit && 'IntersectionObserver' in window) {
      stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting;
          const wasShown = !stickyBar.hidden;
          stickyBar.hidden = !show;
          stickyBar.classList.toggle('is-visible', show);
          // Announce when the bar first appears (hide on scroll is too noisy).
          if (!wasShown && show) {
            announceStatus(
              stickyBar.getAttribute('data-sticky-shown') ||
                document.body.getAttribute('data-sticky-atc-shown') ||
                'Add to cart bar available'
            );
          }
          // If sticky ATC hides while focused, speak then return to the main submit.
          if (wasShown && !show && stickyBar.contains(document.activeElement)) {
            announceStatus(
              stickyBar.getAttribute('data-sticky-hidden') ||
                document.body.getAttribute('data-sticky-atc-hidden') ||
                'Add to cart bar closed'
            );
            if (submit instanceof HTMLElement) {
              submit.focus({ preventScroll: true });
            }
          }
        },
        { threshold: 0, rootMargin: '0px 0px -8% 0px' }
      );
      stickyObserver.observe(submit);
    }

    return () => {
      optionSelects.forEach((select) => select.removeEventListener('change', onOptionChange));
      thumbs.forEach((thumb) => {
        thumb.removeEventListener('click', onThumbClick);
        thumb.removeEventListener('keydown', onThumbKeyDown);
      });
      galleryPrev?.removeEventListener('click', onGalleryPrev);
      galleryNext?.removeEventListener('click', onGalleryNext);
      gallery?.removeEventListener('keydown', onGalleryKeyDown);
      gallery?.removeEventListener('pointerdown', onGalleryPointerDown);
      gallery?.removeEventListener('pointerup', onGalleryPointerUp);
      gallery?.removeEventListener('pointercancel', onGalleryPointerCancel);
      form.removeEventListener('submit', onSubmit);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (stickyObserver) stickyObserver.disconnect();
    };
  };

  document.addEventListener('theme:cart:update', async () => {
    if (window.__brightsideCartAbort) window.__brightsideCartAbort.abort();
    window.__brightsideCartAbort = new AbortController();
    const signal = window.__brightsideCartAbort.signal;

    const announceCartRefreshFailure = () => {
      announceStatus(
        document.body.getAttribute('data-cart-refresh-error') ||
          'Could not update cart. Try again.'
      );
    };

    const fetchCartJson = async () => {
      const response = await fetchWithTimeout(
        `${window.Shopify?.routes?.root || '/'}cart.js`,
        {
          signal,
          headers: { Accept: 'application/json' },
        },
        20000
      );
      if (!response.ok) throw new Error('Cart fetch failed');
      return withTimeout(response.json(), 20000);
    };

    try {
      let cart;
      try {
        cart = await fetchCartJson();
      } catch (firstError) {
        // Superseded only — hang timeouts also surface as AbortError from fetchWithTimeout.
        if (firstError && firstError.name === 'AbortError' && signal.aborted) return;
        // One retry for transient network/section blips before surfacing failure.
        cart = await fetchCartJson();
      }

      // Superseded while awaiting — don't clobber a newer badge/drawer refresh.
      if (signal.aborted) return;

      const count = Number(cart.item_count || 0);
      const countTemplate = document.body.getAttribute('data-cart-count-template') || '[count] items in cart';
      const updatedTemplate =
        document.body.getAttribute('data-cart-updated-template') || 'Cart updated. [count] items in cart.';
      const emptyAnnounce =
        document.body.getAttribute('data-cart-empty-announce') || 'Your cart is empty';
      const countLabel = countTemplate.replace('[count]', String(count));
      const updatedLabel =
        count === 0 ? emptyAnnounce : updatedTemplate.replace('[count]', String(count));

      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.hidden = count === 0;
        const visible = badge.querySelector('[aria-hidden="true"]');
        if (visible) visible.textContent = String(count);
        if (count > 0) {
          badge.classList.remove('is-popping');
          // Force reflow so the pop animation can restart.
          void badge.offsetWidth;
          badge.classList.add('is-popping');
        }
      });

      document.querySelectorAll('[data-cart-link]').forEach((link) => {
        link.setAttribute('aria-label', count > 0 ? countLabel : link.getAttribute('data-cart-empty-label') || 'Cart');
      });

      announceStatus(updatedLabel);

      document.dispatchEvent(new CustomEvent('theme:cart-drawer:refresh'));
    } catch (error) {
      if (error && error.name === 'AbortError' && signal.aborted) return;
      announceCartRefreshFailure();
    }
  });


  const readCartApiMessage = async (response) => {
    try {
      const payload = await withTimeout(response.json(), 20000);
      if (payload && typeof payload.description === 'string' && payload.description.trim()) {
        return payload.description.trim();
      }
      if (payload && typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message.trim();
      }
    } catch (error) {
      // Ignore parse failures; callers use a localized fallback.
    }
    return '';
  };

  const announceCartError = (root, itemRoot, overrideMessage) => {
    const message =
      (typeof overrideMessage === 'string' && overrideMessage.trim()) ||
      (root instanceof HTMLElement && root.getAttribute('data-cart-error')) ||
      'Could not update cart. Try again.';
    announceStatus(message);
    if (itemRoot instanceof HTMLElement) {
      itemRoot.classList.add('is-error');
      window.setTimeout(() => {
        itemRoot.classList.remove('is-error');
      }, 1600);
    }
  };

  const enhanceCartPage = (element) => {
    const sectionId = element.getAttribute('data-section-id');
    if (!sectionId) return;

    let refreshInFlight = null;
    let refreshQueued = false;
    let refreshAbort = null;
    // Busy-disable moves focus off +/- before refresh reads activeElement — stash from trigger.
    let pendingLineFocus = null;

    const runRefreshPage = async () => {
      const noteInput = element.querySelector('[data-note-input]');
      // Qty/drawer parity: persist a dirty note before section HTML is replaced.
      if (
        noteInput instanceof HTMLTextAreaElement &&
        noteInput.dataset.noteSavedValue !== noteInput.value
      ) {
        await persistCartNote(noteInput);
      }
      const noteState = captureNoteFieldState(
        element.querySelector('[data-note-input]')
      );
      const pending = pendingLineFocus;
      pendingLineFocus = null;
      const active = document.activeElement;
      const activeLineKey =
        pending?.lineKey ||
        (active instanceof HTMLElement &&
          (active.getAttribute('data-line-key') ||
            active.closest('[data-line-key]')?.getAttribute('data-line-key'))) ||
        '';
      const focusedNote =
        noteState.focused ||
        (active instanceof HTMLElement && active.hasAttribute('data-note-input'));
      const activeQtyDelta =
        pending?.activeQtyDelta != null
          ? pending.activeQtyDelta
          : active instanceof HTMLElement && active.hasAttribute('data-cart-qty-change')
            ? active.getAttribute('data-cart-qty-change')
            : '';
      const focusedQtyInput =
        pending?.focusedQtyInput != null
          ? pending.focusedQtyInput
          : active instanceof HTMLElement && active.hasAttribute('data-cart-page-qty-input');

      if (refreshAbort) refreshAbort.abort();
      refreshAbort = new AbortController();
      const { signal } = refreshAbort;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetchWithTimeout(
          url.toString(),
          { headers: { Accept: 'text/html' }, signal },
          15000
        );
        if (!response.ok || signal.aborted) return false;
        const html = await withTimeout(response.text(), 15000);
        if (signal.aborted) return false;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-component="cart-page"]');
        if (!next || signal.aborted) return false;
        element.innerHTML = next.innerHTML;

        restoreNoteFieldValue(element, noteState);

        enhanceNoteFields(element);
        if (typeof enhanceMotion === 'function') enhanceMotion(element);

        const activeAfter = document.activeElement;
        const focusInCart =
          activeAfter instanceof HTMLElement && element.contains(activeAfter);
        // Don't yank back if the shopper moved to header/footer/etc. during the fetch.
        // body / #MainContent still count as restorable (busy handoff / destroyed controls).
        const deliberateOutside =
          activeAfter instanceof HTMLElement &&
          !focusInCart &&
          activeAfter !== document.body &&
          activeAfter.id !== 'MainContent';

        if (!focusInCart && !deliberateOutside) {
          if (focusedNote) {
            focusRestoredNote(element.querySelector('[data-note-input]'), noteState.selection);
          } else if (activeLineKey) {
            let lineControl = null;
            if (focusedQtyInput) {
              lineControl = element.querySelector(
                `[data-cart-page-qty-input][data-line-key="${CSS.escape(activeLineKey)}"]`
              );
            } else if (activeQtyDelta != null && activeQtyDelta !== '') {
              lineControl = element.querySelector(
                `[data-cart-page-change][data-cart-qty-change="${CSS.escape(activeQtyDelta)}"][data-line-key="${CSS.escape(activeLineKey)}"]`
              );
            }
            if (!(lineControl instanceof HTMLElement)) {
              lineControl = element.querySelector(
                `[data-cart-page-qty-input][data-line-key="${CSS.escape(activeLineKey)}"], [data-cart-page-change][data-line-key="${CSS.escape(activeLineKey)}"]`
              );
            }
            if (lineControl instanceof HTMLElement) {
              lineControl.focus({ preventScroll: true });
            } else {
              // Line removed: land on another line, empty CTAs, or checkout — not nowhere.
              const nextLine = element.querySelector(
                '[data-cart-page-qty-input], [data-cart-page-change], .cart-page__remove'
              );
              const emptyAction = element.querySelector(
                '.cart-page__empty-actions .button, .cart-page__empty a'
              );
              const checkout = element.querySelector(
                '[name="checkout"], .cart-page__checkout button, .cart-page__cta .button, form[action*="cart"] button[type="submit"]'
              );
              const fallback = nextLine || emptyAction || checkout;
              if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
            }
          } else {
            const emptyAction = element.querySelector(
              '.cart-page__empty-actions .button, .cart-page__empty a'
            );
            const nextLine = element.querySelector(
              '[data-cart-page-qty-input], [data-cart-page-change], .cart-page__remove'
            );
            const checkout = element.querySelector(
              '[name="checkout"], .cart-page__checkout button, .cart-page__cta .button, form[action*="cart"] button[type="submit"]'
            );
            const fallback = emptyAction || nextLine || checkout;
            if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
          }
        }
        return true;
      } catch (error) {
        // Keep existing cart markup on refresh failure.
        return false;
      }
    };

    // Coalesce overlapping line updates so parallel replacements don't thrash focus.
    const refreshPage = async () => {
      if (refreshInFlight) {
        refreshQueued = true;
        return refreshInFlight;
      }

      refreshInFlight = (async () => {
        let ok = true;
        try {
          do {
            refreshQueued = false;
            ok = await runRefreshPage();
          } while (refreshQueued);
          return ok;
        } finally {
          refreshInFlight = null;
        }
      })();

      return refreshInFlight;
    };

    const isIncreaseAtMax = (control) => {
      if (!(control instanceof HTMLButtonElement)) return false;
      const delta = Number(control.getAttribute('data-cart-qty-change') || 0);
      if (!(delta > 0)) return false;
      const maxRaw = control.getAttribute('data-quantity-max');
      if (maxRaw == null || maxRaw === '') return false;
      const max = Number(maxRaw);
      const itemRoot = control.closest('.cart-page__item');
      const input = itemRoot?.querySelector('[data-cart-page-qty-input]');
      const qty =
        input instanceof HTMLInputElement
          ? Number(input.value || 0)
          : Number(control.getAttribute('data-quantity') || 0);
      return Number.isFinite(max) && Number.isFinite(qty) && qty >= max;
    };

    // PDP stepper parity (586): keep +/- disabled in sync with the typed value before blur.
    const syncCartPageQtyControls = (itemRoot) => {
      if (!(itemRoot instanceof HTMLElement) || itemRoot.classList.contains('is-busy')) return;
      const input = itemRoot.querySelector('[data-cart-page-qty-input]');
      const decrease = itemRoot.querySelector(
        '[data-cart-page-change][data-cart-qty-change="-1"]'
      );
      const increase = itemRoot.querySelector(
        '[data-cart-page-change][data-cart-qty-change="1"]'
      );
      if (!(input instanceof HTMLInputElement)) return;

      const min = Number(input.min || 0);
      const maxRaw =
        input.getAttribute('max') ||
        (increase instanceof HTMLElement ? increase.getAttribute('data-quantity-max') : null);
      const max = maxRaw == null || maxRaw === '' ? Infinity : Number(maxRaw);
      let value = Number(input.value || min);
      if (!Number.isFinite(value)) value = min;

      if (decrease instanceof HTMLButtonElement) {
        decrease.disabled = value <= min;
        // SSR uses qty<=1 for Remove; keep label/glyph in sync while typing (732 was disabled-only).
        const isRemove = value <= 1;
        const removeLabel =
          decrease.getAttribute('data-label-remove') ||
          document.body.getAttribute('data-cart-remove-label') ||
          'Remove';
        const decreaseLabel =
          decrease.getAttribute('data-label-decrease') ||
          'Decrease quantity';
        decrease.setAttribute('aria-label', isRemove ? removeLabel : decreaseLabel);
        decrease.textContent = isRemove ? '×' : '−';
      }
      if (increase instanceof HTMLButtonElement) {
        increase.disabled = Number.isFinite(max) && value >= max;
      }

      const active = document.activeElement;
      if (
        (active === decrease && decrease instanceof HTMLButtonElement && decrease.disabled) ||
        (active === increase && increase instanceof HTMLButtonElement && increase.disabled)
      ) {
        input.focus({ preventScroll: true });
      }
    };

    // Drawer 734 parity: keep input / data-quantity / aria-value* aligned before section refresh.
    const syncCartPageLineQtyOptimistic = (itemRoot, qty) => {
      if (!(itemRoot instanceof HTMLElement) || qty <= 0) return;
      const input = itemRoot.querySelector('[data-cart-page-qty-input]');
      if (input instanceof HTMLInputElement) {
        input.value = String(qty);
        input.setAttribute('data-quantity', String(qty));
        const min = Number(input.min || 0);
        const maxRaw = input.getAttribute('max');
        const max = maxRaw == null || maxRaw === '' ? Infinity : Number(maxRaw);
        input.setAttribute('aria-valuemin', String(min));
        if (Number.isFinite(max)) input.setAttribute('aria-valuemax', String(max));
        else input.removeAttribute('aria-valuemax');
        input.setAttribute('aria-valuenow', String(qty));
      }
      itemRoot.querySelectorAll('[data-cart-page-change]').forEach((control) => {
        if (control instanceof HTMLElement) control.setAttribute('data-quantity', String(qty));
      });
      syncCartPageQtyControls(itemRoot);
    };

    // Block Checkout/Update while any line qty/remove is in flight (stale-cart race).
    const syncCartSubmitBusy = () => {
      const anyBusy = Boolean(element.querySelector('.cart-page__item.is-busy'));
      element.querySelectorAll('[name="checkout"]').forEach((control) => {
        if (!(control instanceof HTMLButtonElement) && !(control instanceof HTMLInputElement)) {
          return;
        }
        control.disabled = anyBusy;
        control.classList.toggle('is-busy', anyBusy);
        if (anyBusy) control.setAttribute('aria-busy', 'true');
        else control.removeAttribute('aria-busy');
      });
      const active = document.activeElement;
      if (
        anyBusy &&
        active instanceof HTMLElement &&
        element.contains(active) &&
        active.getAttribute('name') === 'checkout' &&
        active.disabled
      ) {
        const note = element.querySelector('[data-note-input]');
        const main = document.getElementById('MainContent');
        const fallback = note || main;
        if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
      }
    };

    const setLineBusy = (itemRoot, busy) => {
      if (!itemRoot) return;
      itemRoot.classList.toggle('is-busy', busy);
      if (busy) itemRoot.setAttribute('aria-busy', 'true');
      else itemRoot.removeAttribute('aria-busy');
      itemRoot
        .querySelectorAll('[data-cart-page-change], [data-cart-page-qty-input]')
        .forEach((control) => {
          if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
            control.disabled = busy || isIncreaseAtMax(control);
          } else if (control instanceof HTMLAnchorElement) {
            // Remove is an <a> — pointer-events alone doesn't stop keyboard Enter.
            if (busy) {
              control.setAttribute('aria-disabled', 'true');
              control.setAttribute('tabindex', '-1');
            } else {
              control.removeAttribute('aria-disabled');
              control.removeAttribute('tabindex');
            }
          }
          control.classList.toggle('is-busy', busy);
          if (busy) control.setAttribute('aria-busy', 'true');
          else control.removeAttribute('aria-busy');
        });
      syncCartSubmitBusy();
      // Qty stepper parity: after unlock, sync +/- to the live typed value.
      if (!busy) {
        syncCartPageQtyControls(itemRoot);
        const active = document.activeElement;
        if (
          active instanceof HTMLElement &&
          itemRoot.contains(active) &&
          'disabled' in active &&
          active.disabled
        ) {
          const qtyInput = itemRoot.querySelector('[data-cart-page-qty-input]');
          const decrease = itemRoot.querySelector(
            '[data-cart-page-change][data-cart-qty-change="-1"]'
          );
          const fallback =
            (qtyInput instanceof HTMLElement && !qtyInput.disabled && qtyInput) ||
            (decrease instanceof HTMLElement && !decrease.disabled && decrease) ||
            null;
          if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
        }
      }
    };

    // ATC busy parity: don't leave focus on a locked line (disable collapses to body).
    // Never park on Checkout/Update — those stay disabled for the in-flight request.
    const handoffBusyLineFocus = (itemRoot) => {
      const active = document.activeElement;
      const needsHandoff =
        !(active instanceof HTMLElement) ||
        active === document.body ||
        (itemRoot instanceof HTMLElement && itemRoot.contains(active)) ||
        (active instanceof HTMLElement &&
          element.contains(active) &&
          (active.getAttribute('name') === 'checkout' || active.getAttribute('name') === 'update') &&
          active.disabled);
      if (!needsHandoff) return;
      const otherLine = element.querySelector(
        '.cart-page__item:not(.is-busy) [data-cart-page-qty-input], .cart-page__item:not(.is-busy) [data-cart-page-change]'
      );
      const note = element.querySelector('[data-note-input]');
      const fallback = otherLine || note || document.getElementById('MainContent');
      if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
    };

    const restorePendingLineFocus = () => {
      const pending = pendingLineFocus;
      if (!pending?.lineKey) return;
      let lineControl = null;
      if (pending.focusedQtyInput) {
        lineControl = element.querySelector(
          `[data-cart-page-qty-input][data-line-key="${CSS.escape(pending.lineKey)}"]`
        );
      } else if (pending.activeQtyDelta) {
        lineControl = element.querySelector(
          `[data-cart-page-change][data-cart-qty-change="${CSS.escape(pending.activeQtyDelta)}"][data-line-key="${CSS.escape(pending.lineKey)}"]`
        );
      }
      if (!(lineControl instanceof HTMLElement)) {
        lineControl = element.querySelector(
          `[data-cart-page-qty-input][data-line-key="${CSS.escape(pending.lineKey)}"], [data-cart-page-change][data-line-key="${CSS.escape(pending.lineKey)}"]`
        );
      }
      if (
        lineControl instanceof HTMLElement &&
        !lineControl.disabled &&
        lineControl.getAttribute('aria-disabled') !== 'true'
      ) {
        lineControl.focus({ preventScroll: true });
      }
    };

    const changeLine = async (lineKey, nextQty, itemRoot, focusSource) => {
      if (!lineKey || !Number.isFinite(nextQty)) return;
      // Capture before busy-disable — disabling the focused +/- collapses activeElement.
      const source =
        focusSource instanceof HTMLElement
          ? focusSource
          : document.activeElement instanceof HTMLElement &&
              itemRoot instanceof HTMLElement &&
              itemRoot.contains(document.activeElement)
            ? document.activeElement
            : null;
      pendingLineFocus =
        source instanceof HTMLElement
          ? {
              lineKey:
                source.getAttribute('data-line-key') ||
                source.closest('[data-line-key]')?.getAttribute('data-line-key') ||
                lineKey,
              focusedQtyInput: source.hasAttribute('data-cart-page-qty-input'),
              activeQtyDelta: source.hasAttribute('data-cart-page-change')
                ? source.getAttribute('data-cart-qty-change') || ''
                : '',
            }
          : { lineKey, focusedQtyInput: false, activeQtyDelta: '' };

      const qtyInput = itemRoot?.querySelector('[data-cart-page-qty-input]');
      // Prefer last-committed data-quantity over the live typed value for error revert.
      const previousQty = Number(
        (qtyInput instanceof HTMLInputElement
          ? qtyInput.getAttribute('data-quantity')
          : null) ||
          (source instanceof HTMLElement ? source.getAttribute('data-quantity') : null) ||
          (qtyInput instanceof HTMLInputElement ? qtyInput.value : null) ||
          0
      );

      // Optimistic input / stepper sync before section refresh (drawer 734 parity).
      if (nextQty > 0) syncCartPageLineQtyOptimistic(itemRoot, nextQty);

      setLineBusy(itemRoot, true);
      handoffBusyLineFocus(itemRoot);
      // Speak the new count (or remove) while the line is busy.
      if (nextQty <= 0) {
        announceStatus(
          document.body.getAttribute('data-cart-removing') || 'Removing item'
        );
      } else {
        const template =
          document.body.getAttribute('data-quantity-value-template') || 'Quantity [count]';
        announceStatus(template.replaceAll('[count]', String(nextQty)));
      }

      try {
        const noteInput = element.querySelector('[data-note-input]');
        if (noteInput instanceof HTMLTextAreaElement) {
          const noteSaved = await persistCartNote(noteInput);
          if (!noteSaved) {
            if (Number.isFinite(previousQty) && previousQty > 0) {
              syncCartPageLineQtyOptimistic(itemRoot, previousQty);
            }
            setLineBusy(itemRoot, false);
            pendingLineFocus = null;
            noteInput.focus({ preventScroll: true });
            return;
          }
        }

        const response = await fetchWithTimeout(
          `${window.Shopify?.routes?.root || '/'}cart/change.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id: lineKey, quantity: Math.max(0, nextQty) }),
          },
          20000
        );
        if (!response.ok) {
          const apiMessage = await readCartApiMessage(response);
          throw new Error(apiMessage || 'Cart change failed');
        }
        document.dispatchEvent(
          new CustomEvent('theme:cart:update', { detail: { skipDrawer: true } })
        );
        // Section fetch/body are bounded + abortable — late HTML can't clobber a new note edit.
        const refreshed = await refreshPage();
        if (!refreshed && itemRoot instanceof HTMLElement && itemRoot.isConnected) {
          // change.js already succeeded — clear busy quietly (drawer 779 parity).
          setLineBusy(itemRoot, false);
          restorePendingLineFocus();
          pendingLineFocus = null;
        }
      } catch (error) {
        if (Number.isFinite(previousQty) && previousQty > 0) {
          syncCartPageLineQtyOptimistic(itemRoot, previousQty);
        }
        setLineBusy(itemRoot, false);
        restorePendingLineFocus();
        pendingLineFocus = null;
        const apiMessage =
          error instanceof Error && error.message && error.message !== 'Cart change failed'
            ? error.message
            : '';
        announceCartError(element, itemRoot, apiMessage);
      }
    };

    const readQtyMax = (node) => {
      const maxRaw = node.getAttribute('data-quantity-max') || node.getAttribute('max');
      if (maxRaw == null || maxRaw === '') return Infinity;
      const max = Number(maxRaw);
      return Number.isFinite(max) ? max : Infinity;
    };

    const onClick = (event) => {
      const trigger = event.target.closest('[data-cart-page-change]');
      if (!(trigger instanceof HTMLElement) || !element.contains(trigger)) return;

      event.preventDefault();
      const itemRoot = trigger.closest('.cart-page__item');
      // Busy remove <a> stays focusable — ignore keyboard re-activation mid-request.
      if (
        trigger.getAttribute('aria-disabled') === 'true' ||
        trigger.classList.contains('is-busy') ||
        (itemRoot instanceof HTMLElement && itemRoot.classList.contains('is-busy'))
      ) {
        return;
      }
      const lineKey = trigger.getAttribute('data-line-key');
      const delta = Number(trigger.getAttribute('data-cart-qty-change') || 0);
      const currentQty = Number(trigger.getAttribute('data-quantity') || 0);
      if (!lineKey || !Number.isFinite(delta) || !Number.isFinite(currentQty)) return;

      let nextQty = Math.max(0, currentQty + delta);
      const max = readQtyMax(trigger);
      if (delta > 0 && nextQty > max) nextQty = max;
      if (delta > 0 && currentQty >= max) {
        announceQuantityBound(1, 0, max);
        return;
      }
      if (delta < 0 && currentQty <= 0) {
        announceQuantityBound(-1, 0, max);
        return;
      }

      changeLine(lineKey, nextQty, itemRoot, trigger);
    };

    const onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-cart-page-qty-input')) return;
      if (!element.contains(input)) return;
      if (input.closest('.cart-page__item')?.classList.contains('is-busy')) return;

      const lineKey = input.getAttribute('data-line-key');
      let nextQty = Math.max(0, Number(input.value || 0));
      if (!lineKey || !Number.isFinite(nextQty)) return;

      const max = readQtyMax(input);
      let clampedToMax = false;
      if (nextQty > max) {
        nextQty = max;
        input.value = String(max);
        clampedToMax = true;
      }

      const currentQty = Number(input.getAttribute('data-quantity') || input.defaultValue || 0);
      if (Number.isFinite(currentQty) && nextQty === currentQty) {
        if (clampedToMax) announceQuantityBound(1, 0, max);
        return;
      }

      const itemRoot = input.closest('.cart-page__item');
      changeLine(lineKey, nextQty, itemRoot, input);
    };

    // Enter in a qty field would implicitly submit Update (full-page POST) — use AJAX instead.
    const onQtyKeyDown = (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-cart-page-qty-input')) {
        return;
      }
      if (!element.contains(input)) return;

      event.preventDefault();

      const form = input.closest('form');
      const itemRoot = input.closest('.cart-page__item');
      if (
        itemRoot?.classList.contains('is-busy') ||
        form?.classList.contains('is-busy') ||
        form?.getAttribute('aria-busy') === 'true'
      ) {
        return;
      }

      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const onQtyInput = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-cart-page-qty-input')) {
        return;
      }
      if (!element.contains(input)) return;
      syncCartPageQtyControls(input.closest('.cart-page__item'));
    };

    element.querySelectorAll('.cart-page__item').forEach((item) => {
      if (item instanceof HTMLElement) syncCartPageQtyControls(item);
    });

    element.addEventListener('click', onClick);
    element.addEventListener('change', onChange);
    element.addEventListener('keydown', onQtyKeyDown);
    element.addEventListener('input', onQtyInput);

    return () => {
      element.removeEventListener('click', onClick);
      element.removeEventListener('change', onChange);
      element.removeEventListener('keydown', onQtyKeyDown);
      element.removeEventListener('input', onQtyInput);
    };
  };

  const enhanceCartDrawer = (element) => {
    const dialog = element.querySelector('[data-cart-drawer]');
    const sectionId = element.getAttribute('data-section-id');
    if (!dialog || !(dialog instanceof HTMLDialogElement)) return;

    let lastFocused = null;
    let refreshInFlight = null;
    let refreshQueued = false;
    let refreshAbort = null;
    // Busy-disable moves focus off +/- before refresh reads activeElement — stash from trigger.
    let pendingLineFocus = null;

    const announceCartDrawerState = () => {
      const badge = document.querySelector('[data-cart-count] [aria-hidden="true"]');
      const count = badge ? Number(String(badge.textContent || '').trim()) : NaN;
      if (!Number.isFinite(count) || count <= 0) {
        announceStatus(
          document.body.getAttribute('data-cart-empty-announce') || 'Your cart is empty'
        );
        return;
      }
      const countTemplate =
        document.body.getAttribute('data-cart-count-template') || '[count] items in cart';
      announceStatus(countTemplate.replace('[count]', String(count)));
    };

    const openDrawer = async ({ announceOpen = false, returnFocus = null } = {}) => {
      const wasOpen = dialog.open;
      if (!wasOpen) {
        // ATC busy handoff parks focus on qty/status — prefer the pressed ATC when provided.
        lastFocused =
          returnFocus instanceof HTMLElement && document.contains(returnFocus)
            ? returnFocus
            : document.activeElement;
      }
      // Open before section HTML so a hung refresh can't orphan ATC focus forever.
      if (!dialog.open) dialog.showModal();
      const refreshed = await refreshDrawer();
      // Escape/backdrop abort mid-refresh — don't false-error or focus a closed dialog.
      if (!dialog.open) return;
      if (!refreshed) announceCartError(element);
      // Already-open refreshes restore line focus inside refreshDrawer — don't steal it.
      if (wasOpen) return;
      const focusables = getFocusable(dialog);
      const closeButton = dialog.querySelector('[data-cart-drawer-close]');
      (focusables[0] || closeButton || dialog).focus({ preventScroll: true });
      // Manual opens only — ATC path already announced via theme:cart:update.
      if (announceOpen) announceCartDrawerState();
    };

    const closeDrawer = () => {
      if (refreshAbort) refreshAbort.abort();
      if (dialog.open) dialog.close();
    };

    const onDrawerClose = () => {
      if (refreshAbort) refreshAbort.abort();
      const noteInput = dialog.querySelector('[data-note-input]');
      if (noteInput instanceof HTMLTextAreaElement) {
        persistCartNote(noteInput);
      }
      announceStatus(document.body.getAttribute('data-cart-closed') || 'Cart closed');
      let target = lastFocused;
      lastFocused = null;
      const openTrigger = document.querySelector('[data-cart-drawer-open]');
      // Sticky ATC may have scrolled away while the drawer was open — use main submit instead.
      if (target instanceof HTMLElement && document.contains(target)) {
        const sticky = target.closest('[data-product-sticky-bar]');
        if (sticky instanceof HTMLElement && sticky.hidden) {
          const mainSubmit = document.querySelector('[data-product-submit]');
          if (mainSubmit instanceof HTMLElement && !mainSubmit.disabled) {
            target = mainSubmit;
          }
        }
      }
      // Menu/search parity: restore prior focus when still in the document, else the open control.
      if (
        target instanceof HTMLElement &&
        document.contains(target) &&
        typeof target.focus === 'function'
      ) {
        target.focus({ preventScroll: true });
      } else if (openTrigger instanceof HTMLElement) {
        openTrigger.focus({ preventScroll: true });
      }
    };

    const runRefreshDrawer = async () => {
      if (!sectionId) return false;
      const noteInput = dialog.querySelector('[data-note-input]');
      // Qty path already persists before mutate; ATC-open refresh must too.
      if (
        noteInput instanceof HTMLTextAreaElement &&
        noteInput.dataset.noteSavedValue !== noteInput.value
      ) {
        await persistCartNote(noteInput);
      }
      const noteState = captureNoteFieldState(dialog.querySelector('[data-note-input]'));
      const pending = pendingLineFocus;
      pendingLineFocus = null;
      const active = document.activeElement;
      const activeLineKey =
        pending?.lineKey ||
        (active instanceof HTMLElement &&
          dialog.contains(active) &&
          (active.getAttribute('data-line-key') ||
            active.closest('[data-line-key]')?.getAttribute('data-line-key'))) ||
        '';
      const focusedNote =
        noteState.focused ||
        (active instanceof HTMLElement &&
          dialog.contains(active) &&
          active.hasAttribute('data-note-input'));
      const activeQtyDelta =
        pending?.activeQtyDelta != null
          ? pending.activeQtyDelta
          : active instanceof HTMLElement && active.hasAttribute('data-cart-qty-change')
            ? active.getAttribute('data-cart-qty-change')
            : '';

      if (refreshAbort) refreshAbort.abort();
      refreshAbort = new AbortController();
      const { signal } = refreshAbort;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetchWithTimeout(
          url.toString(),
          { headers: { Accept: 'text/html' }, signal },
          15000
        );
        if (!response.ok || signal.aborted) return false;
        const html = await withTimeout(response.text(), 15000);
        if (signal.aborted) return false;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-cart-drawer]');
        if (!next || signal.aborted) return false;
        dialog.innerHTML = next.innerHTML;

        restoreNoteFieldValue(dialog, noteState);

        enhanceNoteFields(dialog);
        if (typeof enhanceMotion === 'function') enhanceMotion(dialog);

        // Qty/remove replaces the focused control — restore same line when possible.
        const activeAfter = document.activeElement;
        const focusInDrawer =
          activeAfter instanceof HTMLElement && dialog.contains(activeAfter);
        // Don't yank back if the shopper moved outside during the fetch (715 parity).
        // body / #MainContent still count as restorable (busy handoff / destroyed controls).
        const deliberateOutside =
          activeAfter instanceof HTMLElement &&
          !focusInDrawer &&
          activeAfter !== document.body &&
          activeAfter.id !== 'MainContent';

        if (dialog.open && !focusInDrawer && !deliberateOutside) {
          if (focusedNote) {
            focusRestoredNote(dialog.querySelector('[data-note-input]'), noteState.selection);
          } else if (activeLineKey) {
            const deltaSelector =
              activeQtyDelta != null && activeQtyDelta !== ''
                ? `[data-cart-qty-change="${CSS.escape(activeQtyDelta)}"][data-line-key="${CSS.escape(activeLineKey)}"]`
                : `[data-cart-qty-change][data-line-key="${CSS.escape(activeLineKey)}"]`;
            const lineControl = dialog.querySelector(deltaSelector);
            if (lineControl instanceof HTMLElement) {
              lineControl.focus({ preventScroll: true });
            } else {
              const nextLine = dialog.querySelector(
                '[data-cart-qty-change], [data-cart-drawer-quantity] input, .cart-drawer__remove'
              );
              const emptyAction = dialog.querySelector(
                '.cart-drawer__empty-actions .button, .cart-drawer__empty a'
              );
              const closeButton = dialog.querySelector('[data-cart-drawer-close]');
              (nextLine || emptyAction || closeButton || dialog).focus({ preventScroll: true });
            }
          } else {
            const focusables = getFocusable(dialog);
            const emptyAction = dialog.querySelector(
              '.cart-drawer__empty-actions .button, .cart-drawer__empty a'
            );
            const closeButton = dialog.querySelector('[data-cart-drawer-close]');
            (emptyAction || focusables[0] || closeButton || dialog).focus({ preventScroll: true });
          }
        }
        return true;
      } catch (error) {
        // Keep existing drawer markup on refresh failure.
        return false;
      }
    };

    // Coalesce overlapping ATC + count refreshes so parallel fetches don't thrash focus.
    const refreshDrawer = async () => {
      if (refreshInFlight) {
        refreshQueued = true;
        return refreshInFlight;
      }

      refreshInFlight = (async () => {
        let ok = true;
        try {
          do {
            refreshQueued = false;
            ok = await runRefreshDrawer();
          } while (refreshQueued);
          return ok;
        } finally {
          refreshInFlight = null;
        }
      })();

      return refreshInFlight;
    };

    const onOpenClick = (event) => {
      const trigger = event.target.closest('[data-cart-drawer-open]');
      if (!trigger) return;
      event.preventDefault();
      openDrawer({ announceOpen: true });
    };

    const onDialogClick = async (event) => {
      if (event.target === dialog) {
        closeDrawer();
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.target.closest('[data-cart-drawer-close-link]')) {
        closeDrawer();
      }

      const qtyButton = event.target.closest('[data-cart-qty-change]');
      if (!(qtyButton instanceof HTMLElement) || !dialog.contains(qtyButton)) return;

      event.preventDefault();
      const itemRoot = qtyButton.closest('.cart-drawer__item');
      // Busy remove <a> stays focusable — ignore keyboard re-activation mid-request.
      if (
        qtyButton.getAttribute('aria-disabled') === 'true' ||
        qtyButton.classList.contains('is-busy') ||
        (itemRoot instanceof HTMLElement && itemRoot.classList.contains('is-busy'))
      ) {
        return;
      }
      const lineKey = qtyButton.getAttribute('data-line-key');
      const delta = Number(qtyButton.getAttribute('data-cart-qty-change') || 0);
      const currentQty = Number(qtyButton.getAttribute('data-quantity') || 0);
      if (!lineKey || !Number.isFinite(delta) || !Number.isFinite(currentQty)) return;

      const maxRaw = qtyButton.getAttribute('data-quantity-max');
      const max = maxRaw == null || maxRaw === '' ? Infinity : Number(maxRaw);
      let nextQty = Math.max(0, currentQty + delta);
      if (Number.isFinite(max) && delta > 0) {
        if (currentQty >= max) {
          announceQuantityBound(1, 0, max);
          return;
        }
        if (nextQty > max) nextQty = max;
      }
      const controls = qtyButton.closest('[data-cart-drawer-quantity]');
      const buttons = controls
        ? Array.from(controls.querySelectorAll('[data-cart-qty-change]'))
        : [qtyButton];
      if (itemRoot) {
        itemRoot.querySelectorAll('[data-cart-qty-change], .cart-drawer__remove').forEach((control) => {
          if (!buttons.includes(control)) buttons.push(control);
        });
      }

      const isIncreaseAtMax = (control) => {
        if (!(control instanceof HTMLButtonElement)) return false;
        const delta = Number(control.getAttribute('data-cart-qty-change') || 0);
        if (!(delta > 0)) return false;
        const maxRaw = control.getAttribute('data-quantity-max');
        if (maxRaw == null || maxRaw === '') return false;
        const max = Number(maxRaw);
        const qty = Number(control.getAttribute('data-quantity') || 0);
        return Number.isFinite(max) && Number.isFinite(qty) && qty >= max;
      };

      // Block Checkout while any line qty/remove is in flight (stale-cart race).
      const syncCartSubmitBusy = () => {
        const anyBusy = Boolean(dialog.querySelector('.cart-drawer__item.is-busy'));
        dialog.querySelectorAll('[name="checkout"]').forEach((control) => {
          if (!(control instanceof HTMLButtonElement) && !(control instanceof HTMLInputElement)) {
            return;
          }
          control.disabled = anyBusy;
          control.classList.toggle('is-busy', anyBusy);
          if (anyBusy) control.setAttribute('aria-busy', 'true');
          else control.removeAttribute('aria-busy');
        });
        const active = document.activeElement;
        if (
          anyBusy &&
          active instanceof HTMLElement &&
          dialog.contains(active) &&
          active.getAttribute('name') === 'checkout' &&
          active.disabled
        ) {
          const note = dialog.querySelector('[data-note-input]');
          const closeButton = dialog.querySelector('[data-cart-drawer-close]');
          const fallback = note || closeButton || getFocusable(dialog)[0];
          if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
        }
      };

      const setBusy = (busy) => {
        if (itemRoot) {
          itemRoot.classList.toggle('is-busy', busy);
          if (busy) itemRoot.setAttribute('aria-busy', 'true');
          else itemRoot.removeAttribute('aria-busy');
        }
        buttons.forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            button.disabled = busy || isIncreaseAtMax(button);
          } else if (button instanceof HTMLAnchorElement) {
            // Remove is an <a> — pointer-events alone doesn't stop keyboard Enter.
            if (busy) {
              button.setAttribute('aria-disabled', 'true');
              button.setAttribute('tabindex', '-1');
            } else {
              button.removeAttribute('aria-disabled');
              button.removeAttribute('tabindex');
            }
          }
          button.classList.toggle('is-busy', busy);
          if (busy) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        });
        syncCartSubmitBusy();
        if (!busy && itemRoot) {
          const active = document.activeElement;
          if (
            active instanceof HTMLElement &&
            itemRoot.contains(active) &&
            'disabled' in active &&
            active.disabled
          ) {
            const qtyInput = itemRoot.querySelector(
              '[data-cart-drawer-quantity] input[type="number"]'
            );
            const decrease = itemRoot.querySelector('[data-cart-qty-change="-1"]');
            const fallback =
              (qtyInput instanceof HTMLElement && !qtyInput.disabled && qtyInput) ||
              (decrease instanceof HTMLElement && !decrease.disabled && decrease) ||
              null;
            if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
          }
        }
      };

      // ATC busy parity: keep focus inside the dialog while the line is locked.
      // Prefer note/close over Checkout — Checkout is disabled for the in-flight request.
      const handoffBusyLineFocus = () => {
        const active = document.activeElement;
        const needsHandoff =
          !(active instanceof HTMLElement) ||
          active === document.body ||
          !dialog.contains(active) ||
          (itemRoot instanceof HTMLElement && itemRoot.contains(active)) ||
          (active instanceof HTMLElement &&
            active.getAttribute('name') === 'checkout' &&
            active.disabled);
        if (!needsHandoff) return;
        const otherLine = dialog.querySelector(
          '.cart-drawer__item:not(.is-busy) [data-cart-qty-change]'
        );
        const note = dialog.querySelector('[data-note-input]');
        const closeButton = dialog.querySelector('[data-cart-drawer-close]');
        const focusables = getFocusable(dialog).filter(
          (el) => el.getAttribute('name') !== 'checkout'
        );
        const fallback = otherLine || note || closeButton || focusables[0];
        if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
      };

      const restorePendingLineFocus = () => {
        const pending = pendingLineFocus;
        if (!pending?.lineKey) return;
        const deltaSelector =
          pending.activeQtyDelta != null && pending.activeQtyDelta !== ''
            ? `[data-cart-qty-change="${CSS.escape(pending.activeQtyDelta)}"][data-line-key="${CSS.escape(pending.lineKey)}"]`
            : `[data-cart-qty-change][data-line-key="${CSS.escape(pending.lineKey)}"]`;
        const lineControl = dialog.querySelector(deltaSelector);
        if (
          lineControl instanceof HTMLElement &&
          !lineControl.disabled &&
          lineControl.getAttribute('aria-disabled') !== 'true'
        ) {
          lineControl.focus({ preventScroll: true });
        }
      };

      // Capture before busy-disable — disabling the focused +/- collapses activeElement.
      pendingLineFocus = {
        lineKey,
        activeQtyDelta: String(delta),
      };

      // Optimistic live-region / data-quantity sync (PDP bump parity) before busy lock.
      const syncDrawerLineQtyOptimistic = (qty) => {
        if (!(itemRoot instanceof HTMLElement) || qty <= 0) return;
        const valueEl = itemRoot.querySelector('.cart-drawer__quantity-value');
        if (valueEl instanceof HTMLElement) {
          // Re-assert so polite aria-live announces the new count mid-flight.
          valueEl.textContent = '';
          valueEl.textContent = String(qty);
        }
        itemRoot
          .querySelectorAll('[data-cart-drawer-quantity] [data-cart-qty-change]')
          .forEach((control) => {
            if (!(control instanceof HTMLElement)) return;
            control.setAttribute('data-quantity', String(qty));
            const change = Number(control.getAttribute('data-cart-qty-change') || 0);
            if (change < 0 && control instanceof HTMLButtonElement) {
              const isRemove = qty <= 1;
              const removeLabel =
                control.getAttribute('data-label-remove') ||
                document.body.getAttribute('data-cart-remove-label') ||
                'Remove';
              const decreaseLabel =
                control.getAttribute('data-label-decrease') || 'Decrease quantity';
              control.setAttribute('aria-label', isRemove ? removeLabel : decreaseLabel);
              // Keep the decrease glyph stable; remove is announced via aria-label.
              control.textContent = '−';
            }
          });
      };

      syncDrawerLineQtyOptimistic(nextQty);

      setBusy(true);
      handoffBusyLineFocus();
      // Speak the new count (or remove) while the line is busy — live region can miss focus handoff.
      if (nextQty <= 0) {
        announceStatus(
          document.body.getAttribute('data-cart-removing') || 'Removing item'
        );
      } else {
        const template =
          document.body.getAttribute('data-quantity-value-template') || 'Quantity [count]';
        announceStatus(template.replaceAll('[count]', String(nextQty)));
      }

      try {
        const noteInput = dialog.querySelector('[data-note-input]');
        if (noteInput instanceof HTMLTextAreaElement) {
          const noteSaved = await persistCartNote(noteInput);
          if (!noteSaved) {
            syncDrawerLineQtyOptimistic(currentQty);
            setBusy(false);
            pendingLineFocus = null;
            noteInput.focus({ preventScroll: true });
            return;
          }
        }

        const response = await fetchWithTimeout(
          `${window.Shopify?.routes?.root || '/'}cart/change.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id: lineKey, quantity: nextQty }),
          },
          20000
        );
        if (!response.ok) {
          const apiMessage = await readCartApiMessage(response);
          throw new Error(apiMessage || 'Cart change failed');
        }
        // skipDrawer: don't re-open after a mid-flight Escape/backdrop dismiss.
        // Open drawers still refresh via theme:cart-drawer:refresh from the badge updater.
        document.dispatchEvent(
          new CustomEvent('theme:cart:update', { detail: { skipDrawer: true } })
        );
        // change.js already succeeded — if section HTML is slow, clear busy quietly
        // (never false-announce an error while refresh is still in flight).
        window.setTimeout(() => {
          if (!(itemRoot instanceof HTMLElement) || !itemRoot.isConnected) return;
          setBusy(false);
          if (!dialog.open) {
            pendingLineFocus = null;
            return;
          }
          const active = document.activeElement;
          // Handoff parks on note/close — don't yank back if the shopper stayed there.
          const stillOnLine =
            active === document.body ||
            (active instanceof HTMLElement && active.id === 'MainContent') ||
            (active instanceof HTMLElement && itemRoot.contains(active));
          if (stillOnLine) restorePendingLineFocus();
          pendingLineFocus = null;
        }, 2200);
      } catch (error) {
        syncDrawerLineQtyOptimistic(currentQty);
        setBusy(false);
        // Dismissed mid-flight: don't announce/restore into a closed dialog.
        if (!dialog.open) {
          pendingLineFocus = null;
          return;
        }
        restorePendingLineFocus();
        pendingLineFocus = null;
        const apiMessage =
          error instanceof Error && error.message && error.message !== 'Cart change failed'
            ? error.message
            : '';
        announceCartError(element, itemRoot, apiMessage);
      }
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Tab' || !dialog.open) return;
      const focusables = getFocusable(dialog);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const onRefresh = () => {
      if (dialog.open) refreshDrawer();
    };

    const onAdded = (event) => {
      if (event?.detail?.skipDrawer) return;
      if (dialog.open) {
        refreshDrawer();
        return;
      }
      if (document.querySelector('[data-cart-drawer-open]')) {
        openDrawer({ returnFocus: event?.detail?.returnFocus || null });
      }
    };

    document.addEventListener('click', onOpenClick);
    dialog.addEventListener('click', onDialogClick);
    dialog.addEventListener('keydown', onKeyDown);
    dialog.addEventListener('close', onDrawerClose);
    document.addEventListener('theme:cart-drawer:refresh', onRefresh);
    document.addEventListener('theme:cart:update', onAdded);

    return () => {
      document.removeEventListener('click', onOpenClick);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('keydown', onKeyDown);
      dialog.removeEventListener('close', onDrawerClose);
      document.removeEventListener('theme:cart-drawer:refresh', onRefresh);
      document.removeEventListener('theme:cart:update', onAdded);
    };
  };
  const enhancePredictiveSearch = (element) => {
    const openButton = element.querySelector('[data-search-open]');
    const closeButton = element.querySelector('[data-search-close]');
    const dialog = element.querySelector('[data-search-dialog]');
    const input = element.querySelector('[data-search-input]');
    const results = element.querySelector('[data-search-results]');
    const form = element.querySelector('.site-header__search-form');
    const predictiveUrl = element.getAttribute('data-predictive-url');
    const sectionId = element.getAttribute('data-section-id') || 'predictive-search';
    const loadingLabel = element.getAttribute('data-loading-label') || 'Loading';
    const recentLabel = element.getAttribute('data-recent-label') || 'Recent searches';
    const clearRecentLabel = element.getAttribute('data-clear-recent-label') || 'Clear';
    const startHint = element.getAttribute('data-start-hint') || '';
    const errorHint =
      element.getAttribute('data-error-hint') ||
      'Search suggestions unavailable. Try again or press Enter to search the store.';

    if (
      !openButton ||
      !closeButton ||
      !dialog ||
      !(dialog instanceof HTMLDialogElement) ||
      !input ||
      !results ||
      !predictiveUrl
    ) {
      return;
    }

    const panel = dialog.querySelector('.site-header__search-panel');
    let closeTimer = null;
    let closeTransitionHandler = null;

    const clearCloseWait = () => {
      window.clearTimeout(closeTimer);
      closeTimer = null;
      if (closeTransitionHandler && panel instanceof HTMLElement) {
        panel.removeEventListener('transitionend', closeTransitionHandler);
        closeTransitionHandler = null;
      }
    };

    const finishSearchClose = () => {
      clearCloseWait();
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
    };

    let lastFocused = null;
    let debounceTimer = null;
    let abortController = null;

    const setExpanded = (open) => {
      openButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    const setResultsBusy = (busy) => {
      results.setAttribute('aria-busy', busy ? 'true' : 'false');
      results.classList.toggle('is-loading', busy);
    };

    const replaceResultsHtml = (html) => {
      if (!dialog.open) {
        results.innerHTML = html;
        return;
      }

      const active = document.activeElement;
      // Don't yank back if the shopper moved outside the dialog during a fetch (715 parity).
      const deliberateOutside =
        active instanceof HTMLElement &&
        !dialog.contains(active) &&
        active !== document.body &&
        active.id !== 'MainContent';

      const resultSelector =
        '.predictive-search__item, .predictive-search__view-all, [data-recent-query]';
      const priorItems = Array.from(results.querySelectorAll(resultSelector)).filter(
        (el) => el instanceof HTMLElement && !el.closest('[hidden]')
      );
      const priorIndex =
        active instanceof HTMLElement && results.contains(active)
          ? priorItems.indexOf(active)
          : -1;
      const focusInResults =
        active instanceof HTMLElement && results.contains(active);

      results.innerHTML = html;

      if (deliberateOutside) return;

      const activeAfter = document.activeElement;
      const focusLost =
        !(activeAfter instanceof HTMLElement) ||
        activeAfter === document.body ||
        !dialog.contains(activeAfter);

      if (!focusLost) return;

      // Keep Arrow-key list navigation when a late paint wiped a focused result.
      if (focusInResults && priorIndex >= 0) {
        const nextItems = Array.from(results.querySelectorAll(resultSelector)).filter(
          (el) => el instanceof HTMLElement && !el.closest('[hidden]')
        );
        if (nextItems.length) {
          nextItems[Math.min(priorIndex, nextItems.length - 1)].focus({
            preventScroll: true,
          });
          return;
        }
      }

      // Loading / empty / destroyed controls — recover to the query field.
      if (typeof input.focus === 'function') {
        input.focus({ preventScroll: true });
      }
    };

    const showLoading = () => {
      setResultsBusy(true);
      replaceResultsHtml(`<p class="site-header__search-loading" role="status">${loadingLabel}</p>`);
      announceStatus(loadingLabel);
    };

    const renderRecent = () => {
      const items = readRecentSearches();
      setResultsBusy(false);
      if (items.length === 0) {
        replaceResultsHtml(
          startHint ? `<p class="site-header__search-hint">${escapeHtml(startHint)}</p>` : ''
        );
        return;
      }

      replaceResultsHtml(`
        <div class="site-header__search-recent">
          <div class="site-header__search-recent-header">
            <h3 class="site-header__search-recent-heading">${escapeHtml(recentLabel)}</h3>
            <button type="button" class="site-header__search-recent-clear" data-recent-clear>
              <span>${escapeHtml(clearRecentLabel)}</span>
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <ul class="site-header__search-recent-list" role="list">
            ${items
              .map(
                (query) => `
              <li>
                <button
                  type="button"
                  class="site-header__search-recent-chip"
                  data-recent-query="${escapeHtml(query)}"
                >
                  ${escapeHtml(query)}
                </button>
              </li>`
              )
              .join('')}
          </ul>
        </div>
      `);
    };

    const openSearch = () => {
      const wasOpen = dialog.open && !dialog.classList.contains('is-closing');
      if (!wasOpen) lastFocused = document.activeElement;
      clearCloseWait();
      dialog.classList.remove('is-closing');
      if (!dialog.open) dialog.showModal();
      setExpanded(true);
      if (!wasOpen) {
        announceStatus(
          (openButton.getAttribute('aria-label') || openButton.textContent || 'Search')
            .replace(/\s+/g, ' ')
            .trim() || 'Search'
        );
      }
      input.focus({ preventScroll: true });
      input.select();
      if (input.value.trim().length < 2) renderRecent();
    };

    const closeSearch = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      clearCloseWait();
      dialog.classList.add('is-closing');
      setExpanded(false);

      if (prefersReducedMotion() || !(panel instanceof HTMLElement)) {
        finishSearchClose();
        return;
      }

      const complete = () => finishSearchClose();
      closeTransitionHandler = (event) => {
        if (!(event instanceof TransitionEvent)) return;
        if (event.target !== panel || event.propertyName !== 'opacity') return;
        complete();
      };
      panel.addEventListener('transitionend', closeTransitionHandler);
      closeTimer = window.setTimeout(complete, 420);
    };

    const cancelPredictiveFetch = () => {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      setResultsBusy(false);
    };

    // Soft abort only — don't permanently kill recovery until dialog close / teardown.
    let needsVisibleRetry = false;
    const softAbortPredictive = () => {
      const hadDebounce = Boolean(debounceTimer);
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      const hadFetch = Boolean(abortController);
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      if (
        dialog.open &&
        (hadFetch || hadDebounce || results.classList.contains('is-loading'))
      ) {
        needsVisibleRetry = true;
      }
    };

    const onPageHide = () => {
      if (dialog.open) softAbortPredictive();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (dialog.open) softAbortPredictive();
        return;
      }
      if (!dialog.open || !needsVisibleRetry) return;
      needsVisibleRetry = false;
      const trimmed = input.value.trim();
      if (trimmed.length >= 2) renderResults(input.value);
      else renderRecent();
    };

    const renderResults = async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        // Drop in-flight suggestions so a late response can’t overwrite recent chips.
        cancelPredictiveFetch();
        if (dialog.open) renderRecent();
        return;
      }

      if (abortController) abortController.abort();
      abortController = new AbortController();
      const request = abortController;
      showLoading();

      const url = new URL(predictiveUrl, window.location.origin);
      url.searchParams.set('q', trimmed);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '8');

      try {
        const response = await fetchWithTimeout(
          url.toString(),
          {
            signal: request.signal,
            headers: { Accept: 'text/html' },
          },
          15000
        );
        if (!dialog.open || abortController !== request) return;
        if (!response.ok) throw new Error('Predictive search failed');
        const html = await withTimeout(response.text(), 15000);
        if (!dialog.open || abortController !== request) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const markup = doc.querySelector('#predictive-search-results');
        replaceResultsHtml(markup ? markup.outerHTML : '');
        setResultsBusy(false);
        const itemCount = results.querySelectorAll('.predictive-search__item').length;
        if (markup && markup.querySelector('.predictive-search__item, .predictive-search__view-all')) {
          rememberRecentSearch(trimmed);
        }
        if (itemCount > 0) {
          const template =
            element.getAttribute('data-suggestions-count-template') ||
            document.body.getAttribute('data-suggestions-count-template') ||
            '[count] suggestions';
          announceStatus(template.replaceAll('[count]', String(itemCount)));
        } else {
          announceStatus(
            element.getAttribute('data-no-suggestions') ||
              document.body.getAttribute('data-no-suggestions') ||
              'No suggestions'
          );
        }
      } catch (error) {
        // Superseded / closed — a newer request owns busy, or cancelPredictiveFetch cleared it.
        if (!dialog.open || abortController !== request) return;
        // Hang timeout and hard failures share recovery — don't leave "Loading" forever.
        setResultsBusy(false);
        replaceResultsHtml(
          `<p class="site-header__search-error" role="alert">${escapeHtml(errorHint)}</p>`
        );
        // Global live region — error markup can miss when focus stays on the query field.
        announceStatus(errorHint);
      }
    };

    const onInput = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        renderResults(input.value);
      }, 280);
    };

    const lockRecentChips = (triggerChip) => {
      results.querySelectorAll('[data-recent-query], [data-recent-clear]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (el === triggerChip) {
          el.classList.add('is-busy');
          el.setAttribute('aria-busy', 'true');
        }
        el.setAttribute('aria-disabled', 'true');
        el.tabIndex = -1;
      });
    };

    const onResultsClick = (event) => {
      const clear = event.target.closest('[data-recent-clear]');
      if (clear && results.contains(clear)) {
        if (
          clear.classList.contains('is-busy') ||
          clear.getAttribute('aria-busy') === 'true' ||
          results.classList.contains('is-loading') ||
          results.getAttribute('aria-busy') === 'true'
        ) {
          return;
        }
        // Drop pending typed-query debounce so it can’t re-fetch after clear.
        window.clearTimeout(debounceTimer);
        debounceTimer = null;
        cancelPredictiveFetch();
        writeRecentSearches([]);
        renderRecent();
        announceStatus(
          element.getAttribute('data-recent-cleared-label') ||
            document.body.getAttribute('data-recent-searches-cleared') ||
            'Recent searches cleared'
        );
        input.focus({ preventScroll: true });
        return;
      }

      const chip = event.target.closest('[data-recent-query]');
      if (!(chip instanceof HTMLElement) || !results.contains(chip)) return;
      // Re-entry gate — search-page chips got this in 716; header must too.
      if (
        chip.classList.contains('is-busy') ||
        chip.getAttribute('aria-busy') === 'true' ||
        Boolean(results.querySelector('[data-recent-query].is-busy'))
      ) {
        return;
      }
      // Pending onInput debounce would re-call renderResults after this chip fetch.
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      const query = chip.getAttribute('data-recent-query') || '';
      if (query) {
        const template =
          document.body.getAttribute('data-recent-search-template') || 'Recent search: [query]';
        announceStatus(template.replaceAll('[query]', query));
      }
      lockRecentChips(chip);
      input.value = query;
      input.focus({ preventScroll: true });
      renderResults(query);
    };

    const onFormSubmit = () => {
      rememberRecentSearch(input.value);
    };

    const onOpenClick = () => openSearch();
    const onCloseClick = () => closeSearch();
    const onDialogClose = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      needsVisibleRetry = false;
      cancelPredictiveFetch();
      clearCloseWait();
      dialog.classList.remove('is-closing');
      setExpanded(false);
      results.innerHTML = '';
      announceStatus(document.body.getAttribute('data-search-closed') || 'Search closed');
      const target = lastFocused;
      lastFocused = null;
      if (
        target instanceof HTMLElement &&
        document.contains(target) &&
        typeof target.focus === 'function'
      ) {
        target.focus({ preventScroll: true });
      } else {
        openButton.focus({ preventScroll: true });
      }
    };
    const getResultItems = () => {
      const items = Array.from(
        results.querySelectorAll(
          '.predictive-search__item, .predictive-search__view-all, [data-recent-query]'
        )
      ).filter((el) => el instanceof HTMLElement && !el.closest('[hidden]'));
      // Clear sits after chips in the recent panel — include it in Arrow/Home/End trails (727 parity).
      const clear = results.querySelector('[data-recent-clear]');
      if (clear instanceof HTMLElement && !clear.closest('[hidden]')) items.push(clear);
      return items;
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && dialog.open) {
        const active = document.activeElement;
        const items = getResultItems();
        const onResult =
          items.includes(active) ||
          (active instanceof HTMLElement && results.contains(active) && active !== input);

        // Result focus → input (keep query); input with query → clear; else native close.
        if (onResult) {
          event.preventDefault();
          input.focus({ preventScroll: true });
          return;
        }
        if (input.value) {
          event.preventDefault();
          input.value = '';
          renderResults('');
          input.focus({ preventScroll: true });
          announceStatus(
            document.body.getAttribute('data-search-cleared') || 'Search cleared'
          );
          return;
        }
        event.preventDefault();
        closeSearch();
        return;
      }

      if (
        (event.key === 'ArrowDown' ||
          event.key === 'ArrowUp' ||
          event.key === 'Home' ||
          event.key === 'End') &&
        dialog.open
      ) {
        const items = getResultItems();
        if (items.length === 0) return;
        const active = document.activeElement;
        const onInput = active === input;
        const index = items.indexOf(active);
        if (!onInput && index < 0 && !(active instanceof HTMLElement && results.contains(active))) {
          return;
        }
        event.preventDefault();
        if (event.key === 'Home') {
          items[0].focus({ preventScroll: true });
          return;
        }
        if (event.key === 'End') {
          items[items.length - 1].focus({ preventScroll: true });
          return;
        }
        if (event.key === 'ArrowDown') {
          if (onInput || index < 0) items[0].focus({ preventScroll: true });
          else if (index >= items.length - 1) input.focus({ preventScroll: true });
          else items[index + 1].focus({ preventScroll: true });
        } else if (onInput) {
          items[items.length - 1].focus({ preventScroll: true });
        } else if (index <= 0) {
          input.focus({ preventScroll: true });
        } else {
          items[index - 1].focus({ preventScroll: true });
        }
        return;
      }

      if (event.key !== 'Tab' || !dialog.open) return;
      const focusables = getFocusable(dialog);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const onShortcutKeyDown = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest('input, textarea, select, [contenteditable="true"]'))
      ) {
        return;
      }
      const searchShortcutLabel = () =>
        (openButton.getAttribute('aria-label') || openButton.textContent || 'Search')
          .replace(/\s+/g, ' ')
          .trim() || 'Search';

      // Don't steal `/` from cart/menu dialogs; refocus input if search is already open.
      if (dialog.open) {
        event.preventDefault();
        if (typeof input.focus === 'function') input.focus({ preventScroll: true });
        announceStatus(searchShortcutLabel());
        return;
      }
      if (document.querySelector('dialog[open]')) return;
      // On the search results page, focus the page field instead of opening the header dialog.
      if (document.body.classList.contains('template-search')) {
        const pageInput = document.querySelector('.search-page__form input[type="search"]');
        if (pageInput instanceof HTMLInputElement) {
          event.preventDefault();
          pageInput.focus({ preventScroll: true });
          pageInput.select();
          announceStatus(searchShortcutLabel());
          return;
        }
      }
      event.preventDefault();
      openSearch();
    };

    const onCancel = (event) => {
      event.preventDefault();
      const active = document.activeElement;
      const items = getResultItems();
      const onResult =
        items.includes(active) ||
        (active instanceof HTMLElement && results.contains(active) && active !== input);

      if (onResult) {
        input.focus({ preventScroll: true });
        return;
      }
      if (input.value) {
        input.value = '';
        renderResults('');
        input.focus({ preventScroll: true });
        announceStatus(
          document.body.getAttribute('data-search-cleared') || 'Search cleared'
        );
        return;
      }
      closeSearch();
    };

    openButton.addEventListener('click', onOpenClick);
    closeButton.addEventListener('click', onCloseClick);
    input.addEventListener('input', onInput);
    results.addEventListener('click', onResultsClick);
    form?.addEventListener('submit', onFormSubmit);
    dialog.addEventListener('close', onDialogClose);
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('keydown', onKeyDown);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeSearch();
    });
    document.addEventListener('keydown', onShortcutKeyDown);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      needsVisibleRetry = false;
      cancelPredictiveFetch();
      clearCloseWait();
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      openButton.removeEventListener('click', onOpenClick);
      closeButton.removeEventListener('click', onCloseClick);
      input.removeEventListener('input', onInput);
      results.removeEventListener('click', onResultsClick);
      form?.removeEventListener('submit', onFormSubmit);
      dialog.removeEventListener('close', onDialogClose);
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keydown', onShortcutKeyDown);
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
    };
  };

  const enhanceSearchPage = (element) => {
    const form = element.querySelector('.search-page__form');
    const input = element.querySelector('.search-page__form input[type="search"]');
    if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) {
      return;
    }

    const onFormSubmit = () => {
      rememberRecentSearch(input.value);
    };

    form.addEventListener('submit', onFormSubmit);

    if (input.value.trim().length >= 2) {
      rememberRecentSearch(input.value);
    }

    const mount = element.querySelector('[data-search-recent]');
    let renderRecent = () => {};

    const onInputKeyDown = (event) => {
      if (event.key !== 'Escape' || !input.value) return;
      event.preventDefault();
      input.value = '';
      input.focus({ preventScroll: true });
      renderRecent();
      announceStatus(document.body.getAttribute('data-search-cleared') || 'Search cleared');
    };
    input.addEventListener('keydown', onInputKeyDown);

    if (!mount) {
      return () => {
        form.removeEventListener('submit', onFormSubmit);
        input.removeEventListener('keydown', onInputKeyDown);
      };
    }

    const getRecentItems = () => {
      const items = Array.from(mount.querySelectorAll('[data-recent-query]')).filter(
        (el) => el instanceof HTMLElement && !el.closest('[hidden]')
      );
      const clear = mount.querySelector('[data-recent-clear]');
      if (clear instanceof HTMLElement && !clear.closest('[hidden]')) items.push(clear);
      return items;
    };

    const moveRecentFocus = (event) => {
      const active = document.activeElement;
      const onInput = active === input;
      const items = getRecentItems();
      const index = items.indexOf(active);

      // Escape from a recent chip/clear returns to the query field (input Escape clears).
      if (event.key === 'Escape' && !onInput && index >= 0) {
        event.preventDefault();
        input.focus({ preventScroll: true });
        return;
      }

      if (
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp' &&
        event.key !== 'Home' &&
        event.key !== 'End'
      ) {
        return;
      }
      if (items.length === 0) return;
      if (!onInput && index < 0) return;
      event.preventDefault();
      if (event.key === 'Home') {
        items[0].focus({ preventScroll: true });
        return;
      }
      if (event.key === 'End') {
        items[items.length - 1].focus({ preventScroll: true });
        return;
      }
      if (event.key === 'ArrowDown') {
        if (onInput || index < 0) items[0].focus({ preventScroll: true });
        else if (index >= items.length - 1) input.focus({ preventScroll: true });
        else items[index + 1].focus({ preventScroll: true });
      } else if (onInput) {
        items[items.length - 1].focus({ preventScroll: true });
      } else if (index <= 0) {
        input.focus({ preventScroll: true });
      } else {
        items[index - 1].focus({ preventScroll: true });
      }
    };

    const recentLabel = element.getAttribute('data-recent-label') || 'Recent searches';
    const clearRecentLabel = element.getAttribute('data-clear-recent-label') || 'Clear';
    const startHint = element.getAttribute('data-start-hint') || '';
    const emptyAsHint = mount.hasAttribute('data-empty-as-hint');

    renderRecent = () => {
      const items = readRecentSearches();
      if (items.length === 0) {
        if (emptyAsHint && startHint) {
          mount.innerHTML = `<p class="search-page__hint">${escapeHtml(startHint)}</p>`;
          mount.hidden = false;
        } else {
          mount.innerHTML = '';
          mount.hidden = true;
        }
        return;
      }

      mount.hidden = false;
      mount.innerHTML = `
        <div class="search-page__recent-panel">
          <div class="search-page__recent-header">
            <h2 class="search-page__recent-heading">${escapeHtml(recentLabel)}</h2>
            <button type="button" class="search-page__recent-clear" data-recent-clear>
              <span>${escapeHtml(clearRecentLabel)}</span>
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <ul class="search-page__recent-list" role="list">
            ${items
              .map(
                (query) => `
              <li>
                <button
                  type="button"
                  class="search-page__recent-chip"
                  data-recent-query="${escapeHtml(query)}"
                >
                  ${escapeHtml(query)}
                </button>
              </li>`
              )
              .join('')}
          </ul>
        </div>
      `;
    };

    const onMountClick = (event) => {
      const clear = event.target.closest('[data-recent-clear]');
      if (clear && mount.contains(clear)) {
        writeRecentSearches([]);
        renderRecent();
        announceStatus(
          element.getAttribute('data-recent-cleared-label') ||
            document.body.getAttribute('data-recent-searches-cleared') ||
            'Recent searches cleared'
        );
        input.focus({ preventScroll: true });
        return;
      }

      const chip = event.target.closest('[data-recent-query]');
      if (!(chip instanceof HTMLElement) || !mount.contains(chip)) return;
      // Re-entry gate — global submit busy-locks submit buttons, not chips (713 parity).
      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (
        chip.classList.contains('is-busy') ||
        form.classList.contains('is-busy') ||
        (submitBtn instanceof HTMLElement && submitBtn.classList.contains('is-busy'))
      ) {
        return;
      }
      const query = chip.getAttribute('data-recent-query') || '';
      if (query) {
        const template =
          document.body.getAttribute('data-recent-search-template') || 'Recent search: [query]';
        announceStatus(template.replaceAll('[query]', query));
      }
      input.value = query;
      rememberRecentSearch(query);
      chip.classList.add('is-busy');
      chip.setAttribute('aria-busy', 'true');
      mount.querySelectorAll('[data-recent-query]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.setAttribute('aria-disabled', 'true');
        el.tabIndex = -1;
      });
      form.requestSubmit();
    };

    mount.addEventListener('click', onMountClick);
    input.addEventListener('keydown', moveRecentFocus);
    mount.addEventListener('keydown', moveRecentFocus);
    renderRecent();

    return () => {
      mount.removeEventListener('click', onMountClick);
      mount.removeEventListener('keydown', moveRecentFocus);
      form.removeEventListener('submit', onFormSubmit);
      input.removeEventListener('keydown', onInputKeyDown);
      input.removeEventListener('keydown', moveRecentFocus);
    };
  };

  const enhanceRelatedProducts = (element) => {
    const url = element.getAttribute('data-url');
    // Grid or SSR empty (with CTAs) — don't re-fetch and wipe focusable chrome.
    if (!url || element.querySelector('.related-products__grid, [data-related-empty]')) return;

    let disposed = false;
    let loadGen = 0;
    let controller = new AbortController();

    const stillNeedsLoad = () =>
      element.isConnected &&
      !disposed &&
      !element.querySelector('.related-products__grid, [data-related-empty]');

    const restoreRelatedFocus = (hadFocus) => {
      if (!hadFocus) return;
      const next =
        element.querySelector(
          [
            '.related-products__grid .card-product__title a[href]',
            '.related-products__empty-actions a[href]',
            '.related-products__empty-actions .button',
          ].join(', ')
        ) || element.querySelector('.related-products__heading');
      if (!(next instanceof HTMLElement)) return;
      if (
        next.matches('h1, h2, h3, h4') &&
        !next.hasAttribute('tabindex')
      ) {
        next.setAttribute('tabindex', '-1');
      }
      next.focus({ preventScroll: true });
    };

    const applyMarkup = (markup) => {
      const active = document.activeElement;
      const hadFocus = active instanceof HTMLElement && element.contains(active);
      element.hidden = false;
      element.innerHTML = markup;
      element.removeAttribute('aria-busy');
      if (typeof enhance === 'function') {
        enhance(element);
      } else if (typeof enhanceMotion === 'function') {
        enhanceMotion(element);
      }
      restoreRelatedFocus(hadFocus);
    };

    const showEmptyFallback = ({ failed = false } = {}) => {
      const active = document.activeElement;
      const hadFocus = active instanceof HTMLElement && element.contains(active);
      const template = element.querySelector('[data-related-empty-template]');
      const inner = element.querySelector('.related-products__inner');
      if (!(template instanceof HTMLTemplateElement) || !(inner instanceof HTMLElement)) {
        element.hidden = true;
        element.removeAttribute('aria-busy');
        if (failed) {
          announceStatus(
            element.getAttribute('data-error-label') || 'Could not load related products'
          );
        }
        if (hadFocus) {
          const main = document.getElementById('MainContent');
          if (main instanceof HTMLElement) main.focus({ preventScroll: true });
        }
        return;
      }
      element.hidden = false;
      inner.replaceChildren(template.content.cloneNode(true));
      element.removeAttribute('aria-busy');
      announceStatus(
        failed
          ? element.getAttribute('data-error-label') || 'Could not load related products'
          : element.getAttribute('data-empty-label') ||
              inner.querySelector('[data-related-empty] .related-products__empty-title')?.textContent?.trim() ||
              'No related products right now'
      );
      if (typeof enhance === 'function') {
        enhance(element);
      } else if (typeof enhanceMotion === 'function') {
        enhanceMotion(element);
      }
      restoreRelatedFocus(hadFocus);
    };

    const load = async () => {
      if (!stillNeedsLoad()) return;
      // Cancel any prior attempt, then start a new generation.
      controller.abort();
      controller = new AbortController();
      const gen = ++loadGen;

      element.setAttribute('aria-busy', 'true');
      announceStatus(
        element.getAttribute('data-loading-label') ||
          document.body.getAttribute('data-loading-announce') ||
          'Loading related products'
      );
      try {
        const response = await fetchWithTimeout(
          url,
          {
            signal: controller.signal,
            headers: { Accept: 'text/html' },
          },
          20000
        );
        if (!response.ok) throw new Error('Recommendations failed');
        const html = await withTimeout(response.text(), 20000);
        if (disposed || gen !== loadGen) return;
        if (!stillNeedsLoad()) {
          element.removeAttribute('aria-busy');
          return;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.related-products');
        if (!section) {
          showEmptyFallback();
          return;
        }

        const hasGrid = Boolean(section.querySelector('.related-products__grid'));
        const hasEmpty = Boolean(section.querySelector('[data-related-empty]'));
        if (hasGrid || hasEmpty) {
          const emptyTitle =
            section.querySelector('[data-related-empty] .related-products__empty-title')?.textContent?.trim() ||
            '';
          const count = section.querySelectorAll('.card-product').length;
          applyMarkup(section.innerHTML);
          if (hasGrid) {
            const template =
              element.getAttribute('data-loaded-label') || 'Related products loaded. [count] items.';
            announceStatus(template.replaceAll('[count]', String(count)));
          } else {
            announceStatus(
              emptyTitle ||
                element.getAttribute('data-empty-label') ||
                'No related products right now'
            );
          }
          return;
        }

        showEmptyFallback();
      } catch (error) {
        if (disposed || gen !== loadGen) return;
        // Soft abort bumps loadGen before aborting — hang timeout keeps gen and must recover.
        if (error && error.name === 'AbortError') {
          if (stillNeedsLoad()) showEmptyFallback({ failed: true });
          else element.removeAttribute('aria-busy');
          return;
        }
        showEmptyFallback({ failed: true });
      }
    };

    // Soft abort only — don't permanently kill recovery until enhance teardown.
    const softAbort = () => {
      loadGen += 1;
      controller.abort();
    };

    const onPageHide = () => softAbort();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        softAbort();
        return;
      }
      if (document.visibilityState === 'visible' && stillNeedsLoad()) {
        load();
      }
    };

    load();
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      softAbort();
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  };

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement) {
      const isLocalization = target.matches('[data-localization-select]');
      const isFacetsSort = target.matches('[data-facets-sort]');
      if (!isLocalization && !isFacetsSort) return;
      // Prevent double-submit while the page is navigating after a prior change.
      if (target.disabled || target.classList.contains('is-busy')) return;

      const form = target.closest('form');
      if (!form) return;

      announceStatus(
        isLocalization
          ? document.body.getAttribute('data-localization-loading') ||
              'Updating country and language'
          : document.body.getAttribute('data-sort-loading') || 'Updating sort'
      );
      // Facets Apply parity: leave the select before it becomes disabled mid-navigation.
      const focusFallback =
        form.querySelector('button[type="submit"]') ||
        form.closest('[data-facets-drawer]')?.querySelector(':scope > summary') ||
        document.getElementById('MainContent');
      if (
        focusFallback instanceof HTMLElement &&
        document.activeElement instanceof HTMLElement &&
        (document.activeElement === target ||
          form.contains(document.activeElement))
      ) {
        if (!focusFallback.hasAttribute('tabindex') && focusFallback.id === 'MainContent') {
          focusFallback.setAttribute('tabindex', '-1');
        }
        focusFallback.focus({ preventScroll: true });
      }
      const busySelects = isLocalization
        ? form.querySelectorAll('[data-localization-select]')
        : [target];
      // Keep selects enabled — disabled controls are omitted from the POST/GET set
      // (price fields use readOnly for the same reason). CSS pointer-events locks UI.
      busySelects.forEach((select) => {
        if (!(select instanceof HTMLSelectElement)) return;
        select.classList.add('is-busy');
        select.setAttribute('aria-busy', 'true');
        select.setAttribute('aria-disabled', 'true');
      });
      form.submit();
      return;
    }

    // List/boolean collection filters apply immediately; price range still uses Apply.
    if (
      target instanceof HTMLInputElement &&
      target.type === 'checkbox' &&
      !target.disabled
    ) {
      const form = target.closest('form.facets__form');
      if (!(form instanceof HTMLFormElement)) return;
      if (form.classList.contains('is-busy') || form.getAttribute('aria-busy') === 'true') return;

      announceStatus(
        document.body.getAttribute('data-filters-loading') || 'Updating filters'
      );
      // Don't mark form busy here — submit listener owns that (would bail on requestSubmit).
      // Leave the checkbox before form.is-busy pointer-events-locks the drawer.
      const drawer = form.closest('[data-facets-drawer]');
      const summary = drawer?.querySelector(':scope > summary');
      if (
        summary instanceof HTMLElement &&
        document.activeElement instanceof HTMLElement &&
        form.contains(document.activeElement)
      ) {
        summary.focus({ preventScroll: true });
      }
      // Don't disable checkboxes — disabled inputs are dropped from the GET.
      // Form is-busy (submit listener / fallback below) pointer-events-locks the drawer.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        const apply = form.querySelector('button[type="submit"]');
        if (apply instanceof HTMLButtonElement) {
          apply.disabled = true;
          apply.classList.add('is-busy');
          apply.setAttribute('aria-busy', 'true');
        }
        // Native submit() skips the submit event — mark form busy here.
        form.classList.add('is-busy');
        form.setAttribute('aria-busy', 'true');
        form.submit();
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const facetsDrawer = document.querySelector('[data-facets-drawer][open]');
      if (facetsDrawer instanceof HTMLDetailsElement && facetsDrawer.contains(document.activeElement)) {
        const focusables = getFocusable(facetsDrawer);
        if (focusables.length > 0) {
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus({ preventScroll: true });
            return;
          }
          if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus({ preventScroll: true });
            return;
          }
        }
      }
    }

    if (event.key !== 'Escape') return;

    const target = event.target instanceof HTMLElement ? event.target : null;

    // Inside dialogs (mobile menu / search), close nested details before the dialog.
    const dialog = target ? target.closest('dialog[open]') : null;
    if (!(dialog instanceof HTMLDialogElement)) {
      const announcement = target?.closest('[data-announcement-bar]');
      if (
        announcement instanceof HTMLElement &&
        !announcement.hidden &&
        !announcement.hasAttribute('data-announcement-design-mode')
      ) {
        const dismiss = announcement.querySelector('[data-announcement-dismiss]');
        if (dismiss instanceof HTMLElement) {
          event.preventDefault();
          dismiss.click();
          return;
        }
      }
    }
    if (dialog instanceof HTMLDialogElement) {
      const nestedOpen = target.closest('details[open]');
      if (
        nestedOpen instanceof HTMLDetailsElement &&
        dialog.contains(nestedOpen) &&
        nestedOpen !== dialog
      ) {
        event.preventDefault();
        nestedOpen.open = false;
        const summary = nestedOpen.querySelector('summary');
        if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
      }
      return;
    }

    const drawer = target ? target.closest('[data-facets-drawer]') : null;
    if (drawer instanceof HTMLDetailsElement && drawer.open) {
      event.preventDefault();
      drawer.open = false;
      const summary = drawer.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
      return;
    }

    const navDetails = target ? target.closest('.site-header__details') : null;
    if (navDetails instanceof HTMLDetailsElement && navDetails.open) {
      event.preventDefault();
      navDetails.open = false;
      const summary = navDetails.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
      return;
    }

    const disclosure = target ? target.closest('details.disclosure') : null;
    if (disclosure instanceof HTMLDetailsElement && disclosure.open) {
      event.preventDefault();
      disclosure.open = false;
      const summary = disclosure.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Home' &&
      event.key !== 'End'
    ) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (
      target.closest(
        'input, textarea, select, [contenteditable="true"], dialog[open], [data-product-gallery], [data-thumb]'
      )
    ) {
      return;
    }

    const breadcrumbs = target.closest('.breadcrumbs');
    if (breadcrumbs instanceof HTMLElement && breadcrumbs.contains(document.activeElement)) {
      const links = Array.from(breadcrumbs.querySelectorAll('a.breadcrumbs__link[href]')).filter(
        (el) => el instanceof HTMLElement
      );
      const index = links.indexOf(target);
      if (index >= 0 && links.length > 0) {
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
        else if (event.key === 'ArrowRight') nextIndex = Math.min(links.length - 1, index + 1);
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = links.length - 1;
        if (nextIndex !== index && links[nextIndex] instanceof HTMLElement) {
          event.preventDefault();
          links[nextIndex].focus({ preventScroll: true });
        }
        return;
      }
    }

    // Blog / article tag chip trails — breadcrumb-style Arrow/Home/End focus roving.
    const tagToolbar = target.closest('.blog-page__filters, .article-page__tags');
    if (tagToolbar instanceof HTMLElement && tagToolbar.contains(document.activeElement)) {
      const links = [
        ...tagToolbar.querySelectorAll('a.blog-page__tag[href], a.article-page__tag[href]'),
        ...tagToolbar.querySelectorAll('a.blog-page__tags-clear[href]'),
      ].filter((el) => el instanceof HTMLElement);
      const index = links.indexOf(target);
      if (index >= 0 && links.length > 0) {
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
        else if (event.key === 'ArrowRight') nextIndex = Math.min(links.length - 1, index + 1);
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = links.length - 1;
        if (nextIndex !== index && links[nextIndex] instanceof HTMLElement) {
          event.preventDefault();
          links[nextIndex].focus({ preventScroll: true });
        }
        return;
      }
    }

    // Facet remove chips / Clear — same roving as blog tags (720 has re-entry; 727 had no facets trail).
    const facetsChipTrail = target.closest('.facets__summary, .facets__active');
    if (facetsChipTrail instanceof HTMLElement && facetsChipTrail.contains(document.activeElement)) {
      const links = Array.from(
        facetsChipTrail.querySelectorAll(
          'a.facets__summary-chip[href], a.facets__active-chip[href], a.facets__summary-clear[href], a.facets__clear[href]'
        )
      ).filter((el) => el instanceof HTMLElement);
      const index = links.indexOf(target);
      if (index >= 0 && links.length > 0) {
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
        else if (event.key === 'ArrowRight') nextIndex = Math.min(links.length - 1, index + 1);
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = links.length - 1;
        if (nextIndex !== index && links[nextIndex] instanceof HTMLElement) {
          event.preventDefault();
          links[nextIndex].focus({ preventScroll: true });
        }
        return;
      }
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const articleNav = target.closest('.article-page__nav');
    if (articleNav instanceof HTMLElement && articleNav.contains(document.activeElement)) {
      // Don't synthesize another click while prev/next is already in flight (722 parity).
      if (
        articleNav.querySelector(
          'a.article-page__nav-link--prev.is-busy, a.article-page__nav-link--next.is-busy'
        )
      ) {
        event.preventDefault();
        return;
      }
      const prev = articleNav.querySelector('a.article-page__nav-link--prev[href]');
      const next = articleNav.querySelector('a.article-page__nav-link--next[href]');
      if (event.key === 'ArrowLeft' && prev instanceof HTMLElement) {
        event.preventDefault();
        prev.click();
        return;
      }
      if (event.key === 'ArrowRight' && next instanceof HTMLElement) {
        event.preventDefault();
        next.click();
        return;
      }
    }

    const pagination = target.closest('nav');
    if (!(pagination instanceof HTMLElement)) return;
    if (!pagination.querySelector('.page, .next, .prev, .previous')) return;
    if (!pagination.contains(document.activeElement)) return;
    // Don't synthesize another click while a pagination nav is already in flight.
    if (
      pagination.querySelector(
        '.page a.is-busy, .next a.is-busy, .prev a.is-busy, .previous a.is-busy'
      )
    ) {
      event.preventDefault();
      return;
    }

    const prev = pagination.querySelector('.prev a[href], .previous a[href]');
    const next = pagination.querySelector('.next a[href]');
    if (event.key === 'ArrowLeft' && prev instanceof HTMLElement) {
      event.preventDefault();
      prev.click();
      return;
    }
    if (event.key === 'ArrowRight' && next instanceof HTMLElement) {
      event.preventDefault();
      next.click();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (
      event.key !== 'ArrowDown' &&
      event.key !== 'ArrowUp' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Home' &&
      event.key !== 'End'
    ) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest('input, textarea, select, [contenteditable="true"]')) return;

    const navDetails =
      target.closest('.site-header__details') ||
      target.closest('.site-header__mobile-menu details');
    if (navDetails instanceof HTMLDetailsElement && navDetails.open) {
      const summary = navDetails.querySelector(':scope > summary');
      const items = getFocusable(navDetails).filter((el) => el !== summary);
      const onSummary =
        summary instanceof HTMLElement && (target === summary || summary.contains(target));
      const index = items.findIndex((el) => el === target || el.contains(target));

      if (onSummary && event.key === 'ArrowDown' && items[0] instanceof HTMLElement) {
        event.preventDefault();
        items[0].focus({ preventScroll: true });
        return;
      }

      if (index >= 0) {
        if (event.key === 'ArrowLeft' && summary instanceof HTMLElement) {
          event.preventDefault();
          summary.focus({ preventScroll: true });
          return;
        }
        let nextIndex = index;
        if (event.key === 'ArrowDown') {
          nextIndex = Math.min(index + 1, items.length - 1);
        } else if (event.key === 'ArrowUp') {
          if (index === 0 && summary instanceof HTMLElement) {
            event.preventDefault();
            summary.focus({ preventScroll: true });
            return;
          }
          nextIndex = Math.max(index - 1, 0);
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = items.length - 1;
        } else {
          nextIndex = -1;
        }
        if (nextIndex >= 0 && items[nextIndex] instanceof HTMLElement) {
          event.preventDefault();
          items[nextIndex].focus({ preventScroll: true });
          return;
        }
      }
    }

    // Desktop menubar: Left/Right between top-level items; close the open submenu when leaving it.
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (target.closest('dialog[open]')) return;
    const desktopNav = target.closest('.site-header__nav');
    if (!(desktopNav instanceof HTMLElement)) return;

    const topLevel = Array.from(
      desktopNav.querySelectorAll(
        '.site-header__menu > .site-header__item > a.site-header__link, .site-header__menu > .site-header__item > details > summary'
      )
    ).filter((el) => el instanceof HTMLElement);

    let currentIndex = topLevel.findIndex(
      (el) => el === target || el.contains(target) || (el.closest('details')?.contains(target) ?? false)
    );
    // When focus is inside a submenu, map to that details' summary.
    if (currentIndex < 0) {
      const details = target.closest('.site-header__details');
      if (details instanceof HTMLDetailsElement) {
        const summary = details.querySelector(':scope > summary');
        currentIndex = topLevel.indexOf(summary);
      }
    }
    if (currentIndex < 0) return;

    const nextIndex =
      event.key === 'ArrowRight'
        ? Math.min(currentIndex + 1, topLevel.length - 1)
        : Math.max(currentIndex - 1, 0);
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    const currentDetails = topLevel[currentIndex]?.closest('details.site-header__details');
    if (currentDetails instanceof HTMLDetailsElement) currentDetails.open = false;

    const next = topLevel[nextIndex];
    if (!(next instanceof HTMLElement)) return;
    const nextDetails = next.closest('details.site-header__details');
    if (nextDetails instanceof HTMLDetailsElement) {
      nextDetails.open = true;
      next.focus({ preventScroll: true });
      return;
    }
    next.focus({ preventScroll: true });
  });

  window.addEventListener('offline', () => {
    announceStatus(
      document.body.getAttribute('data-offline-announce') ||
        'You are offline. Some actions may not work until you reconnect.'
    );
  });
  window.addEventListener('online', () => {
    announceStatus(document.body.getAttribute('data-online-announce') || 'You are back online.');
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    const filterNavLink =
      target instanceof Element
        ? target.closest(
            '.facets__summary-chip, .facets__summary-clear, .facets__active-chip, .facets__clear'
          )
        : null;
    if (filterNavLink instanceof HTMLAnchorElement && filterNavLink.href) {
      // Re-entry gate — Apply/Enter and checkbox auto-apply already bail (717/576).
      const facetsForm =
        filterNavLink.closest('form.facets__form') ||
        document.querySelector('form.facets__form');
      const formBusy =
        facetsForm instanceof HTMLFormElement &&
        (facetsForm.classList.contains('is-busy') ||
          facetsForm.getAttribute('aria-busy') === 'true');
      const chipBusy = Boolean(
        document.querySelector(
          '.facets__summary-chip.is-busy, .facets__summary-clear.is-busy, .facets__active-chip.is-busy, .facets__clear.is-busy'
        )
      );
      if (
        filterNavLink.classList.contains('is-busy') ||
        filterNavLink.getAttribute('aria-busy') === 'true' ||
        formBusy ||
        chipBusy
      ) {
        event.preventDefault();
        return;
      }
      announceStatus(document.body.getAttribute('data-filters-loading') || 'Updating filters');
      filterNavLink.classList.add('is-busy');
      filterNavLink.setAttribute('aria-busy', 'true');
    }

    // Blog tag filters / clear — speak the control before navigation (facets parity).
    const blogTagLink =
      target instanceof Element
        ? target.closest(
            '.blog-page__tag, .blog-page__tags-clear, .blog-card__tag, .article-page__tag'
          )
        : null;
    if (blogTagLink instanceof HTMLAnchorElement && blogTagLink.href) {
      // Re-entry gate — facet chips (720) / pagination (722) parity for tag navigations.
      const tagBusy = Boolean(
        document.querySelector(
          [
            '.blog-page__tag.is-busy',
            '.blog-page__tags-clear.is-busy',
            '.blog-card__tag.is-busy',
            '.article-page__tag.is-busy',
            '.blog-page__empty-actions a.is-busy',
          ].join(', ')
        )
      );
      if (
        blogTagLink.classList.contains('is-busy') ||
        blogTagLink.getAttribute('aria-busy') === 'true' ||
        tagBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        blogTagLink.getAttribute('aria-label') ||
        blogTagLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      blogTagLink.classList.add('is-busy');
      blogTagLink.setAttribute('aria-busy', 'true');
    }

    // Speak the pagination control before navigation (page number / Next / Previous).
    const paginationLink =
      target instanceof Element
        ? target.closest(
            'nav[aria-label] .page a[href], nav[aria-label] .next a[href], nav[aria-label] .prev a[href], nav[aria-label] .previous a[href]'
          )
        : null;
    if (paginationLink instanceof HTMLAnchorElement && paginationLink.href) {
      const nav = paginationLink.closest('nav');
      if (nav?.querySelector('.page, .next, .prev, .previous')) {
        // Re-entry gate — ArrowLeft/Right synthesizes .click(); key-repeat must not stack navigations.
        const navBusy = Boolean(
          nav.querySelector(
            '.page a.is-busy, .next a.is-busy, .prev a.is-busy, .previous a.is-busy'
          )
        );
        if (
          paginationLink.classList.contains('is-busy') ||
          paginationLink.getAttribute('aria-busy') === 'true' ||
          navBusy
        ) {
          event.preventDefault();
          return;
        }
        const label = (
          paginationLink.getAttribute('aria-label') ||
          paginationLink.textContent ||
          ''
        )
          .replace(/\s+/g, ' ')
          .trim();
        if (label) announceStatus(label);
        paginationLink.classList.add('is-busy');
        paginationLink.setAttribute('aria-busy', 'true');
      }
    }

    // Article previous/next — speak direction + title before navigation.
    const articleNavLink =
      target instanceof Element
        ? target.closest('a.article-page__nav-link--prev[href], a.article-page__nav-link--next[href]')
        : null;
    if (articleNavLink instanceof HTMLAnchorElement && articleNavLink.href) {
      // Re-entry gate — ArrowLeft/Right synthesizes .click(); key-repeat must not stack navigations.
      const articleNavRoot = articleNavLink.closest('.article-page__nav');
      const navBusy = Boolean(
        articleNavRoot?.querySelector(
          'a.article-page__nav-link--prev.is-busy, a.article-page__nav-link--next.is-busy'
        )
      );
      if (
        articleNavLink.classList.contains('is-busy') ||
        articleNavLink.getAttribute('aria-busy') === 'true' ||
        navBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        articleNavLink.getAttribute('aria-label') ||
        articleNavLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      articleNavLink.classList.add('is-busy');
      articleNavLink.setAttribute('aria-busy', 'true');
    }

    // Breadcrumb trail — speak the crumb before navigation.
    const breadcrumbLink =
      target instanceof Element ? target.closest('a.breadcrumbs__link[href]') : null;
    if (breadcrumbLink instanceof HTMLAnchorElement && breadcrumbLink.href) {
      // Re-entry gate — blog tags (737) / predictive (740) parity.
      const crumbBusy = Boolean(document.querySelector('a.breadcrumbs__link.is-busy'));
      if (
        breadcrumbLink.classList.contains('is-busy') ||
        breadcrumbLink.getAttribute('aria-busy') === 'true' ||
        crumbBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        breadcrumbLink.getAttribute('aria-label') ||
        breadcrumbLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      breadcrumbLink.classList.add('is-busy');
      breadcrumbLink.setAttribute('aria-busy', 'true');
    }

    // Featured view-all + 404 collection chips — speak before navigation.
    const wayfindingLink =
      target instanceof Element
        ? target.closest(
            'a.featured-collection__view-all[href], a.utility-page__chip[href]'
          )
        : null;
    if (wayfindingLink instanceof HTMLAnchorElement && wayfindingLink.href) {
      // Re-entry gate — breadcrumb (750) / empty-CTA parity.
      const wayBusy = Boolean(
        document.querySelector(
          'a.featured-collection__view-all.is-busy, a.utility-page__chip.is-busy'
        )
      );
      if (
        wayfindingLink.classList.contains('is-busy') ||
        wayfindingLink.getAttribute('aria-busy') === 'true' ||
        wayBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        wayfindingLink.getAttribute('aria-label') ||
        wayfindingLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      wayfindingLink.classList.add('is-busy');
      wayfindingLink.setAttribute('aria-busy', 'true');
    }

    // New-tab links (service external, social, etc.) — speak label + destination context.
    const newTabLink =
      target instanceof Element
        ? target.closest('a[href][target="_blank"]')
        : null;
    if (newTabLink instanceof HTMLAnchorElement && newTabLink.href) {
      const img = newTabLink.querySelector('img[alt]');
      const label = (
        newTabLink.getAttribute('aria-label') ||
        (img instanceof HTMLImageElement ? img.alt : '') ||
        newTabLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      const opens =
        document.body.getAttribute('data-opens-new-tab') || 'Opens in a new tab';
      announceStatus(label ? `${label}. ${opens}` : opens);
    }

    // Empty-state recovery CTAs — speak before navigation.
    const emptyCta =
      target instanceof Element
        ? target.closest(
            '[class*="__empty-actions"] a[href], [class*="__empty"] > a.button[href], .utility-page__actions a[href], .predictive-search__empty a[href]'
          )
        : null;
    if (emptyCta instanceof HTMLAnchorElement && emptyCta.href) {
      // Blog tagged-empty Clear shares in-flight state with the filter toolbar (737).
      const blogEmptyClear = emptyCta.closest('.blog-page__empty-actions');
      const blogTagBusy = Boolean(
        blogEmptyClear &&
          document.querySelector(
            '.blog-page__tag.is-busy, .blog-page__tags-clear.is-busy, .blog-page__empty-actions a.is-busy'
          )
      );
      if (
        emptyCta.classList.contains('is-busy') ||
        emptyCta.getAttribute('aria-busy') === 'true' ||
        blogTagBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        emptyCta.getAttribute('aria-label') ||
        emptyCta.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      emptyCta.classList.add('is-busy');
      emptyCta.setAttribute('aria-busy', 'true');
    }

    // Predictive suggestion / view-all — speak before navigation from the search dialog.
    const predictiveLink =
      target instanceof Element
        ? target.closest(
            'a.predictive-search__item[href], a.predictive-search__view-all[href], .predictive-search__footer a[href]'
          )
        : null;
    if (predictiveLink instanceof HTMLAnchorElement && predictiveLink.href) {
      // Re-entry gate — recent chips (726) and blog tags (737) already bail; results must too.
      const predictiveBusy = Boolean(
        document.querySelector(
          'a.predictive-search__item.is-busy, a.predictive-search__view-all.is-busy, .predictive-search__footer a.is-busy'
        )
      );
      if (
        predictiveLink.classList.contains('is-busy') ||
        predictiveLink.getAttribute('aria-busy') === 'true' ||
        predictiveBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        predictiveLink.getAttribute('aria-label') ||
        predictiveLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      predictiveLink.classList.add('is-busy');
      predictiveLink.setAttribute('aria-busy', 'true');
    }

    // Header logo — speak shop/home label before navigating to the storefront root.
    const logoLink =
      target instanceof Element ? target.closest('a.site-header__logo[href]') : null;
    if (logoLink instanceof HTMLAnchorElement && logoLink.href) {
      // Re-entry gate — breadcrumb (750) parity for the most-clicked storefront link.
      if (
        logoLink.classList.contains('is-busy') ||
        logoLink.getAttribute('aria-busy') === 'true'
      ) {
        event.preventDefault();
        return;
      }
      const img = logoLink.querySelector('img[alt]');
      const label = (
        logoLink.getAttribute('aria-label') ||
        (img instanceof HTMLImageElement ? img.alt : '') ||
        logoLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      logoLink.classList.add('is-busy');
      logoLink.setAttribute('aria-busy', 'true');
    }

    // Announcement bar link — speak before navigation (dismiss already announces).
    const announcementLink =
      target instanceof Element
        ? target.closest('a.announcement-bar__link[href]')
        : null;
    if (announcementLink instanceof HTMLAnchorElement && announcementLink.href) {
      // Re-entry gate — Enter-repeat / double-activate must not stack navigations.
      if (
        announcementLink.classList.contains('is-busy') ||
        announcementLink.getAttribute('aria-busy') === 'true'
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        announcementLink.getAttribute('aria-label') ||
        announcementLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      announcementLink.classList.add('is-busy');
      announcementLink.setAttribute('aria-busy', 'true');
    }

    // Mobile menu drawer links / search — speak before leaving the dialog.
    const mobileMenuLink =
      target instanceof Element
        ? target.closest(
            '[data-menu-drawer] .site-header__mobile-menu a[href], a.site-header__drawer-search[href]'
          )
        : null;
    if (mobileMenuLink instanceof HTMLAnchorElement && mobileMenuLink.href) {
      // Sibling-busy gate — one in-flight drawer nav at a time (blog-tag 737 parity).
      const drawerBusy = Boolean(
        document.querySelector(
          '[data-menu-drawer] .site-header__mobile-menu a.is-busy, a.site-header__drawer-search.is-busy'
        )
      );
      if (
        mobileMenuLink.classList.contains('is-busy') ||
        mobileMenuLink.getAttribute('aria-busy') === 'true' ||
        drawerBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        mobileMenuLink.getAttribute('aria-label') ||
        mobileMenuLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      mobileMenuLink.classList.add('is-busy');
      mobileMenuLink.setAttribute('aria-busy', 'true');
    }

    // Desktop primary nav + submenu links — speak before navigation.
    const desktopNavLink =
      target instanceof Element
        ? target.closest(
            '.site-header__nav a.site-header__link[href], .site-header__nav .site-header__submenu a[href]'
          )
        : null;
    if (desktopNavLink instanceof HTMLAnchorElement && desktopNavLink.href) {
      // Sibling-busy gate — Enter-repeat across primary/submenu must not stack.
      const navBusy = Boolean(
        document.querySelector(
          '.site-header__nav a.site-header__link.is-busy, .site-header__nav .site-header__submenu a.is-busy'
        )
      );
      if (
        desktopNavLink.classList.contains('is-busy') ||
        desktopNavLink.getAttribute('aria-busy') === 'true' ||
        navBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        desktopNavLink.getAttribute('aria-label') ||
        desktopNavLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      desktopNavLink.classList.add('is-busy');
      desktopNavLink.setAttribute('aria-busy', 'true');
    }

    // Header cart — when the drawer is off, speak before navigating to the cart page.
    const cartPageNavLink =
      target instanceof Element
        ? target.closest('a[data-cart-link][href]:not([data-cart-drawer-open])')
        : null;
    if (cartPageNavLink instanceof HTMLAnchorElement && cartPageNavLink.href) {
      // Re-entry gate — logo (751) parity for the cart icon when drawer is off.
      if (
        cartPageNavLink.classList.contains('is-busy') ||
        cartPageNavLink.getAttribute('aria-busy') === 'true'
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        cartPageNavLink.getAttribute('aria-label') ||
        cartPageNavLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      cartPageNavLink.classList.add('is-busy');
      cartPageNavLink.setAttribute('aria-busy', 'true');
    }

    // Cart drawer product / view-cart links — speak before leaving the dialog.
    const cartDrawerNavLink =
      target instanceof Element
        ? target.closest(
            '[data-cart-drawer] a.cart-drawer__media[href], [data-cart-drawer] a.cart-drawer__name[href], [data-cart-drawer] .cart-drawer__footer a.button--secondary[href]'
          )
        : null;
    if (cartDrawerNavLink instanceof HTMLAnchorElement && cartDrawerNavLink.href) {
      // Sibling-busy gate — one in-flight leave-drawer navigation at a time.
      const drawerNavBusy = Boolean(
        document.querySelector(
          [
            '[data-cart-drawer] a.cart-drawer__media.is-busy',
            '[data-cart-drawer] a.cart-drawer__name.is-busy',
            '[data-cart-drawer] .cart-drawer__footer a.button--secondary.is-busy',
          ].join(', ')
        )
      );
      if (
        cartDrawerNavLink.classList.contains('is-busy') ||
        cartDrawerNavLink.getAttribute('aria-busy') === 'true' ||
        drawerNavBusy
      ) {
        event.preventDefault();
        return;
      }
      const img = cartDrawerNavLink.querySelector('img[alt]');
      const label = (
        cartDrawerNavLink.getAttribute('aria-label') ||
        (img instanceof HTMLImageElement ? img.alt : '') ||
        cartDrawerNavLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      cartDrawerNavLink.classList.add('is-busy');
      cartDrawerNavLink.setAttribute('aria-busy', 'true');
    }

    // Cart page product media/title links — speak before navigating to the PDP.
    const cartPageProductLink =
      target instanceof Element
        ? target.closest('a.cart-page__media[href], .cart-page__name a[href]')
        : null;
    if (cartPageProductLink instanceof HTMLAnchorElement && cartPageProductLink.href) {
      // Sibling-busy — media + title on the same line must not double-navigate.
      const lineBusy = Boolean(
        document.querySelector(
          'a.cart-page__media.is-busy, .cart-page__name a.is-busy'
        )
      );
      if (
        cartPageProductLink.classList.contains('is-busy') ||
        cartPageProductLink.getAttribute('aria-busy') === 'true' ||
        lineBusy
      ) {
        event.preventDefault();
        return;
      }
      const img = cartPageProductLink.querySelector('img[alt]');
      const label = (
        cartPageProductLink.getAttribute('aria-label') ||
        (img instanceof HTMLImageElement ? img.alt : '') ||
        cartPageProductLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      cartPageProductLink.classList.add('is-busy');
      cartPageProductLink.setAttribute('aria-busy', 'true');
    }

    // Catalog cards — product/blog titles and collection cards before navigation.
    const catalogCardLink =
      target instanceof Element
        ? target.closest(
            '.card-product__title a[href], .blog-card__title a[href], a.blog-card__link[href], a.collection-list__card[href], a.collections-page__card[href]'
          )
        : null;
    if (catalogCardLink instanceof HTMLAnchorElement && catalogCardLink.href) {
      // Same-card media may also navigate — treat card title as busy with its media peer.
      const card = catalogCardLink.closest('.card-product, .blog-card');
      const peerMediaBusy = Boolean(
        card?.querySelector(
          'a.card-product__media.is-busy, a.blog-card__media.is-busy'
        )
      );
      if (
        catalogCardLink.classList.contains('is-busy') ||
        catalogCardLink.getAttribute('aria-busy') === 'true' ||
        peerMediaBusy
      ) {
        event.preventDefault();
        return;
      }
      const titleEl = catalogCardLink.querySelector(
        '.collection-list__title, .collections-page__name'
      );
      const label = (
        catalogCardLink.getAttribute('aria-label') ||
        (titleEl ? titleEl.textContent : '') ||
        catalogCardLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      catalogCardLink.classList.add('is-busy');
      catalogCardLink.setAttribute('aria-busy', 'true');
    }

    // Decorative card media (keyboard-hidden) — speak the card title before navigation.
    const catalogMediaLink =
      target instanceof Element
        ? target.closest(
            'a.card-product__media[href], a.blog-card__media[href], a.search-result-card__media[href]'
          )
        : null;
    if (catalogMediaLink instanceof HTMLAnchorElement && catalogMediaLink.href) {
      const card = catalogMediaLink.closest(
        '.card-product, .blog-card, .search-result-card'
      );
      // Peer title busy — clicking media then title (or double-activate) must not stack.
      const peerTitleBusy = Boolean(
        card?.querySelector(
          '.card-product__title a.is-busy, .blog-card__title a.is-busy, a.blog-card__link.is-busy, .search-result-card__title a.is-busy'
        )
      );
      if (
        catalogMediaLink.classList.contains('is-busy') ||
        catalogMediaLink.getAttribute('aria-busy') === 'true' ||
        peerTitleBusy
      ) {
        event.preventDefault();
        return;
      }
      const titleEl = card
        ? card.querySelector(
            '.card-product__title a, .blog-card__title a, .blog-card__title, .search-result-card__title a, .search-result-card__title'
          )
        : null;
      const img = catalogMediaLink.querySelector('img[alt]');
      const label = (
        (titleEl ? titleEl.textContent : '') ||
        (img instanceof HTMLImageElement ? img.alt : '') ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      catalogMediaLink.classList.add('is-busy');
      catalogMediaLink.setAttribute('aria-busy', 'true');
    }

    // Section CTAs — hero, media-with-text, and feature-list before navigation.
    const sectionCtaLink =
      target instanceof Element
        ? target.closest(
            'a.hero__cta[href], a.hero__cta-secondary[href], a.media-with-text__cta[href], a.media-with-text__cta-secondary[href], a.feature-list__link[href]'
          )
        : null;
    if (sectionCtaLink instanceof HTMLAnchorElement && sectionCtaLink.href) {
      // Same-page hash scrolls in place — busy would stick (rteLink / service 747 parity).
      let samePageHash = false;
      try {
        const url = new URL(sectionCtaLink.href, window.location.href);
        samePageHash =
          url.origin === window.location.origin &&
          url.pathname === window.location.pathname &&
          url.search === window.location.search &&
          Boolean(url.hash);
      } catch (error) {
        samePageHash = false;
      }
      // Re-entry only for real navigations — hash CTAs never take busy.
      if (
        !samePageHash &&
        (sectionCtaLink.classList.contains('is-busy') ||
          sectionCtaLink.getAttribute('aria-busy') === 'true')
      ) {
        event.preventDefault();
        return;
      }
      const titleEl = sectionCtaLink.querySelector('.feature-list__title, .feature-list__cta');
      const label = (
        sectionCtaLink.getAttribute('aria-label') ||
        (titleEl ? titleEl.textContent : '') ||
        sectionCtaLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      if (!samePageHash) {
        sectionCtaLink.classList.add('is-busy');
        sectionCtaLink.setAttribute('aria-busy', 'true');
      }
    }

    // Search result titles — speak before navigating to the matched resource.
    const searchResultLink =
      target instanceof Element
        ? target.closest('.search-result-card__title a[href]')
        : null;
    if (searchResultLink instanceof HTMLAnchorElement && searchResultLink.href) {
      // Peer media busy — catalog card (title↔media) parity for search results.
      const searchCard = searchResultLink.closest('.search-result-card');
      const peerMediaBusy = Boolean(
        searchCard?.querySelector('a.search-result-card__media.is-busy')
      );
      if (
        searchResultLink.classList.contains('is-busy') ||
        searchResultLink.getAttribute('aria-busy') === 'true' ||
        peerMediaBusy
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        searchResultLink.getAttribute('aria-label') ||
        searchResultLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      searchResultLink.classList.add('is-busy');
      searchResultLink.setAttribute('aria-busy', 'true');
    }

    // Footer logo / nav / legal / contact / copyright brand — speak before navigation.
    const footerNavLink =
      target instanceof Element
        ? target.closest(
            'a.site-footer__logo[href], .site-footer__nav a[href], a.site-footer__legal-link[href], .site-footer__contact a[href], .site-footer__copyright a[href]'
          )
        : null;
    if (footerNavLink instanceof HTMLAnchorElement && footerNavLink.href) {
      // Social already announces via target=_blank handling above.
      if (footerNavLink.target !== '_blank') {
        // Re-entry gate — desktop nav (755) parity for footer wayfinding.
        if (
          footerNavLink.classList.contains('is-busy') ||
          footerNavLink.getAttribute('aria-busy') === 'true'
        ) {
          event.preventDefault();
          return;
        }
        const label = (
          footerNavLink.getAttribute('aria-label') ||
          footerNavLink.textContent ||
          ''
        )
          .replace(/\s+/g, ' ')
          .trim();
        if (label) announceStatus(label);
        // mailto/tel never unload the page — busy would stick forever.
        if (!/^mailto:|^tel:/i.test(footerNavLink.href)) {
          footerNavLink.classList.add('is-busy');
          footerNavLink.setAttribute('aria-busy', 'true');
        }
      }
    }

    // Password soft links (contact email / store-owner admin) — speak before leaving.
    const passwordSoftLink =
      target instanceof Element
        ? target.closest(
            '.password-page__contact a[href], .password-page__owner a[href]'
          )
        : null;
    if (passwordSoftLink instanceof HTMLAnchorElement && passwordSoftLink.href) {
      const isProtocol = /^mailto:|^tel:/i.test(passwordSoftLink.href);
      // Re-entry for store-owner / non-protocol links; mailto never takes busy (746).
      if (
        !isProtocol &&
        (passwordSoftLink.classList.contains('is-busy') ||
          passwordSoftLink.getAttribute('aria-busy') === 'true')
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        passwordSoftLink.getAttribute('aria-label') ||
        passwordSoftLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      // mailto/tel never unload the page — busy would stick forever.
      if (!isProtocol) {
        passwordSoftLink.classList.add('is-busy');
        passwordSoftLink.setAttribute('aria-busy', 'true');
      }
    }

    // Contact details + article blog eyebrow — speak before mailto/tel/SMS/blog navigation.
    const contactDetailLink =
      target instanceof Element
        ? target.closest(
            '.contact-page__channel[href], .contact-page__list a[href], a.article-page__eyebrow-link[href], a.sms-support-float[href]'
          )
        : null;
    if (contactDetailLink instanceof HTMLAnchorElement && contactDetailLink.href) {
      const href = contactDetailLink.href;
      const isProtocol = /^mailto:|^tel:|^sms:/i.test(href);
      if (
        !isProtocol &&
        (contactDetailLink.classList.contains('is-busy') ||
          contactDetailLink.getAttribute('aria-busy') === 'true')
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        contactDetailLink.getAttribute('aria-label') ||
        contactDetailLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      // mailto/tel/SMS never unload the page — busy would stick forever.
      if (!isProtocol) {
        contactDetailLink.classList.add('is-busy');
        contactDetailLink.setAttribute('aria-busy', 'true');
      }
    }

    // PDP pathway CTAs — custom quote + service inquiry (externals use new-tab announce).
    const pdpPathwayLink =
      target instanceof Element
        ? target.closest(
            'a.product__quote-link[href], .service-product__actions a.button[href^="#"]'
          )
        : null;
    if (pdpPathwayLink instanceof HTMLAnchorElement && pdpPathwayLink.href) {
      // Same-page hash scrolls in place — busy would stick (rteLink parity).
      let samePageHash = false;
      try {
        const url = new URL(pdpPathwayLink.href, window.location.href);
        samePageHash =
          url.origin === window.location.origin &&
          url.pathname === window.location.pathname &&
          url.search === window.location.search &&
          Boolean(url.hash);
      } catch (error) {
        samePageHash = false;
      }
      if (
        !samePageHash &&
        (pdpPathwayLink.classList.contains('is-busy') ||
          pdpPathwayLink.getAttribute('aria-busy') === 'true')
      ) {
        event.preventDefault();
        return;
      }
      const label = (
        pdpPathwayLink.getAttribute('aria-label') ||
        pdpPathwayLink.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim();
      if (label) announceStatus(label);
      if (!samePageHash) {
        pdpPathwayLink.classList.add('is-busy');
        pdpPathwayLink.setAttribute('aria-busy', 'true');
      }
    }

    // Blog card eyebrow tags + linked images — speak before navigation.
    const softContentLink =
      target instanceof Element
        ? target.closest('[data-blog-card-eyebrow] a[href], a.image[href]')
        : null;
    if (softContentLink instanceof HTMLAnchorElement && softContentLink.href) {
      // New-tab handling already speaks for target=_blank images.
      if (softContentLink.target !== '_blank') {
        if (
          softContentLink.classList.contains('is-busy') ||
          softContentLink.getAttribute('aria-busy') === 'true'
        ) {
          event.preventDefault();
          return;
        }
        const img = softContentLink.querySelector('img[alt]');
        const label = (
          softContentLink.getAttribute('aria-label') ||
          (img instanceof HTMLImageElement ? img.alt : '') ||
          softContentLink.textContent ||
          ''
        )
          .replace(/\s+/g, ' ')
          .trim();
        if (label) announceStatus(label);
        // Same-page hash scrolls in place — busy would stick (rteLink parity).
        let samePageHash = false;
        try {
          const url = new URL(softContentLink.href, window.location.href);
          samePageHash =
            url.origin === window.location.origin &&
            url.pathname === window.location.pathname &&
            url.search === window.location.search &&
            Boolean(url.hash);
        } catch (error) {
          samePageHash = false;
        }
        if (!samePageHash && !/^mailto:|^tel:/i.test(softContentLink.href)) {
          softContentLink.classList.add('is-busy');
          softContentLink.setAttribute('aria-busy', 'true');
        }
      }
    }

    // Long-form / richtext links (RTE, intros, hero, service, callout, FAQs) — speak before navigation.
    const rteLink =
      target instanceof Element
        ? target.closest(
            [
              '.rte a[href]',
              '.content-page__intro a[href]',
              '.contact-page__intro a[href]',
              '.hero__text a[href]',
              '.media-with-text__text a[href]',
              '.service-product__copy a[href]',
              '.product__custom-text a[href]',
              '.disclosure__content a[href]',
            ].join(', ')
          )
        : null;
    if (rteLink instanceof HTMLAnchorElement && rteLink.href) {
      // New-tab handling already speaks; same-page hashes get landing announce.
      if (rteLink.target !== '_blank') {
        let samePageHash = false;
        try {
          const url = new URL(rteLink.href, window.location.href);
          samePageHash =
            url.origin === window.location.origin &&
            url.pathname === window.location.pathname &&
            url.search === window.location.search &&
            Boolean(url.hash);
        } catch (error) {
          samePageHash = false;
        }
        if (!samePageHash) {
          if (
            rteLink.classList.contains('is-busy') ||
            rteLink.getAttribute('aria-busy') === 'true'
          ) {
            event.preventDefault();
            return;
          }
          const img = rteLink.querySelector('img[alt]');
          const label = (
            rteLink.getAttribute('aria-label') ||
            (img instanceof HTMLImageElement ? img.alt : '') ||
            rteLink.textContent ||
            ''
          )
            .replace(/\s+/g, ' ')
            .trim();
          if (label) announceStatus(label);
          if (!/^mailto:|^tel:/i.test(rteLink.href)) {
            rteLink.classList.add('is-busy');
            rteLink.setAttribute('aria-busy', 'true');
          }
        }
      }
    }

    document.querySelectorAll('.site-header__details[open]').forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.contains(target)) return;
      // Escape + facets/disclosure parity: don't leave focus inside a closed submenu.
      const hadFocus = details.contains(document.activeElement);
      details.open = false;
      if (!hadFocus) return;
      const summary = details.querySelector(':scope > summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
    });

    // Mobile nav uses plain <details> (not .site-header__details) — same outside-click restore.
    document.querySelectorAll('.site-header__mobile-menu details[open]').forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.contains(target)) return;
      const hadFocus = details.contains(document.activeElement);
      details.open = false;
      if (!hadFocus) return;
      const summary = details.querySelector(':scope > summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
    });

    document.querySelectorAll('[data-facets-drawer][open]').forEach((drawer) => {
      if (!(drawer instanceof HTMLDetailsElement)) return;
      if (drawer.contains(target)) return;
      const hadFocus = drawer.contains(document.activeElement);
      drawer.open = false;
      if (!hadFocus) return;
      const summary = drawer.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
    });

    document.querySelectorAll('details.disclosure[open]').forEach((disclosure) => {
      if (!(disclosure instanceof HTMLDetailsElement)) return;
      if (disclosure.contains(target)) return;
      const hadFocus = disclosure.contains(document.activeElement);
      disclosure.open = false;
      if (!hadFocus) return;
      const summary = disclosure.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-product-form]')) return;

    // Homepage / grid quick-add — async cart add with busy + success motion.
    if (form.matches('[data-card-product-form]')) {
      event.preventDefault();

      const submit =
        (event.submitter instanceof HTMLElement &&
          (event.submitter.matches('[data-card-atc]')
            ? event.submitter
            : event.submitter.closest('[data-card-atc]'))) ||
        form.querySelector('[data-card-atc]');
      const labelEl = form.querySelector('[data-card-atc-label]');
      const statusEl = form.querySelector('[data-card-status]');
      if (!(submit instanceof HTMLElement) || !(labelEl instanceof HTMLElement)) return;
      if (submit.classList.contains('is-busy') || submit.disabled) return;
      if (!window.fetch || !window.FormData) {
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      const labels = {
        add: submit.getAttribute('data-label-add') || 'Add to cart',
        adding: submit.getAttribute('data-label-adding') || 'Adding to cart',
        added: submit.getAttribute('data-label-added') || 'Added to cart',
        error: submit.getAttribute('data-label-error') || 'Could not add to cart. Try again.',
      };

      const ensureAtcCheck = (button) => {
        let icon = button.querySelector('[data-atc-check]');
        if (!(icon instanceof HTMLElement)) {
          icon = document.createElement('span');
          icon.setAttribute('data-atc-check', '');
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '✓';
          button.prepend(icon);
        }
        return icon;
      };

      const run = async () => {
        if (statusEl instanceof HTMLElement) {
          statusEl.hidden = true;
          statusEl.textContent = '';
          statusEl.classList.remove('is-error', 'is-success');
        }
        submit.disabled = true;
        submit.classList.add('is-busy');
        submit.setAttribute('aria-busy', 'true');
        labelEl.textContent = labels.adding;
        announceStatus(labels.adding);

        let failed = false;
        try {
          const response = await fetchWithTimeout(
            `${window.Shopify?.routes?.root || '/'}cart/add.js`,
            {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(form),
            },
            20000
          );
          let payload = null;
          try {
            payload = await withTimeout(response.json(), 20000);
          } catch (parseError) {
            payload = null;
          }
          if (!response.ok) {
            const apiMessage =
              (payload && typeof payload.description === 'string' && payload.description.trim()) ||
              (payload && typeof payload.message === 'string' && payload.message.trim()) ||
              '';
            throw new Error(apiMessage || labels.error);
          }

          const check = ensureAtcCheck(submit);
          check.hidden = false;
          labelEl.textContent = labels.added;
          submit.classList.add('is-success');
          if (statusEl instanceof HTMLElement) {
            statusEl.hidden = false;
            statusEl.textContent = labels.added;
            statusEl.classList.add('is-success');
          }
          document.dispatchEvent(new CustomEvent('theme:cart:update'));
          window.setTimeout(() => {
            check.hidden = true;
            labelEl.textContent = labels.add;
            submit.classList.remove('is-success');
            statusEl?.classList.remove('is-success');
          }, 1600);
        } catch (error) {
          failed = true;
          const message =
            error instanceof Error && error.message ? error.message : labels.error;
          if (statusEl instanceof HTMLElement) {
            statusEl.hidden = false;
            statusEl.textContent = message;
            statusEl.classList.add('is-error');
          }
          announceStatus(message);
          submit.classList.add('is-error');
          window.setTimeout(() => submit.classList.remove('is-error'), 1600);
        } finally {
          submit.disabled = false;
          submit.classList.remove('is-busy');
          submit.removeAttribute('aria-busy');
          if (failed) labelEl.textContent = labels.add;
        }
      };

      run();
      return;
    }

    if (event.defaultPrevented) return;

    // Re-entry gate — button disable alone doesn't block Enter-in-input key-repeat.
    if (form.classList.contains('is-busy') || form.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }
    form.classList.add('is-busy');
    form.setAttribute('aria-busy', 'true');

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;

    // Swap inverted From/To so Shopify gets a valid price range.
    if (form.matches('.facets__form')) {
      let priceRangeAdjusted = false;
      form.querySelectorAll('.facets__price').forEach((price) => {
        const inputs = Array.from(price.querySelectorAll('input[type="number"]'));
        if (inputs.length < 2) return;
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;
        if (minInput.value === '' || maxInput.value === '') return;
        const min = Number(minInput.value);
        const max = Number(maxInput.value);
        if (!Number.isFinite(min) || !Number.isFinite(max) || min <= max) return;
        minInput.value = String(max);
        maxInput.value = String(min);
        priceRangeAdjusted = true;
      });
      // Keep values submittable: readonly (not disabled) locks price fields mid-navigation.
      form.querySelectorAll('.facets__price input[type="number"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.readOnly = true;
        input.setAttribute('aria-busy', 'true');
      });
      const drawer = form.closest('[data-facets-drawer]');
      const summary = drawer?.querySelector(':scope > summary');
      if (
        summary instanceof HTMLElement &&
        document.activeElement instanceof HTMLElement &&
        (form.contains(document.activeElement) || document.activeElement === submitter)
      ) {
        summary.focus({ preventScroll: true });
      }
      // Don't disable checkboxes — disabled filter values would be omitted from the GET
      // (price uses readOnly; form.is-busy already pointer-events-locks the UI).
      // Apply / Enter submits (checkbox auto-apply announces on change instead).
      // Enter from a price field can omit event.submitter — still announce loading.
      const loading =
        document.body.getAttribute('data-filters-loading') || 'Updating filters';
      if (priceRangeAdjusted) {
        const adjusted =
          document.body.getAttribute('data-price-range-adjusted') || 'Price range adjusted';
        announceStatus(`${adjusted}. ${loading}`);
      } else {
        announceStatus(loading);
      }
    } else if (
      submitter instanceof HTMLElement &&
      document.activeElement === submitter
    ) {
      // Contact/comment/password/cart checkout: leave the submit control before it disables.
      const fallback =
        form.querySelector('.status-banner, [role="alert"]') ||
        form.querySelector('h1, h2, h3, legend') ||
        form.querySelector(
          'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
        ) ||
        form;
      if (fallback instanceof HTMLElement) {
        const needsTabIndex =
          !(fallback instanceof HTMLInputElement) &&
          !(fallback instanceof HTMLTextAreaElement) &&
          !(fallback instanceof HTMLSelectElement) &&
          !(fallback instanceof HTMLButtonElement) &&
          !fallback.hasAttribute('tabindex');
        if (needsTabIndex) fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
      }
    }

    // Cart page / drawer Checkout — speak before navigation while the button goes busy.
    if (
      (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) &&
      submitter.name === 'checkout'
    ) {
      // Disabling the submitter below drops name=checkout from the entry list (HTML
      // skips disabled controls). Without it, Shopify POSTs /cart as an update and
      // never redirects to checkout. Mirror the submitter with a hidden field first.
      let checkoutField = form.querySelector(
        'input[type="hidden"][name="checkout"][data-theme-checkout-sentinel]'
      );
      if (!(checkoutField instanceof HTMLInputElement)) {
        checkoutField = document.createElement('input');
        checkoutField.type = 'hidden';
        checkoutField.name = 'checkout';
        checkoutField.value = '';
        checkoutField.setAttribute('data-theme-checkout-sentinel', '');
        form.appendChild(checkoutField);
      }
      announceStatus(
        document.body.getAttribute('data-checkout-loading') || 'Proceeding to checkout'
      );
    } else if (form.matches('.site-footer__localization')) {
      // Manual Update (select change already announces on auto-submit).
      announceStatus(
        document.body.getAttribute('data-localization-loading') ||
          'Updating country and language'
      );
    } else if (
      form.matches(
        '.contact-page__form, .password-page__form, .site-footer__form, .service-product__contact, .article-page__comment-form, #ContactForm'
      )
    ) {
      // Full-page posts: speak while the submit control is busy/disabled.
      announceStatus(document.body.getAttribute('data-form-sending') || 'Sending');
    } else if (
      form.matches(
        '.search-page__form, .site-header__search-form, .utility-page__search'
      ) ||
      form.getAttribute('role') === 'search'
    ) {
      // Full search navigations (header, search page, 404) — predictive loading announces separately.
      announceStatus(document.body.getAttribute('data-search-loading') || 'Searching');
    }

    const buttons = Array.from(
      form.querySelectorAll('button[type="submit"], button:not([type]), input[type="submit"]')
    );

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if ('disabled' in button) button.disabled = true;
      if (!submitter || button === submitter) {
        button.classList.add('is-busy');
        button.setAttribute('aria-busy', 'true');
      }
    });
  });

  const clearTransientBusyState = () => {
    document.querySelectorAll('.is-busy').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      // Keep product/cart in-flight UIs that are still mid-request on a live page;
      // pageshow recovery is for restored documents / back-forward cache.
      el.classList.remove('is-busy');
      el.removeAttribute('aria-busy');
      if (
        (el instanceof HTMLButtonElement ||
          el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement) &&
        el.disabled &&
        (el.matches('button[type="submit"], button:not([type]), input[type="submit"]') ||
          el.matches('[data-localization-select], [data-facets-sort]'))
      ) {
        el.disabled = false;
      }
      if (el.matches('[data-localization-select], [data-facets-sort]')) {
        el.removeAttribute('aria-disabled');
      }
      if (el.matches('form.facets__form')) {
        el.querySelectorAll('.facets__price input[type="number"]').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.readOnly = false;
          input.removeAttribute('aria-busy');
        });
      }
    });
    // Related products soft-abort leaves section aria-busy without .is-busy.
    document
      .querySelectorAll('[data-component="related-products"][aria-busy="true"]')
      .forEach((el) => {
        if (el instanceof HTMLElement) el.removeAttribute('aria-busy');
      });
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted || performance.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward') {
      clearTransientBusyState();
    }
  });

  const shareResetTimers = new WeakMap();

  const setProductShareExpanded = (root, expanded, { announce = true } = {}) => {
    if (!(root instanceof HTMLElement)) return;
    const toggle = root.querySelector('[data-product-share-toggle]');
    const options = root.querySelector('[data-product-share-options]');
    if (!(toggle instanceof HTMLButtonElement) || !(options instanceof HTMLElement)) return;

    root.classList.toggle('is-expanded', expanded);
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    options.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    options.toggleAttribute('inert', !expanded);

    if (announce) {
      announceStatus(
        root.getAttribute(expanded ? 'data-label-opened' : 'data-label-closed') ||
          (expanded ? 'Sharing options opened' : 'Sharing options closed')
      );
    }
  };

  const copyTextValue = async (value) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error('Copy timed out')), 8000);
        navigator.clipboard.writeText(value).then(
          () => {
            window.clearTimeout(timer);
            resolve();
          },
          (error) => {
            window.clearTimeout(timer);
            reject(error);
          }
        );
      });
      return;
    }

    const area = document.createElement('textarea');
    area.value = value;
    area.setAttribute('readonly', '');
    area.style.position = 'absolute';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    if (!ok) throw new Error('Copy failed');
  };

  const enhancePromoModal = (element) => {
    const dialog = element.querySelector('[data-promo-modal]');
    if (!(dialog instanceof HTMLDialogElement)) return () => {};

    const panel = dialog.querySelector('.promo-modal__panel');
    const mode = element.getAttribute('data-promo-show-mode') || 'once';
    const storageKey = element.getAttribute('data-promo-storage-key') || 'bls-promo';
    const designMode = element.hasAttribute('data-promo-design-mode');
    const media = dialog.querySelector('[data-promo-modal-media]');
    const image =
      dialog.querySelector('[data-promo-modal-image]') ||
      dialog.querySelector('.promo-modal__image');
    let openTimer = 0;
    let closeTimer = 0;
    let closeTransitionHandler = null;

    const hasSeen = () => {
      if (mode !== 'once') return false;
      try {
        return window.localStorage.getItem(storageKey) === '1';
      } catch (error) {
        return false;
      }
    };

    const markSeen = () => {
      if (mode !== 'once') return;
      try {
        window.localStorage.setItem(storageKey, '1');
      } catch (error) {
        // Ignore storage access failures.
      }
    };

    const clearCloseWait = () => {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
      if (closeTransitionHandler && panel instanceof HTMLElement) {
        panel.removeEventListener('transitionend', closeTransitionHandler);
      }
      closeTransitionHandler = null;
    };

    const applyAccentFromImage = () => {
      if (!(image instanceof HTMLImageElement) || !(dialog instanceof HTMLElement)) return;
      if (!image.complete || image.naturalWidth < 2) return;

      const size = 24;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;

      try {
        context.drawImage(image, 0, 0, size, size);
      } catch (error) {
        return;
      }

      let data;
      try {
        data = context.getImageData(0, 0, size, size).data;
      } catch (error) {
        return;
      }

      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let weight = 0;
      let deepR = 0;
      let deepG = 0;
      let deepB = 0;
      let deepWeight = 0;

      for (let index = 0; index < data.length; index += 4) {
        const alpha = data[index + 3];
        if (alpha < 140) continue;

        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const max = Math.max(red, green, blue);
        const min = Math.min(red, green, blue);
        const saturation = max === 0 ? 0 : (max - min) / max;
        const luminance = (red + green + blue) / 3;

        // Prefer mid/high-sat hues; skip near-white cream and deep blacks.
        if (luminance > 242 || luminance < 18) continue;

        const sampleWeight = 0.55 + saturation * 2.2;
        sumR += red * sampleWeight;
        sumG += green * sampleWeight;
        sumB += blue * sampleWeight;
        weight += sampleWeight;

        if (saturation > 0.28 && luminance > 40 && luminance < 210) {
          const vividWeight = saturation * 3;
          deepR += red * vividWeight;
          deepG += green * vividWeight;
          deepB += blue * vividWeight;
          deepWeight += vividWeight;
        }
      }

      if (weight < 1) return;

      const accent = [
        Math.round(sumR / weight),
        Math.round(sumG / weight),
        Math.round(sumB / weight),
      ];
      const deep =
        deepWeight > 1
          ? [
              Math.round(deepR / deepWeight),
              Math.round(deepG / deepWeight),
              Math.round(deepB / deepWeight),
            ]
          : accent;

      dialog.style.setProperty('--promo-modal-accent', `rgb(${accent[0]} ${accent[1]} ${accent[2]})`);
      dialog.style.setProperty(
        '--promo-modal-accent-deep',
        `rgb(${deep[0]} ${deep[1]} ${deep[2]})`
      );
      if (media instanceof HTMLElement) {
        media.style.setProperty('--promo-modal-accent', `rgb(${accent[0]} ${accent[1]} ${accent[2]})`);
      }
    };

    const open = () => {
      if (dialog.open) return;
      if (!designMode && hasSeen()) return;
      clearCloseWait();
      dialog.classList.remove('is-closing');
      if (!dialog.open) dialog.showModal();
      applyAccentFromImage();
      const closeButton = dialog.querySelector('[data-promo-modal-close]');
      const focusables = getFocusable(dialog);
      (focusables[0] || closeButton || dialog).focus({ preventScroll: true });
    };

    const finishClose = () => {
      clearCloseWait();
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
      document.dispatchEvent(new CustomEvent('theme:announcement:replay'));
    };

    const close = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      markSeen();

      if (prefersReducedMotion() || !(panel instanceof HTMLElement)) {
        finishClose();
        return;
      }

      clearCloseWait();
      dialog.classList.add('is-closing');

      closeTransitionHandler = (event) => {
        if (!(event instanceof TransitionEvent)) return;
        if (event.target !== panel || event.propertyName !== 'opacity') return;
        finishClose();
      };
      panel.addEventListener('transitionend', closeTransitionHandler);
      closeTimer = window.setTimeout(finishClose, 420);
    };

    const onCloseClick = (event) => {
      const closeControl = event.target.closest('[data-promo-modal-close]');
      if (!(closeControl instanceof HTMLElement) || !dialog.contains(closeControl)) return;
      event.preventDefault();
      close();
    };

    const onCtaClick = () => {
      markSeen();
    };

    const onDialogClick = (event) => {
      if (event.target === dialog) close();
    };

    const onCancel = (event) => {
      event.preventDefault();
      close();
    };

    const onImageLoad = () => {
      applyAccentFromImage();
    };

    dialog.addEventListener('click', onDialogClick);
    dialog.addEventListener('cancel', onCancel);
    element.addEventListener('click', onCloseClick);
    element.querySelectorAll('[data-promo-modal-cta]').forEach((cta) => {
      cta.addEventListener('click', onCtaClick);
    });
    if (image instanceof HTMLImageElement) {
      image.addEventListener('load', onImageLoad);
      if (image.complete) applyAccentFromImage();
    }

    // Slight delay so first paint / hero can settle; skip travel wait when reduced motion.
    openTimer = window.setTimeout(open, prefersReducedMotion() ? 0 : 420);

    return () => {
      window.clearTimeout(openTimer);
      clearCloseWait();
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('cancel', onCancel);
      element.removeEventListener('click', onCloseClick);
      element.querySelectorAll('[data-promo-modal-cta]').forEach((cta) => {
        cta.removeEventListener('click', onCtaClick);
      });
      if (image instanceof HTMLImageElement) {
        image.removeEventListener('load', onImageLoad);
      }
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
    };
  };

  const enhancePromoGallery = (element) => {
    const slides = Array.from(element.querySelectorAll('[data-promo-slide]'));
    if (slides.length === 0) return () => {};

    const dots = Array.from(element.querySelectorAll('[data-promo-dot]'));
    const live = element.querySelector('[data-promo-live]');
    const prev = element.querySelector('[data-promo-prev]');
    const next = element.querySelector('[data-promo-next]');
    const viewport = element.querySelector('[data-promo-viewport]');
    const progress = element.querySelector('[data-promo-progress]');
    const intervalMs = Math.max(4000, Number(element.getAttribute('data-promo-interval') || 6000));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    if (index < 0) index = 0;
    let timer = 0;
    let pointerId = null;
    let pointerStartX = null;
    let pointerStartY = null;
    let swiping = false;
    let suppressClick = false;
    let leaveTimer = 0;
    let inView = true;
    let paused = false;

    const announce = (slide, current) => {
      if (!(live instanceof HTMLElement)) return;
      const title =
        slide.querySelector('.promo-gallery__slide-title')?.textContent?.trim() ||
        `Promotion ${current + 1}`;
      const template =
        document.body.getAttribute('data-promo-slide-template') ||
        'Promotion [current] of [total]: [title]';
      live.textContent = template
        .replaceAll('[current]', String(current + 1))
        .replaceAll('[total]', String(slides.length))
        .replaceAll('[title]', title);
    };

    const resetProgress = () => {
      if (!(progress instanceof HTMLElement)) return;
      progress.classList.remove('is-running');
      progress.style.animationDuration = '';
      void progress.offsetWidth;
    };

    const runProgress = () => {
      if (!(progress instanceof HTMLElement) || reduceMotion || paused || !inView) return;
      resetProgress();
      progress.style.animationDuration = `${intervalMs}ms`;
      progress.classList.add('is-running');
    };

    const setActive = (nextIndex, { announceChange = true, restartTimer = true } = {}) => {
      if (slides.length === 0) return;
      const total = slides.length;
      const target = ((nextIndex % total) + total) % total;
      if (target === index && slides[target].classList.contains('is-active')) {
        if (restartTimer) {
          stop();
          start();
        }
        return;
      }

      const previous = slides[index];
      const nextSlide = slides[target];

      if (previous instanceof HTMLElement && previous !== nextSlide) {
        previous.classList.add('is-leaving');
        previous.classList.remove('is-active');
        previous.setAttribute('aria-hidden', 'true');
        previous.setAttribute('inert', '');
        window.clearTimeout(leaveTimer);
        leaveTimer = window.setTimeout(
          () => {
            previous.classList.remove('is-leaving');
          },
          reduceMotion ? 0 : 520
        );
      }

      nextSlide.classList.add('is-entering');
      nextSlide.classList.remove('is-leaving');
      // Force enter start position before activating.
      void nextSlide.offsetWidth;
      nextSlide.classList.add('is-active');
      nextSlide.classList.remove('is-entering');
      nextSlide.removeAttribute('aria-hidden');
      nextSlide.removeAttribute('inert');

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === target;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      index = target;
      if (announceChange) announce(nextSlide, target);
      if (restartTimer) {
        stop();
        start();
      } else {
        runProgress();
      }
    };

    const stop = () => {
      window.clearTimeout(timer);
      timer = 0;
      resetProgress();
    };

    const start = () => {
      stop();
      if (reduceMotion || slides.length < 2 || paused || !inView) return;
      if (element.matches(':hover') || element.contains(document.activeElement)) return;
      runProgress();
      timer = window.setTimeout(() => {
        setActive(index + 1, { restartTimer: true });
      }, intervalMs);
    };

    const pause = () => {
      paused = true;
      stop();
    };

    const resume = () => {
      paused = false;
      start();
    };

    const go = (delta) => {
      setActive(index + delta, { restartTimer: true });
    };

    const onPrev = () => go(-1);
    const onNext = () => go(1);
    const onDotClick = (event) => {
      const dot = event.target.closest('[data-promo-dot]');
      if (!(dot instanceof HTMLElement)) return;
      const nextIndex = Number(dot.getAttribute('data-promo-index'));
      if (!Number.isFinite(nextIndex)) return;
      setActive(nextIndex, { restartTimer: true });
    };

    const onFocusIn = () => pause();
    const onFocusOut = (event) => {
      if (element.contains(event.relatedTarget)) return;
      resume();
    };
    const onPointerEnter = () => pause();
    const onPointerLeave = () => resume();

    const clearSwipe = () => {
      pointerId = null;
      pointerStartX = null;
      pointerStartY = null;
      swiping = false;
    };

    const onPointerDown = (event) => {
      if (!(event instanceof PointerEvent) || event.pointerType === 'mouse') return;
      if (slides.length < 2 || !(viewport instanceof HTMLElement)) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      swiping = false;
      try {
        viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        // Capture is best-effort; swipe still works via bubble events.
      }
      pause();
    };

    const onPointerMove = (event) => {
      if (
        !(event instanceof PointerEvent) ||
        pointerId !== event.pointerId ||
        pointerStartX == null ||
        pointerStartY == null
      ) {
        return;
      }

      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;

      if (!swiping) {
        // Let the page scroll when the gesture is clearly vertical.
        if (Math.abs(deltaY) > 12 && Math.abs(deltaY) > Math.abs(deltaX)) {
          clearSwipe();
          resume();
          return;
        }
        if (Math.abs(deltaX) > 12) {
          swiping = true;
        }
      }

      if (swiping) {
        event.preventDefault();
      }
    };

    const onPointerUp = (event) => {
      if (!(event instanceof PointerEvent) || pointerId !== event.pointerId) return;
      const deltaX = pointerStartX == null ? 0 : event.clientX - pointerStartX;
      const didSwipe = swiping && Math.abs(deltaX) >= 40;
      clearSwipe();
      resume();
      if (!didSwipe) return;
      suppressClick = true;
      go(deltaX < 0 ? 1 : -1);
    };

    const onPointerCancel = (event) => {
      if (!(event instanceof PointerEvent) || pointerId !== event.pointerId) return;
      clearSwipe();
      resume();
    };

    const onSwipeClickCapture = (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    };

    const onVisibility = () => {
      if (document.hidden) {
        pause();
      } else {
        resume();
      }
    };

    const onReplay = () => {
      // After promo modal closes, kick the rotator so the first real ad dwell is obvious.
      resume();
    };

    let observer = null;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          inView = Boolean(entry?.isIntersecting);
          if (inView) resume();
          else pause();
        },
        { threshold: 0.35 }
      );
      observer.observe(element);
    }

    prev?.addEventListener('click', onPrev);
    next?.addEventListener('click', onNext);
    element.addEventListener('click', onDotClick);
    element.addEventListener('focusin', onFocusIn);
    element.addEventListener('focusout', onFocusOut);
    element.addEventListener('pointerenter', onPointerEnter);
    element.addEventListener('pointerleave', onPointerLeave);
    viewport?.addEventListener('pointerdown', onPointerDown);
    viewport?.addEventListener('pointermove', onPointerMove, { passive: false });
    viewport?.addEventListener('pointerup', onPointerUp);
    viewport?.addEventListener('pointercancel', onPointerCancel);
    viewport?.addEventListener('click', onSwipeClickCapture, true);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('theme:announcement:replay', onReplay);

    setActive(index, { announceChange: false, restartTimer: true });

    return () => {
      stop();
      window.clearTimeout(leaveTimer);
      observer?.disconnect();
      prev?.removeEventListener('click', onPrev);
      next?.removeEventListener('click', onNext);
      element.removeEventListener('click', onDotClick);
      element.removeEventListener('focusin', onFocusIn);
      element.removeEventListener('focusout', onFocusOut);
      element.removeEventListener('pointerenter', onPointerEnter);
      element.removeEventListener('pointerleave', onPointerLeave);
      viewport?.removeEventListener('pointerdown', onPointerDown);
      viewport?.removeEventListener('pointermove', onPointerMove);
      viewport?.removeEventListener('pointerup', onPointerUp);
      viewport?.removeEventListener('pointercancel', onPointerCancel);
      viewport?.removeEventListener('click', onSwipeClickCapture, true);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('theme:announcement:replay', onReplay);
    };
  };

  const replayAnnouncementBars = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.announcement-bar--promo').forEach((bar) => {
        if (!(bar instanceof HTMLElement) || bar.hidden) return;
        bar.classList.add('is-live');
      });
      return;
    }

    document.querySelectorAll('.announcement-bar--promo').forEach((bar) => {
      if (!(bar instanceof HTMLElement) || bar.hidden || bar.classList.contains('is-dismissed')) {
        return;
      }
      bar.classList.remove('is-live', 'is-replay');
      // Force style recalc so CSS animations restart.
      void bar.offsetWidth;
      bar.classList.add('is-replay');
      window.setTimeout(() => {
        bar.classList.remove('is-replay');
        bar.classList.add('is-live');
      }, 1200);
    });
  };

  const enhanceAnnouncementBars = (root = document) => {
    const enhanceRotator = (bar) => {
      if (!(bar instanceof HTMLElement)) return;
      if (!bar.hasAttribute('data-announcement-rotator') || bar.dataset.announcementRotatorEnhanced === 'true') {
        return;
      }

      const slides = Array.from(bar.querySelectorAll('[data-announcement-slide]')).filter(
        (node) => node instanceof HTMLElement
      );
      if (slides.length < 2) return;

      bar.dataset.announcementRotatorEnhanced = 'true';
      let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (index < 0) index = 0;
      let timer = 0;
      const intervalMs = Math.max(4000, Number(bar.getAttribute('data-announcement-interval') || 5500));

      const show = (nextIndex) => {
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          if (active) {
            slide.removeAttribute('inert');
            slide.setAttribute('aria-hidden', 'false');
          } else {
            slide.setAttribute('inert', '');
            slide.setAttribute('aria-hidden', 'true');
          }
        });
      };

      const stop = () => {
        window.clearInterval(timer);
        timer = 0;
      };

      const start = () => {
        if (prefersReducedMotion()) {
          stop();
          show(0);
          return;
        }
        stop();
        timer = window.setInterval(() => show(index + 1), intervalMs);
      };

      const onEnter = () => stop();
      const onLeave = () => start();
      const onFocusIn = () => stop();
      const onFocusOut = (event) => {
        if (event instanceof FocusEvent && event.relatedTarget instanceof Node && bar.contains(event.relatedTarget)) {
          return;
        }
        start();
      };

      bar.addEventListener('pointerenter', onEnter);
      bar.addEventListener('pointerleave', onLeave);
      bar.addEventListener('focusin', onFocusIn);
      bar.addEventListener('focusout', onFocusOut);
      show(index);
      start();
    };

    root.querySelectorAll('[data-announcement-bar]').forEach((bar) => {
      if (!(bar instanceof HTMLElement)) return;
      if (bar.hasAttribute('data-announcement-design-mode')) {
        if (bar.classList.contains('announcement-bar--promo')) {
          bar.classList.add('is-live');
        }
        enhanceRotator(bar);
        return;
      }

      const key = bar.getAttribute('data-announcement-key');
      if (!key) return;

      try {
        if (window.sessionStorage.getItem(key) === '1') {
          bar.hidden = true;
          bar.classList.add('is-dismissed');
          return;
        }
      } catch (error) {
        // Ignore storage access failures.
      }

      if (bar.classList.contains('announcement-bar--promo')) {
        const modalOpen = Boolean(document.querySelector('[data-promo-modal][open]'));
        if (!modalOpen) {
          bar.classList.add('is-live');
        }
      }

      enhanceRotator(bar);
    });
  };

  document.addEventListener('theme:announcement:replay', replayAnnouncementBars);

  document.addEventListener('click', (event) => {
    const dismiss = event.target.closest('[data-announcement-dismiss]');
    if (dismiss instanceof HTMLElement) {
      const bar = dismiss.closest('[data-announcement-bar]');
      if (!(bar instanceof HTMLElement) || bar.hasAttribute('data-announcement-design-mode')) return;
      // Re-entry gate — Escape synthesizes click; double-fire spam announce/focus restore.
      if (bar.hidden || bar.classList.contains('is-dismissed')) return;

      const key = bar.getAttribute('data-announcement-key');
      bar.classList.add('is-dismissed');
      announceStatus(
        document.body.getAttribute('data-announcement-dismissed') || 'Announcement dismissed'
      );
      window.setTimeout(() => {
        const active = document.activeElement;
        const focusStillInBar = active instanceof HTMLElement && bar.contains(active);
        bar.hidden = true;
        // After hide, focus often collapses to <body> if it was still inside the bar.
        const focusCollapsed =
          !(document.activeElement instanceof HTMLElement) ||
          document.activeElement === document.body;
        // Don't yank keyboard users who already moved into header/search/cart during exit.
        if (!focusStillInBar && !focusCollapsed) return;
        const focusTarget =
          document.querySelector(
            '.site-header [data-menu-open], .site-header [data-search-open], .site-header a, .site-header button'
          ) || document.getElementById('MainContent');
        if (focusTarget instanceof HTMLElement && typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }
      }, 280);

      if (key) {
        try {
          window.sessionStorage.setItem(key, '1');
        } catch (error) {
          // Ignore storage access failures.
        }
      }
      return;
    }

    const toggle = event.target.closest('[data-password-toggle]');
    if (toggle instanceof HTMLElement) {
      const wrap = toggle.closest('.password-page__input-wrap, [data-password-field]');
      const input =
        (wrap && wrap.querySelector('[data-password-input], input[type="password"], input[type="text"]')) ||
        document.getElementById(toggle.getAttribute('aria-controls') || '');
      if (!(input instanceof HTMLInputElement)) return;

      const showLabel = toggle.getAttribute('data-label-show') || 'Show';
      const hideLabel = toggle.getAttribute('data-label-hide') || 'Hide';
      const shownLabel = toggle.getAttribute('data-label-shown') || 'Password visible';
      const hiddenLabel = toggle.getAttribute('data-label-hidden') || 'Password hidden';
      const revealing = input.type === 'password';
      input.type = revealing ? 'text' : 'password';
      toggle.textContent = revealing ? hideLabel : showLabel;
      toggle.setAttribute('aria-pressed', revealing ? 'true' : 'false');
      announceStatus(revealing ? shownLabel : hiddenLabel);
      // Keep caret usable after the control flips between password/text.
      input.focus({ preventScroll: true });
      if (revealing && typeof input.setSelectionRange === 'function') {
        const end = input.value.length;
        try {
          input.setSelectionRange(end, end);
        } catch (error) {
          // Some input types reject selection ranges.
        }
      }
      // Click-to-toggle can blur with a null relatedTarget and clear Caps Lock — restore quietly.
      const capsHint = wrap?.querySelector('[data-password-caps]');
      if (
        capsHint instanceof HTMLElement &&
        typeof event.getModifierState === 'function' &&
        event.getModifierState('CapsLock')
      ) {
        capsHint.hidden = false;
      }
      return;
    }

    const chipCopy = event.target.closest('[data-product-sku-copy], [data-product-barcode-copy]');
    if (chipCopy instanceof HTMLElement) {
      if (chipCopy.classList.contains('is-busy')) return;
      const isBarcode = chipCopy.hasAttribute('data-product-barcode-copy');
      const valueEl = chipCopy.querySelector(
        isBarcode ? '[data-product-barcode-value]' : '[data-product-sku-value]'
      );
      const statusEl = chipCopy.parentElement?.querySelector(
        isBarcode ? '[data-product-barcode-status]' : '[data-product-sku-status]'
      );
      const value = (valueEl?.textContent || '').trim();
      if (!value) return;

      const labelCopied =
        chipCopy.getAttribute('data-label-copied') || (isBarcode ? 'Barcode copied' : 'SKU copied');

      const clearBusy = () => {
        chipCopy.classList.remove('is-busy');
        chipCopy.removeAttribute('aria-busy');
      };

      const markCopied = () => {
        let iconEl = chipCopy.querySelector('[data-share-icon]');
        if (!(iconEl instanceof HTMLElement)) {
          iconEl = document.createElement('span');
          iconEl.setAttribute('data-share-icon', '');
          iconEl.setAttribute('aria-hidden', 'true');
          iconEl.textContent = '✓';
          chipCopy.prepend(iconEl);
        }
        iconEl.hidden = false;
        chipCopy.classList.add('is-success');
        if (statusEl) {
          statusEl.classList.remove('is-info', 'is-error');
          statusEl.hidden = false;
          statusEl.textContent = labelCopied;
        }
        // Global live region — chip status can miss when focus returns to the control.
        announceStatus(labelCopied);
        window.clearTimeout(shareResetTimers.get(chipCopy));
        shareResetTimers.set(
          chipCopy,
          window.setTimeout(() => {
            iconEl.hidden = true;
            chipCopy.classList.remove('is-success');
            if (statusEl) {
              statusEl.classList.remove('is-info', 'is-error');
              statusEl.hidden = true;
              statusEl.textContent = '';
            }
          }, 1800)
        );
      };

      chipCopy.classList.add('is-busy');
      chipCopy.setAttribute('aria-busy', 'true');
      announceStatus(
        chipCopy.getAttribute('data-label-copying') ||
          document.body.getAttribute('data-copy-loading') ||
          'Copying'
      );

      (async () => {
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await new Promise((resolve, reject) => {
              const timer = window.setTimeout(() => reject(new Error('Copy timed out')), 8000);
              navigator.clipboard.writeText(value).then(
                () => {
                  window.clearTimeout(timer);
                  resolve();
                },
                (error) => {
                  window.clearTimeout(timer);
                  reject(error);
                }
              );
            });
          } else {
            const area = document.createElement('textarea');
            area.value = value;
            area.setAttribute('readonly', '');
            area.style.position = 'absolute';
            area.style.left = '-9999px';
            document.body.appendChild(area);
            area.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(area);
            if (!ok) throw new Error('Copy failed');
          }
          clearBusy();
          markCopied();
          if (document.body.contains(chipCopy)) {
            chipCopy.focus({ preventScroll: true });
          }
        } catch (error) {
          clearBusy();
          chipCopy.classList.add('is-error');
          window.setTimeout(() => chipCopy.classList.remove('is-error'), 1600);
          const labelSelect =
            chipCopy.getAttribute('data-label-select') || 'Selected — use your keyboard to copy';
          // Focus the button (span is not tabbable) — gift-card copy parity.
          if (document.body.contains(chipCopy)) {
            chipCopy.focus({ preventScroll: true });
          }
          try {
            if (valueEl instanceof HTMLElement) {
              const range = document.createRange();
              range.selectNodeContents(valueEl);
              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            }
          } catch (selectError) {
            // Selection may fail in some hosts; button focus + status still help.
          }
          if (statusEl) {
            statusEl.classList.remove('is-error');
            statusEl.classList.add('is-info');
            statusEl.hidden = false;
            statusEl.textContent = labelSelect;
            window.clearTimeout(shareResetTimers.get(chipCopy));
            shareResetTimers.set(
              chipCopy,
              window.setTimeout(() => {
                statusEl.classList.remove('is-info');
                statusEl.hidden = true;
                statusEl.textContent = '';
              }, 2800)
            );
          }
          // Global live region — chip status can miss when focus returns to the control.
          announceStatus(labelSelect);
        }
      })();
      return;
    }

    const shareToggle = event.target.closest('[data-product-share-toggle]');
    if (shareToggle instanceof HTMLButtonElement) {
      const root = shareToggle.closest('[data-product-share-panel]');
      if (!(root instanceof HTMLElement)) return;
      setProductShareExpanded(root, shareToggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const copyLinkButton = event.target.closest('[data-product-copy-link]');
    if (copyLinkButton instanceof HTMLButtonElement) {
      if (copyLinkButton.classList.contains('is-busy')) return;
      const root = copyLinkButton.closest('[data-product-share-panel]');
      const status = root?.querySelector('[data-product-share-status]');
      const label = copyLinkButton.querySelector('[data-share-copy-label]');
      const value = copyLinkButton.getAttribute('data-share-url') || window.location.href;
      const copyLabel = copyLinkButton.getAttribute('data-label-copy') || 'Copy link';
      const copiedLabel = copyLinkButton.getAttribute('data-label-copied') || 'Link copied';
      const failedLabel =
        copyLinkButton.getAttribute('data-label-copy-failed') || 'Could not copy the link';

      copyLinkButton.classList.add('is-busy');
      copyLinkButton.setAttribute('aria-busy', 'true');
      if (label instanceof HTMLElement) {
        label.textContent = document.body.getAttribute('data-copy-loading') || 'Copying';
      }

      (async () => {
        try {
          await copyTextValue(value);
          copyLinkButton.classList.remove('is-busy');
          copyLinkButton.removeAttribute('aria-busy');
          copyLinkButton.classList.add('is-success');
          if (label instanceof HTMLElement) label.textContent = copiedLabel;
          if (status instanceof HTMLElement) {
            status.classList.remove('is-error');
            status.hidden = false;
            status.textContent = copiedLabel;
          }
          announceStatus(copiedLabel);
          window.clearTimeout(shareResetTimers.get(copyLinkButton));
          shareResetTimers.set(
            copyLinkButton,
            window.setTimeout(() => {
              copyLinkButton.classList.remove('is-success');
              if (label instanceof HTMLElement) label.textContent = copyLabel;
              if (status instanceof HTMLElement) {
                status.hidden = true;
                status.textContent = '';
              }
            }, 1800)
          );
        } catch (error) {
          copyLinkButton.classList.remove('is-busy');
          copyLinkButton.removeAttribute('aria-busy');
          copyLinkButton.classList.add('is-error');
          if (label instanceof HTMLElement) label.textContent = copyLabel;
          if (status instanceof HTMLElement) {
            status.classList.add('is-error');
            status.hidden = false;
            status.textContent = failedLabel;
          }
          announceStatus(failedLabel);
          window.clearTimeout(shareResetTimers.get(copyLinkButton));
          shareResetTimers.set(
            copyLinkButton,
            window.setTimeout(() => {
              copyLinkButton.classList.remove('is-error');
              if (status instanceof HTMLElement) {
                status.classList.remove('is-error');
                status.hidden = true;
                status.textContent = '';
              }
            }, 2800)
          );
        }
      })();
      return;
    }

    const shareButton = event.target.closest('[data-product-share]');
    if (!(shareButton instanceof HTMLElement)) return;
    if (shareButton.classList.contains('is-busy')) return;

    const root = shareButton.closest('.product__share, .service-product__share, .article-page__share') || shareButton.parentElement;
    const shareStatus = root ? root.querySelector('[data-product-share-status]') : null;
    const labelShare = shareButton.getAttribute('data-label-share') || 'Share';
    const labelCopied = shareButton.getAttribute('data-label-copied') || 'Link copied';
    const labelShared = shareButton.getAttribute('data-label-shared') || 'Shared';
    const url = window.location.href;
    const title = document.title;

    const markSuccess = (message) => {
      let labelEl = shareButton.querySelector('[data-share-label]');
      let iconEl = shareButton.querySelector('[data-share-icon]');
      if (!(labelEl instanceof HTMLElement)) {
        const initial = shareButton.textContent.trim() || labelShare;
        shareButton.replaceChildren();
        iconEl = document.createElement('span');
        iconEl.setAttribute('data-share-icon', '');
        iconEl.setAttribute('aria-hidden', 'true');
        iconEl.hidden = true;
        iconEl.textContent = '✓';
        labelEl = document.createElement('span');
        labelEl.setAttribute('data-share-label', '');
        labelEl.textContent = initial;
        shareButton.append(iconEl, labelEl);
      }

      labelEl.textContent = message;
      if (iconEl instanceof HTMLElement) iconEl.hidden = false;
      shareButton.classList.add('is-success');
      if (shareStatus) {
        shareStatus.classList.remove('is-info', 'is-error');
        shareStatus.hidden = false;
        shareStatus.textContent = message;
      }
      // Global live region — status chip can miss after share-sheet focus restore.
      announceStatus(message);
      window.clearTimeout(shareResetTimers.get(shareButton));
      shareResetTimers.set(
        shareButton,
        window.setTimeout(() => {
          labelEl.textContent = labelShare;
          if (iconEl instanceof HTMLElement) iconEl.hidden = true;
          shareButton.classList.remove('is-success');
          if (shareStatus) {
            shareStatus.classList.remove('is-info', 'is-error');
            shareStatus.hidden = true;
            shareStatus.textContent = '';
          }
        }, 1800)
      );
    };

    const markCopyFailed = () => {
      const labelFailed =
        shareButton.getAttribute('data-label-copy-failed') || 'Could not copy the link';
      shareButton.classList.add('is-error');
      window.setTimeout(() => shareButton.classList.remove('is-error'), 1600);
      if (shareStatus) {
        shareStatus.classList.remove('is-info');
        shareStatus.classList.add('is-error');
        shareStatus.hidden = false;
        shareStatus.textContent = labelFailed;
        window.clearTimeout(shareResetTimers.get(shareButton));
        shareResetTimers.set(
          shareButton,
          window.setTimeout(() => {
            shareStatus.classList.remove('is-error');
            shareStatus.hidden = true;
            shareStatus.textContent = '';
          }, 2800)
        );
      }
      // Global live region — status chip can miss after share-sheet focus restore.
      announceStatus(labelFailed);
    };

    const copy = async () => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        // SKU/gift-card 8s parity — don't wait 30s hang timer to say "Share canceled".
        await new Promise((resolve, reject) => {
          const timer = window.setTimeout(() => reject(new Error('Copy timed out')), 8000);
          navigator.clipboard.writeText(url).then(
            () => {
              window.clearTimeout(timer);
              resolve();
            },
            (error) => {
              window.clearTimeout(timer);
              reject(error);
            }
          );
        });
        return;
      }
      const area = document.createElement('textarea');
      area.value = url;
      area.setAttribute('readonly', '');
      area.style.position = 'absolute';
      area.style.left = '-9999px';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(area);
      if (!ok) throw new Error('Copy failed');
    };

    const shareNative = async () => {
      if (!navigator.share) return 'unsupported';
      try {
        await navigator.share({ title, url });
        return 'shared';
      } catch (error) {
        if (error && error.name === 'AbortError') return 'aborted';
        return 'failed';
      }
    };

    const clearShareBusy = () => {
      shareButton.classList.remove('is-busy');
      shareButton.removeAttribute('aria-busy');
    };

    const restoreShareFocus = () => {
      // OS share sheet steals focus; print already restores after afterprint.
      if (!document.contains(shareButton)) return;
      if (typeof shareButton.focus === 'function') {
        shareButton.focus({ preventScroll: true });
      }
    };

    shareButton.classList.add('is-busy');
    shareButton.setAttribute('aria-busy', 'true');
    announceStatus(
      shareButton.getAttribute('data-label-sharing') ||
        document.body.getAttribute('data-share-loading') ||
        'Sharing'
    );

    let shareSettled = false;
    const hangTimer = window.setTimeout(() => {
      if (shareSettled) return;
      shareSettled = true;
      clearShareBusy();
      announceStatus(
        shareButton.getAttribute('data-label-dismissed') ||
          document.body.getAttribute('data-share-dismissed') ||
          'Share canceled'
      );
      restoreShareFocus();
    }, 30000);

    (async () => {
      const nativeResult = await shareNative();
      if (shareSettled) return;
      if (nativeResult === 'shared') {
        shareSettled = true;
        window.clearTimeout(hangTimer);
        clearShareBusy();
        markSuccess(labelShared);
        restoreShareFocus();
        return;
      }
      if (nativeResult === 'aborted') {
        shareSettled = true;
        window.clearTimeout(hangTimer);
        clearShareBusy();
        announceStatus(
          shareButton.getAttribute('data-label-dismissed') ||
            document.body.getAttribute('data-share-dismissed') ||
            'Share canceled'
        );
        restoreShareFocus();
        return;
      }
      try {
        await copy();
        if (shareSettled) return;
        shareSettled = true;
        window.clearTimeout(hangTimer);
        clearShareBusy();
        markSuccess(labelCopied);
      } catch (error) {
        if (shareSettled) return;
        shareSettled = true;
        window.clearTimeout(hangTimer);
        clearShareBusy();
        markCopyFailed();
      }
      // Restore after a failed native sheet, or keep focus when clipboard-only ran.
      restoreShareFocus();
    })();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const active = document.activeElement;
    const root =
      active instanceof HTMLElement
        ? active.closest('[data-product-share-panel].is-expanded')
        : null;
    if (!(root instanceof HTMLElement)) return;
    event.preventDefault();
    setProductShareExpanded(root, false);
    const toggle = root.querySelector('[data-product-share-toggle]');
    if (toggle instanceof HTMLButtonElement) toggle.focus({ preventScroll: true });
  });

  const isExpandedDetails = (details) =>
    details instanceof HTMLDetailsElement &&
    (details.hasAttribute('data-facets-drawer') ||
      details.classList.contains('disclosure') ||
      details.classList.contains('site-header__details') ||
      Boolean(details.closest('.site-header__mobile-menu')));

  const syncDetailsSummaryExpanded = (details) => {
    if (!isExpandedDetails(details)) return;
    const summary = details.querySelector(':scope > summary');
    if (!(summary instanceof HTMLElement)) return;
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };

  // Outside-click parity: accordion peer close must not leave focus inside a closed panel.
  const closeDetailsPeer = (peer) => {
    if (!(peer instanceof HTMLDetailsElement) || !peer.open) return;
    const hadFocus = peer.contains(document.activeElement);
    peer.open = false;
    syncDetailsSummaryExpanded(peer);
    if (!hadFocus) return;
    const summary = peer.querySelector(':scope > summary');
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  };

  const syncDetailsExpanded = (root = document) => {
    root
      .querySelectorAll(
        '[data-facets-drawer], details.disclosure, details.site-header__details, .site-header__mobile-menu details'
      )
      .forEach((details) => {
        if (details instanceof HTMLDetailsElement) syncDetailsSummaryExpanded(details);
      });
  };

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;

      syncDetailsSummaryExpanded(details);

      if (details.hasAttribute('data-facets-drawer')) {
        if (!details.open) {
          announceStatus(document.body.getAttribute('data-filters-closed') || 'Filters closed');
          return;
        }
        const summary = details.querySelector(':scope > summary');
        const labelEl = details.querySelector('.facets__toggle-label');
        const label = (labelEl?.textContent || summary?.textContent || 'Filters')
          .replace(/\s+/g, ' ')
          .trim();
        if (label) announceStatus(label);
        const panel = details.querySelector('.facets__panel');
        const first = panel ? getFocusable(panel)[0] : null;
        // After opening, move into the panel when focus is still on the summary.
        if (first instanceof HTMLElement && document.activeElement === summary) {
          window.requestAnimationFrame(() => {
            if (details.open) first.focus({ preventScroll: true });
          });
        }
        return;
      }

      if (!details.open) {
        // Facets already announced above; cover nav submenus + FAQ disclosures on close.
        if (
          details.classList.contains('site-header__details') ||
          details.classList.contains('disclosure') ||
          details.closest('.site-header__mobile-menu')
        ) {
          const summary = details.querySelector(':scope > summary');
          const label = (summary?.textContent || '').replace(/\s+/g, ' ').trim();
          if (label) {
            const template =
              document.body.getAttribute('data-details-closed-template') || '[label] closed';
            announceStatus(template.replaceAll('[label]', label));
          }
        }
        return;
      }

      if (details.classList.contains('site-header__details')) {
        document.querySelectorAll('.site-header__details[open]').forEach((peer) => {
          if (peer !== details) closeDetailsPeer(peer);
        });
        const summary = details.querySelector(':scope > summary');
        const submenuLabel = (summary?.textContent || '').replace(/\s+/g, ' ').trim();
        if (submenuLabel) announceStatus(submenuLabel);
        const firstLink = details.querySelector('.site-header__submenu a[href]');
        if (firstLink instanceof HTMLElement && document.activeElement === summary) {
          window.requestAnimationFrame(() => {
            if (details.open) firstLink.focus({ preventScroll: true });
          });
        }
        return;
      }

      const mobileMenu = details.closest('.site-header__mobile-menu');
      if (mobileMenu) {
        mobileMenu.querySelectorAll('details[open]').forEach((peer) => {
          if (peer !== details) closeDetailsPeer(peer);
        });
        const summary = details.querySelector(':scope > summary');
        const submenuLabel = (summary?.textContent || '').replace(/\s+/g, ' ').trim();
        if (submenuLabel) announceStatus(submenuLabel);
        const firstLink = details.querySelector('ul a[href]');
        if (firstLink instanceof HTMLElement && document.activeElement === summary) {
          window.requestAnimationFrame(() => {
            if (!details.open) return;
            firstLink.focus({ preventScroll: true });
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            firstLink.scrollIntoView({
              block: 'nearest',
              behavior: reduceMotion ? 'auto' : 'smooth',
            });
          });
        }
        return;
      }

      if (!details.classList.contains('disclosure')) return;

      const group = details.closest('[data-disclosure-group]');
      const peers = group
        ? Array.from(group.querySelectorAll('details.disclosure'))
        : details.parentElement
          ? Array.from(details.parentElement.children).filter(
              (el) => el instanceof HTMLDetailsElement && el.classList.contains('disclosure')
            )
          : [];

      peers.forEach((peer) => {
        if (peer !== details) closeDetailsPeer(peer);
      });

      const summary = details.querySelector(':scope > summary');
      const disclosureLabel = (summary?.textContent || '').replace(/\s+/g, ' ').trim();
      if (disclosureLabel) announceStatus(disclosureLabel);
      const content = details.querySelector('.disclosure__content');
      const first = content ? getFocusable(content)[0] : null;
      if (first instanceof HTMLElement && document.activeElement === summary) {
        window.requestAnimationFrame(() => {
          if (details.open) first.focus({ preventScroll: true });
        });
      }
    },
    true
  );

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const shouldSkipLandingFocus = () =>
    Boolean(
      document.querySelector(
        '.status-banner--error[role="alert"][autofocus], .status-banner--success[role="status"][autofocus]'
      )
    );

  const focusLandingTarget = (target, { scroll = true, announce = false } = {}) => {
    if (!(target instanceof HTMLElement)) return false;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    if (scroll) {
      target.scrollIntoView({
        block: 'start',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
    target.focus({ preventScroll: true });
    if (announce) {
      const labelSource =
        target.matches(
          '.facets__count, .search-page__count, .blog-page__tag, .collection__empty, [role="status"]'
        )
          ? target
          : target.querySelector(
              [
                '.facets__count',
                '.search-page__count',
                'h1',
                'h2:not(.blog-card__title)',
                'h3:not(.card-product__title)',
                '[role="heading"]:not(.card-product__title):not(.blog-card__title)',
                '.collection__empty',
                '.collections-page__empty-title',
                '.collection-list__empty-title',
                '.blog-page__empty-title',
                '.search-page__empty',
                '.related-products__empty-title',
                '[role="status"]',
                'p',
              ].join(', ')
            );
      const label = (
        target.getAttribute('aria-label') ||
        labelSource?.textContent ||
        target.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
      if (label) announceStatus(label);
    }
    return true;
  };

  const enhanceHashLanding = () => {
    if (shouldSkipLandingFocus()) return false;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return false;
    // Match filter/sort results landing: speak a short label when jumping to an in-page target.
    return focusLandingTarget(document.getElementById(hash), {
      scroll: false,
      announce: true,
    });
  };

  const enhanceResultsLanding = () => {
    // Don't steal focus from form success/error banners.
    if (shouldSkipLandingFocus()) return;

    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get('page') || '1');
    const paged = Number.isFinite(page) && page > 1;
    // After filter/sort/search navigations, land in the results region (page 1 included).
    const filtered = Array.from(params.keys()).some(
      (key) => key.startsWith('filter.') || key === 'sort_by' || (key === 'q' && params.get('q'))
    );
    // Blog tags use /tagged/handle paths (no query string on page 1).
    const taggedBlog = /\/tagged\//i.test(window.location.pathname);
    if (!paged && !filtered && !taggedBlog) return;

    // Prefer empty states, then result counts / active blog tag (not the first card title).
    const target = document.querySelector(
      [
        '.collection__empty-state',
        '.search-page__empty-panel',
        '.blog-page__empty',
        '.facets__count',
        '.search-page__count',
        '.blog-page__tag[aria-current="true"]',
        '.collection__grid',
        '.blog-page__grid',
        '.article-page__comment-list',
        '.collections-page__grid',
      ].join(', ')
    );
    focusLandingTarget(target, { announce: true });
  };

  const enhanceScrollChrome = () => {
    let progress = document.querySelector('[data-scroll-progress]');
    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'scroll-progress';
      progress.setAttribute('data-scroll-progress', '');
      progress.setAttribute('aria-hidden', 'true');
      document.body.prepend(progress);
    }

    document.querySelectorAll('a.skip-link').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement) || link.dataset.skipEnhanced === 'true') return;
      link.dataset.skipEnhanced = 'true';
      link.addEventListener('click', () => {
        announceStatus((link.textContent || '').replace(/\s+/g, ' ').trim() || 'Skip to content');
      });
    });

    let backToTop = document.querySelector('[data-back-to-top]');
    if (!backToTop) {
      backToTop = document.createElement('button');
      backToTop.type = 'button';
      backToTop.className = 'back-to-top';
      backToTop.setAttribute('data-back-to-top', '');
      backToTop.setAttribute(
        'aria-label',
        document.body.getAttribute('data-back-to-top-label') || 'Back to top'
      );
      backToTop.innerHTML =
        '<span class="back-to-top__icon" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 14l6-6 6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>';
      document.body.appendChild(backToTop);
      backToTop.addEventListener('click', () => {
        // Re-entry gate — rapid activate during smooth scroll would spam announce/focus.
        if (backToTop.classList.contains('is-busy')) return;
        backToTop.classList.add('is-busy');
        backToTop.setAttribute('aria-busy', 'true');
        announceStatus(
          backToTop.getAttribute('aria-label') ||
            document.body.getAttribute('data-back-to-top-label') ||
            'Back to top'
        );
        const reduceMotion = prefersReducedMotion();
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        const main = document.getElementById('MainContent');
        const finish = () => {
          backToTop.classList.remove('is-busy');
          backToTop.removeAttribute('aria-busy');
          if (main instanceof HTMLElement) main.focus({ preventScroll: true });
        };
        if (reduceMotion || !(main instanceof HTMLElement)) {
          finish();
          return;
        }
        window.setTimeout(finish, 420);
      });
    }

    const READING_MILESTONES = [25, 50, 75, 100];
    let lastReadingMilestone = 0;
    let readingAnnounceReady = false;

    const onScroll = () => {
      const scrolled = window.scrollY > 12;
      const showTop = window.scrollY > Math.min(720, window.innerHeight * 1.1);
      document.documentElement.classList.toggle('is-scrolled', scrolled);
      const wasVisible = backToTop.classList.contains('is-visible');
      backToTop.classList.toggle('is-visible', showTop);
      backToTop.setAttribute('aria-hidden', showTop ? 'false' : 'true');
      backToTop.tabIndex = showTop ? 0 : -1;
      // Sticky ATC parity: speak, then leave the now-hidden control.
      if (wasVisible && !showTop && backToTop.contains(document.activeElement)) {
        announceStatus(
          document.body.getAttribute('data-back-to-top-hidden') || 'Back to top closed'
        );
        const main = document.getElementById('MainContent');
        if (main instanceof HTMLElement) main.focus({ preventScroll: true });
      }

      const readingTarget = document.querySelector('[data-reading-progress-target]');
      let ratio = 0;
      if (readingTarget instanceof HTMLElement) {
        const rect = readingTarget.getBoundingClientRect();
        const viewH = window.innerHeight || 1;
        const start = window.scrollY + rect.top;
        const height = readingTarget.offsetHeight || 0;
        if (height <= viewH) {
          ratio = rect.bottom <= viewH ? 1 : rect.top < viewH ? Math.min(1, Math.max(0, (viewH - rect.top) / viewH)) : 0;
        } else {
          const end = start + height - viewH;
          ratio = Math.min(1, Math.max(0, (window.scrollY - start) / Math.max(1, end - start)));
        }

        const percent = Math.round(ratio * 100);
        let nextMilestone = lastReadingMilestone;
        READING_MILESTONES.forEach((milestone) => {
          if (percent >= milestone && milestone > lastReadingMilestone) {
            nextMilestone = milestone;
          }
        });
        if (nextMilestone > lastReadingMilestone) {
          lastReadingMilestone = nextMilestone;
          // Seed on first paint so short/restored articles don't spam status.
          if (readingAnnounceReady) {
            const template =
              document.body.getAttribute('data-reading-progress-template') ||
              'Reading progress [percent] percent';
            announceStatus(template.replace(/\[percent\]/g, String(nextMilestone)));
          }
        }
      } else {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        ratio = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      }
      progress.style.setProperty('--scroll-progress', String(ratio));
      progress.classList.toggle('is-reading', Boolean(readingTarget));
    };
    onScroll();
    readingAnnounceReady = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  };

  const assignStaggerDelays = (root = document) => {
    root.querySelectorAll('.motion-stagger').forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        if (!child.matches('[data-motion="reveal"], .motion-reveal')) return;
        if (child.style.getPropertyValue('--motion-delay')) return;
        child.style.setProperty('--motion-delay', `calc(var(--motion-stagger) * ${index})`);
      });
    });
  };

  const enhanceMotion = (root = document) => {
    if (prefersReducedMotion()) return;

    assignStaggerDelays(root);

    // Match CSS: any .motion-reveal is hidden once html.motion-ok is set.
    // Selecting only [data-motion="reveal"] left class-only nodes permanently blank
    // (password page eyebrow/title/form/owner).
    const targets = Array.from(
      root.querySelectorAll('[data-motion="reveal"], .motion-reveal')
    ).filter((el) => !el.classList.contains('is-visible'));
    if (targets.length === 0) {
      document.documentElement.classList.add('motion-ok');
      return;
    }

    const viewportBottom = window.innerHeight * 0.94;
    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportBottom && rect.bottom > 0) {
        el.classList.add('is-visible');
      }
    });

    document.documentElement.classList.add('motion-ok');

    const deferred = targets.filter((el) => !el.classList.contains('is-visible'));
    if (deferred.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      deferred.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!window.__brightsideMotionObserver) {
      window.__brightsideMotionObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
    }

    deferred.forEach((el) => window.__brightsideMotionObserver.observe(el));
  };

  const enhancePasswordCapsLock = (root) => {
    const cleanups = [];

    root.querySelectorAll('[data-password-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.dataset.capsEnhanced === 'true') return;
      const field = input.closest('.password-page__field, [data-password-field]') || input.parentElement;
      const hint = field?.querySelector('[data-password-caps]');
      if (!(hint instanceof HTMLElement)) return;

      input.dataset.capsEnhanced = 'true';

      const sync = (event) => {
        if (!(event instanceof KeyboardEvent) || typeof event.getModifierState !== 'function') return;
        const on = event.getModifierState('CapsLock');
        const wasHidden = hint.hidden;
        hint.hidden = !on;
        // Re-assert chip text + global live region when Caps Lock turns on.
        if (on && wasHidden) {
          const message = (hint.textContent || '').trim() || 'Caps Lock is on';
          hint.textContent = '';
          hint.textContent = message;
          announceStatus(message);
        } else if (!on && !wasHidden) {
          // Blur hides without announcing; key toggle off should speak the clear.
          announceStatus(hint.getAttribute('data-label-off') || 'Caps Lock is off');
        }
      };

      const onBlur = (event) => {
        const related = event instanceof FocusEvent ? event.relatedTarget : null;
        // Keep the warning while focus stays in the field (Show/Hide toggle).
        if (related instanceof Node && field instanceof HTMLElement && field.contains(related)) {
          return;
        }
        hint.hidden = true;
      };

      // Catch CapsLock toggles that happen while the field (input or toggle) is focused
      // (including presses that don't change the input value).
      const onWindowKey = (event) => {
        const active = document.activeElement;
        if (active === input) {
          sync(event);
          return;
        }
        if (
          active instanceof Node &&
          field instanceof HTMLElement &&
          field.contains(active)
        ) {
          sync(event);
        }
      };

      input.addEventListener('keydown', sync);
      input.addEventListener('keyup', sync);
      input.addEventListener('blur', onBlur);
      window.addEventListener('keydown', onWindowKey);
      window.addEventListener('keyup', onWindowKey);
      cleanups.push(() => {
        input.removeEventListener('keydown', sync);
        input.removeEventListener('keyup', sync);
        input.removeEventListener('blur', onBlur);
        window.removeEventListener('keydown', onWindowKey);
        window.removeEventListener('keyup', onWindowKey);
        delete input.dataset.capsEnhanced;
      });
    });

    return () => cleanups.forEach((fn) => fn());
  };

  const enhancePrintButtons = (root) => {
    const cleanups = [];

    root.querySelectorAll('[data-print], [data-gift-card-print]').forEach((button) => {
      if (!(button instanceof HTMLElement) || button.dataset.printEnhanced === 'true') return;
      button.dataset.printEnhanced = 'true';

      let labelEl = button.querySelector('[data-print-label]');
      if (!(labelEl instanceof HTMLElement)) {
        const initial = button.textContent.trim() || 'Print';
        button.replaceChildren();
        const icon = document.createElement('span');
        icon.setAttribute('data-share-icon', '');
        icon.setAttribute('aria-hidden', 'true');
        icon.hidden = true;
        icon.textContent = '✓';
        labelEl = document.createElement('span');
        labelEl.setAttribute('data-print-label', '');
        labelEl.textContent = initial;
        button.append(icon, labelEl);
      }

      const labelPrint = labelEl.textContent;
      let fallbackTimer = 0;
      let finished = false;

      const finish = ({ announce = true } = {}) => {
        if (finished) return;
        finished = true;
        window.clearTimeout(fallbackTimer);
        window.removeEventListener('afterprint', onAfterPrint);
        button.classList.remove('is-busy');
        button.removeAttribute('aria-busy');
        // Silent 60s recover still returns keyboard focus after the OS print UI.
        if (document.body.contains(button)) {
          button.focus({ preventScroll: true });
        }
        if (!announce) return;
        button.classList.add('is-success');
        const icon = button.querySelector('[data-share-icon]');
        if (icon instanceof HTMLElement) icon.hidden = false;
        announceStatus(
          button.getAttribute('data-label-printed') ||
            document.body.getAttribute('data-print-done') ||
            'Print dialog closed'
        );
        window.setTimeout(() => {
          button.classList.remove('is-success');
          if (icon instanceof HTMLElement) icon.hidden = true;
          labelEl.textContent = labelPrint;
        }, 1200);
      };

      const onAfterPrint = () => finish({ announce: true });

      const onClick = () => {
        if (button.classList.contains('is-busy')) return;
        finished = false;
        button.classList.add('is-busy');
        button.setAttribute('aria-busy', 'true');
        announceStatus(
          button.getAttribute('data-label-printing') ||
            document.body.getAttribute('data-print-loading') ||
            'Opening print dialog'
        );
        window.addEventListener('afterprint', onAfterPrint);
        // Long silent recover only — a 2s "done" falsely cleared busy while the sheet was open.
        fallbackTimer = window.setTimeout(() => finish({ announce: false }), 60000);
        window.print();
      };

      button.addEventListener('click', onClick);
      cleanups.push(() => {
        button.removeEventListener('click', onClick);
        window.removeEventListener('afterprint', onAfterPrint);
        window.clearTimeout(fallbackTimer);
        delete button.dataset.printEnhanced;
      });
    });

    return () => cleanups.forEach((fn) => fn());
  };

  const enhance = (root = document) => {
    enhanceMotion(root);
    enhanceQuantityControls(root);
    enhanceNoteFields(root);
    enhanceClampText(root);
    enhanceCardOffers(root);
    enhanceAnnouncementBars(root);
    enhancePasswordCapsLock(root);
    enhancePrintButtons(root);
    syncDetailsExpanded(root);

    root.querySelectorAll('[data-component]').forEach((element) => {
      if (controllers.has(element)) return;

      const name = element.getAttribute('data-component');
      let destroy = () => {};

      if (name === 'header-menu') {
        destroy = enhanceHeaderMenu(element) || destroy;
      }

      if (name === 'predictive-search') {
        destroy = enhancePredictiveSearch(element) || destroy;
      }

      if (name === 'account-menu') {
        destroy = enhanceAccountMenu(element) || destroy;
      }

      if (name === 'search-page') {
        destroy = enhanceSearchPage(element) || destroy;
      }

      if (name === 'product') {
        destroy = enhanceProduct(element) || destroy;
      }

      if (name === 'related-products') {
        destroy = enhanceRelatedProducts(element) || destroy;
      }

      if (name === 'cart-drawer') {
        destroy = enhanceCartDrawer(element) || destroy;
      }

      if (name === 'cart-page') {
        destroy = enhanceCartPage(element) || destroy;
      }

      if (name === 'promo-modal') {
        destroy = enhancePromoModal(element) || destroy;
      }

      if (name === 'promo-gallery') {
        destroy = enhancePromoGallery(element) || destroy;
      }

      const controller = { name, element, destroy };
      controllers.set(element, controller);
      element.dispatchEvent(
        new CustomEvent('theme:component:ready', {
          bubbles: true,
          detail: { name },
        })
      );
    });
  };

  const destroyWithin = (root = document) => {
    root.querySelectorAll('[data-component]').forEach((element) => {
      const controller = controllers.get(element);
      if (!controller) return;
      controller.destroy();
      controllers.delete(element);
    });

    root.querySelectorAll('[data-clamp-text]').forEach((wrap) => {
      const cleanup = clampCleanups.get(wrap);
      if (!cleanup) return;
      cleanup();
      clampCleanups.delete(wrap);
    });
  };

  /*
    Auction Today portals tingle modals onto <body> with the close control
    fixed to the viewport corner. Move it onto the dialog card, normalize
    the oversized × chrome, and delay tingle's hard close so exit motion
    can finish.
  */
  const AUCTION_TODAY_CLOSE_SVG =
    '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path d="M2.1 2.1a.75.75 0 0 1 1.06 0L6 4.94l2.84-2.84a.75.75 0 1 1 1.06 1.06L7.06 6l2.84 2.84a.75.75 0 1 1-1.06 1.06L6 7.06 3.16 9.9a.75.75 0 1 1-1.06-1.06L4.94 6 2.1 3.16a.75.75 0 0 1 0-1.06Z" fill="currentColor"/></svg>';

  const normalizeAuctionTodayModalClose = (close) => {
    if (!(close instanceof HTMLElement)) return;
    close.classList.add('brightside-auction-modal-close');
    [
      'position',
      'top',
      'right',
      'left',
      'bottom',
      'width',
      'height',
      'min-width',
      'min-height',
      'max-width',
      'max-height',
      'font-size',
      'line-height',
      'transform',
      'background',
      'border',
      'box-shadow',
      'color',
      'padding',
      'margin',
    ].forEach((prop) => close.style.removeProperty(prop));

    let icon = close.querySelector('.tingle-modal__closeIcon');
    if (!(icon instanceof HTMLElement)) {
      icon = document.createElement('span');
      icon.className = 'tingle-modal__closeIcon';
      close.insertBefore(icon, close.firstChild);
    }
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = AUCTION_TODAY_CLOSE_SVG;

    close.querySelectorAll('.tingle-modal__closeLabel').forEach((label) => {
      if (label instanceof HTMLElement) label.hidden = true;
    });

    // Drop leftover text nodes (tingle often ships a bare “×”).
    Array.from(close.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        node.textContent = '';
      }
    });
  };

  const placeAuctionTodayModalClose = (modal) => {
    if (!(modal instanceof HTMLElement)) return;
    if (!modal.classList.contains('custom-class-1')) return;
    const box = modal.querySelector(':scope > .tingle-modal-box');
    if (!(box instanceof HTMLElement)) return;

    const close =
      modal.querySelector(':scope > .tingle-modal__close') ||
      box.querySelector(':scope > .tingle-modal__close');
    if (!(close instanceof HTMLElement)) return;

    normalizeAuctionTodayModalClose(close);
    if (close.parentElement !== box) {
      box.insertBefore(close, box.firstChild);
    }
  };

  const getAuctionTodayMotionMs = () => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--motion-duration-base')
      .trim();
    const ms = Number.parseFloat(raw);
    if (!Number.isFinite(ms) || ms <= 0) return 0;
    return Math.round(ms) + 80;
  };

  const prefersAuctionTodayReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const patchAuctionTodayTingleClose = () => {
    const proto = window.tingle?.modal?.prototype;
    if (!proto || proto.__brightsideAuctionClosePatched) return;
    const originalClose = proto.close;
    if (typeof originalClose !== 'function') return;

    proto.close = function brightsideAuctionClose(force) {
      const modal = this.modal;
      const canAnimate =
        !force &&
        modal instanceof HTMLElement &&
        modal.classList.contains('custom-class-1') &&
        modal.classList.contains('tingle-modal--visible') &&
        !modal.classList.contains('tingle-modal--closing') &&
        !prefersAuctionTodayReducedMotion();

      if (!canAnimate) {
        if (modal instanceof HTMLElement) {
          modal.classList.remove('tingle-modal--closing');
        }
        return originalClose.call(this);
      }

      if (typeof this._isBusy === 'function' && this._isBusy()) return this;
      if (typeof this.opts?.beforeClose === 'function' && !this.opts.beforeClose.call(this)) {
        return this;
      }

      if (typeof this._busy === 'function') this._busy(true);

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        modal.removeEventListener('animationend', onAnimationEnd);
        window.clearTimeout(fallbackTimer);
        document.body.classList.remove('tingle-enabled');
        document.body.style.top = null;
        window.scrollTo({
          top: this._scrollPosition || 0,
          behavior: 'instant',
        });
        modal.classList.remove('tingle-modal--closing', 'tingle-modal--visible');
        modal.style.display = 'none';
        if (typeof this.opts?.onClose === 'function') this.opts.onClose.call(this);
        if (typeof this._busy === 'function') this._busy(false);
      };

      const onAnimationEnd = (event) => {
        if (!(event.target instanceof HTMLElement)) return;
        if (event.target !== modal && !event.target.classList.contains('tingle-modal-box')) return;
        finish();
      };

      modal.classList.add('tingle-modal--closing');
      modal.classList.remove('tingle-modal--visible');
      modal.addEventListener('animationend', onAnimationEnd);
      const fallbackTimer = window.setTimeout(finish, Math.max(getAuctionTodayMotionMs(), 180));
      return this;
    };

    proto.__brightsideAuctionClosePatched = true;
  };

  const enhanceAuctionTodayModals = () => {
    patchAuctionTodayTingleClose();

    document.querySelectorAll('.tingle-modal.custom-class-1').forEach((modal) => {
      placeAuctionTodayModalClose(modal);
    });

    if (document.documentElement.dataset.auctionTodayModalObserver === 'true') return;
    document.documentElement.dataset.auctionTodayModalObserver = 'true';

    const observer = new MutationObserver((mutations) => {
      patchAuctionTodayTingleClose();
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          if (mutation.target.classList.contains('tingle-modal')) {
            placeAuctionTodayModalClose(mutation.target);
          }
        }
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.classList.contains('tingle-modal')) {
            placeAuctionTodayModalClose(node);
            continue;
          }
          node.querySelectorAll?.('.tingle-modal.custom-class-1').forEach((modal) => {
            placeAuctionTodayModalClose(modal);
          });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  const boot = () => {
    enhanceScrollChrome();
    enhanceAuctionTodayModals();
    enhance(document);
    const alert = document.querySelector(
      '.status-banner--error[role="alert"], .status-banner--success[role="status"][autofocus]'
    );
    if (alert && typeof alert.focus === 'function') {
      alert.focus({ preventScroll: true });
      // Global live region — success status + error handoff can miss the banner text.
      const message = (alert.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 200);
      if (message) announceStatus(message);
      // After the error summary is announced, move to the first invalid field to fix it.
      if (alert.classList.contains('status-banner--error')) {
        const form = alert.closest('form');
        const invalid = form?.querySelector('[aria-invalid="true"]');
        if (invalid instanceof HTMLElement) {
          window.setTimeout(() => {
            if (document.activeElement === alert) {
              invalid.focus({ preventScroll: true });
              const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              invalid.scrollIntoView({
                block: 'nearest',
                behavior: reduceMotion ? 'auto' : 'smooth',
              });
            }
          }, 120);
        }
      }
      return;
    }
    // Prefer in-page anchors (e.g. article comments pagination) over list/filter landing.
    if (!enhanceHashLanding()) {
      enhanceResultsLanding();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('hashchange', () => {
    enhanceHashLanding();
  });

  document.addEventListener('shopify:section:load', (event) => {
    enhance(event.target);
    formatJudgemeAffiliateFooters(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });

  window.BrightSideTheme = {
    enhance,
    destroyWithin,
  };
})();
